// HeadOffice.js — fleet dashboard for /api/headoffice/* endpoints
//
// Scaffolded UI that mirrors the structure of the head-office backend.
// Each module gets a card showing active-branch count plus any urgent
// rule failure counts. Click a card to drill in and see per-branch
// detail for that module.
//
// All endpoints assume server.js handles JWT verification — we just pass
// the Authorization header from localStorage.

import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import PopupContent from "./PopupContent";
// ActionCentre import removed — the Action centre now lives on the
// Communications page. ActionCentre.js itself is unchanged.
// holiday_rota / StaffAbsences / Staff / ClockInOut imports removed with the
// staff cards. App.js still routes Staff and the holiday rota directly.
import PeriodicTaskEntryDialog from "./PeriodicTaskEntryDialog";
import FleetMap from "./FleetMap";
import { useMaskNames, displayName, MaskToggleButton } from "./nameMask";
import { fmtEta, etaBasisText } from "./etaFormat";
import "./Popup.css";

import API_BASE from "./apiBase";
import VehiclesCard, { VehiclesView } from "./VehiclesCard";

// ---------------------------------------------------------------------------
// Module metadata — paths under /api/headoffice/ and which live rules to poll
// ---------------------------------------------------------------------------
const MODULES = [
  {
    key: "rounds", title: "Delivery Rounds", path: "rounds",
    blurb: "Live now. Branches with a driver out — a round started and not yet completed.",
    activeLabel: "Branches with a round open",
    activeHint: "Branches with at least one delivery round that has been started and not completed.",
    activeEndpoint: "branches-with-open-round",
    urgentRules: [
      { rule: "no-progress", label: "No drop in 90 min", severity: "fail",
        hint: "A round with drops still aboard and nothing delivered for an hour and a half. Usually a flat battery, a breakdown, or an app that has stopped uploading — in every one of those cases somebody should be ringing the driver." },
      { rule: "open-late",   label: "Out past 6pm",      severity: "warn",
        hint: "Rounds still open after the usual finish. Not necessarily a problem — evening rounds are normal in some operations — but worth a glance." }
    ],
    // The rules colour the card face and tint the grid rows; they do NOT get
    // their own drill-in tables, because the grid below already lists every
    // round and repeating a subset above it just makes the same rounds appear
    // twice under different headings.
    urgentOnCardOnly: true,
    cardRollups: [
      { endpoint: "parcels-out-total", field: "onRound", label: "Parcels out",
        tone: "ok",
        hint: "Parcels currently aboard a van across every branch you can see." }
    ],
    snapshot: "open-rounds",
    snapshotTitle: "Open rounds"
  },
  {
    key: "deliveriesToday", title: "All todays deliveries", path: "deliveries-today",
    // The scope differs from Rounds on purpose and the two cards will disagree.
    // Rounds is live now — a round closed at 11am has left it. This is the
    // whole day. Saying so here is cheaper than explaining it every time
    // somebody notices the delivered figures do not match.
    blurb: "Since midnight, by customer. Rounds is live now; this is the whole day.",
    activeLabel: "Branches",
    activeHint: "Branches you can see.",
    urgentRules: [
      { rule: "nothing-out", label: "Nothing out yet", severity: "warn",
        hint: "A branch with parcels waiting and no round open. Fine early on; less so by mid-morning, so it only fires after 10am." }
    ],
    urgentOnCardOnly: true,
    // All four read one endpoint — the engine fetches each distinct endpoint
    // once. Total is deliberately untinted: it is the denominator, not a
    // verdict. Cancelled and returned sit outside these numbers so that
    // total = delivered + failed + outstanding holds on screen.
    cardRollups: [
      { endpoint: "totals", field: "total", quiet: true,       label: "Parcels today", tone: "plain",
        hint: "Delivered, failed and still outstanding added together." },
      { endpoint: "totals", field: "delivered", quiet: true,   label: "Delivered",     tone: "ok",
        hint: "Handed over today." },
      { endpoint: "totals", field: "failed", quiet: true,      label: "Failed",        tone: "fail",
        hint: "Attempted and failed today." },
      { endpoint: "totals", field: "outstanding", quiet: true, label: "Outstanding",   tone: "warn",
        hint: "Still to go right now, whenever it was placed." }
    ],
    snapshot: "deliveries",
    snapshotTitle: "Today's deliveries"
  },
  {
    key: "failedAttempts", title: "Failed attempts", path: "failed-attempts",
    blurb: "The exception queue — anything that has failed at least once and is not finished, however long ago.",
    activeLabel: "Branches with failures",
    activeHint: "Branches carrying at least one unresolved exception.",
    activeEndpoint: "branches-with-failures",
    urgentRules: [
      { rule: "three-attempts", label: "3+ attempts",     severity: "fail",
        hint: "Tried three times or more. At this point somebody needs to ring the customer rather than send the van again." },
      { rule: "unresolved-48h", label: "Failed 48h+ ago", severity: "warn",
        hint: "Failed two days ago and still not delivered, returned or cancelled — so it is sitting somewhere with nobody owning it." }
    ],
    urgentOnCardOnly: true,
    cardRollups: [
      { endpoint: "totals", field: "unresolved", quiet: true,
        label: "Parcels in queue", tone: "plain",
        hint: "Every parcel that has failed at least once and is not delivered, returned or cancelled." },
      { endpoint: "totals", field: "oldestDays", quiet: true,
        label: "Oldest (days)", tone: "plain",
        hint: "Days since the oldest queue item was last attempted." }
    ],
    snapshot: "queue",
    snapshotTitle: "Exception queue"
  },
  {
    key: "drivers", title: "Drivers", path: "drivers",
    blurb: "Shifts and mileage from the clock, not from rounds. Last 7 days.",
    activeLabel: "On shift now",
    activeHint: "Drivers with an open shift dated today. A shift left open from an earlier day is counted as not clocked out, not as someone still working.",
    activeEndpoint: "on-shift-now",
    urgentRules: [
      { rule: "no-clock-out",    label: "Not clocked out",  severity: "warn",
        hint: "A shift with a start and no end, from an earlier day. Usually a driver who forgot rather than one still working — but the timesheet is wrong either way." },
      { rule: "missing-mileage", label: "Mileage missing",  severity: "warn",
        hint: "A finished shift with no opening or closing odometer reading, so the day's mileage cannot be worked out." }
    ],
    urgentOnCardOnly: true,
    cardRollups: [
      { endpoint: "totals", field: "hoursWeek", quiet: true,
        label: "Hours (7d)", tone: "plain",
        hint: "Completed shifts only — an open shift has no length yet." },
      { endpoint: "totals", field: "milesWeek", quiet: true,
        label: "Miles (7d)", tone: "plain",
        hint: "Closing minus opening odometer, summed over completed shifts with both readings." }
    ],
    snapshot: "roster",
    snapshotTitle: "Driver roster"
  }
];

// ---------------------------------------------------------------------------
// Tiny fetch helper
// ---------------------------------------------------------------------------
// Append the viewer's organisation as a query param so the backend scopes
// the fan-out to that group's member branches. "Pharmace all" (admin) and
// an absent value both mean "whole fleet" server-side, so we can always send
// it. Mirrors the identity Mockupp.js reads from localStorage.
function withOrgScope(path) {
  const org = localStorage.getItem("organisationName") || "";
  if (!org) return path;
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}organisationName=${encodeURIComponent(org)}`;
}

async function apiGet(path, token) {
  let res;
  try {
    res = await fetch(`${API_BASE}/api/headoffice/${withOrgScope(path)}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  } catch (e) {
    // Network-level failure (connection reset, CORS, timeout) — name the path
    // so the surfaced error isn't a bare "Failed to fetch".
    throw new Error(`${path} → network error (${e.message})`);
  }
  if (!res.ok) throw new Error(`${path} → HTTP ${res.status}`);
  return res.json();
}

// Write helper for POST/PUT/DELETE (periodic tasks CRUD). Mirrors apiGet's
// error-naming so failures surface the endpoint.
async function apiSend(path, token, method = "POST", body = null) {
  let res;
  try {
    res = await fetch(`${API_BASE}/api/headoffice/${withOrgScope(path)}`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      body: body != null ? JSON.stringify(body) : undefined
    });
  } catch (e) {
    throw new Error(`${path} → network error (${e.message})`);
  }
  if (!res.ok) {
    let msg = `${path} → HTTP ${res.status}`;
    try { const j = await res.json(); if (j?.error) msg += ` — ${j.error}`; } catch (_) {}
    throw new Error(msg);
  }
  return res.json();
}

// Root-host POST helper for the SOP / Near-Miss / Action-Centre summary
// endpoints. These live at the API root (e.g. /headoffice_sop_card), not
// under /api/headoffice/, and are POST with a Bearer token.
async function apiPostRoot(path, token, body = {}) {
  // Carry the viewer's organisation so the root-host card endpoints scope to
  // the group's branches, same as the /api/headoffice/* calls. The backend
  // treats "Pharmace all"/absent as the whole fleet.
  const org = localStorage.getItem("organisationName") || "";
  const scopedBody = org ? { ...(body || {}), organisationName: org } : (body || {});
  let res;
  try {
    res = await fetch(`${API_BASE}/${path}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify(scopedBody)
    });
  } catch (e) {
    throw new Error(`${path} → network error (${e.message})`);
  }
  if (!res.ok) throw new Error(`${path} → HTTP ${res.status}`);
  return res.json();
}

// ---------------------------------------------------------------------------
// Reusable centre-screen loading overlay. Use for detail panels whose data
// takes a noticeable moment to compute (e.g. messaging, which fans out across
// the whole fleet). Renders a dimmed backdrop with a centred spinner + label.
// ---------------------------------------------------------------------------
function LoadingOverlay({ label = "Loading…", fullscreen = true }) {
  const wrap = fullscreen
    ? { position: "fixed", inset: 0, zIndex: 1000 }
    : { position: "absolute", inset: 0, zIndex: 10 };
  return (
    <div style={{
      ...wrap, display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      background: "rgba(255,255,255,0.65)", backdropFilter: "blur(1px)"
    }}>
      <style>{`@keyframes ho-spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{
        width: 40, height: 40, borderRadius: "50%",
        border: "4px solid #dbeafe", borderTopColor: "#1976d2",
        animation: "ho-spin 0.8s linear infinite"
      }} />
      <div style={{ marginTop: 12, fontSize: 14, color: "#374151", fontWeight: 500 }}>
        {label}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styled hover tooltip. Wrap any element; shows `text` in a dark floating box
// on hover. Self-contained (no portal) — positioned just above the trigger.
// ---------------------------------------------------------------------------
function InfoTip({ text, children, width = 260, block = false }) {
  const [show, setShow] = useState(false);
  return (
    <span
      style={{ position: "relative",
               display: block ? "block" : "inline-flex",
               alignItems: "center" }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      {show && (
        <span style={{
          position: "absolute", bottom: "calc(100% + 8px)", left: 0,
          width, zIndex: 50, background: "#111827", color: "#f9fafb",
          fontSize: 12, lineHeight: 1.45, fontWeight: 400,
          padding: "8px 10px", borderRadius: 6,
          boxShadow: "0 4px 14px rgba(0,0,0,0.25)", pointerEvents: "none",
          whiteSpace: "normal"
        }}>
          {text}
          <span style={{
            position: "absolute", top: "100%", left: 14,
            borderLeft: "6px solid transparent", borderRight: "6px solid transparent",
            borderTop: "6px solid #111827"
          }} />
        </span>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Style tokens
// ---------------------------------------------------------------------------
const S = {
  wrap:     { padding: 0, fontFamily: "Inter, system-ui, sans-serif" },
  h1:       { fontSize: 22, fontWeight: 600, margin: "0 0 6px" },
  sub:      { fontSize: 13, color: "#6b7280", margin: "0 0 16px" },
  // 280px let only five cards across before wrapping, so the Staff row (six)
  // spilled onto a second line with the rest of the width sitting empty.
  // 205px fits six comfortably on a normal screen and seven on a wide one;
  // the 1fr half of the minmax still stretches them to fill whatever is
  // there, so rows with fewer cards look no different.
  grid:     { display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(205px, 1fr))",
              gap: 12 },
  card:     { background: "#fff", border: "1px solid #e7ebf1", borderRadius: 10,
              boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 1px 3px rgba(16,24,40,0.06)",
              boxSizing: "border-box", padding: 13, cursor: "pointer",
              // Narrower cards mean longer labels have to wrap rather than
              // push the figure off the right edge.
              minWidth: 0, overflowWrap: "anywhere",
              transition: "box-shadow 120ms, border-color 200ms" },
  cardTitle:{ fontSize: 14, fontWeight: 600, marginBottom: 5, color: "#111827",
              lineHeight: 1.25 },
  // alignItems/gap so a wrapped label keeps its number beside it rather than
  // stretching the row; the number never wraps.
  metaRow:  { display: "flex", justifyContent: "space-between",
              alignItems: "baseline", gap: 8, fontSize: 12.5,
              color: "#344054", padding: "2px 0", lineHeight: 1.3 },
  urgent:   { color: "#b91c1c", fontWeight: 600 },
  ok:       { color: "#059669", fontWeight: 600 },
  drill:    { background: "#fff", border: "1px solid #e7ebf1", borderRadius: 10,
              boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 1px 3px rgba(16,24,40,0.06)",
              padding: 18, marginTop: 12 },
  drillTitle:{ fontSize: 18, fontWeight: 600, marginBottom: 10 },
  table:    { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th:       { textAlign: "left", borderBottom: "1px solid #e7ebf1",
              padding: "6px 8px", fontWeight: 600, color: "#344054" },
  td:       { borderBottom: "1px solid #f2f4f7", padding: "6px 8px",
              color: "#1f2937", verticalAlign: "top" },
  btn:      { background: "#1976d2", color: "#fff", border: "none",
              borderRadius: 8, padding: "6px 12px", cursor: "pointer",
              fontSize: 13 },
  btnGhost: { background: "#ffffff", color: "#344054", border: "1px solid #d8dee8",
              borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 13 },
  error:    { color: "#b91c1c", fontSize: 13, padding: "4px 0" }
};

// ---------------------------------------------------------------------------
// Formatters — per-module, per-rule table column definitions
// ---------------------------------------------------------------------------
// Each urgent-rule formatter is a list of column defs; rows are produced by
// flattening fleetLive results (one row per (branch × offender)).
// Each snapshot formatter takes the snapshot payload and returns JSX.

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-GB") : "—";
const fmtDateTime = (d) =>
  d ? new Date(d).toLocaleString("en-GB", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit"
  }) : "—";
const fmtPct = (n) => n == null ? "—" : `${n.toFixed(1)}%`;

// Background colour for a 0–100 percentage: full green at 100%, full red at
// 10% or below (but >0), interpolated through amber/yellow between. Returns
// undefined for null so the cell stays plain. Text stays dark for legibility
// since these are pale fills.
function pctBg(pct) {
  if (pct == null || Number.isNaN(pct)) return undefined;
  // Clamp the meaningful band to [10, 100]; <=10 is full red, >=100 full green.
  const t = Math.max(0, Math.min(1, (pct - 10) / 90));
  // Interpolate hue 0 (red) → 120 (green) in HSL, pale and legible.
  const hue = Math.round(120 * t);
  return `hsl(${hue}, 75%, 88%)`;
}
const fmtNum = (n) => n == null ? "—" : n.toLocaleString();
const fmtTemp = (min, max) => {
  const m = (v) => (v == null ? null : `${v}°C`);
  if (min == null && max == null) return "—";
  if (min != null && max != null) return min === max ? m(min) : `${min}–${max}°C`;
  return m(min) ?? m(max);
};

// Flatten {branch, offenders[]} → flat rows
function flattenOffenders(results) {
  const out = [];
  for (const r of results || []) {
    for (const o of r.offenders || []) {
      out.push({ branchId: r.branchId, branchName: r.branchName, ...o });
    }
  }
  return out;
}

function GenericTable({ rows, columns, empty }) {
  const masked = useMaskNames();
  if (rows.length === 0) return <div style={{ fontSize: 13, color: "#059669" }}>{empty || "All good — nothing failing."}</div>;
  return (
    <table style={S.table}>
      <thead>
        <tr>{columns.map(c => <th key={c.key} style={S.th}>{c.label}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i}>
            {columns.map(c => (
              <td key={c.key} style={S.td}>
                {c.render ? c.render(row, masked) : row[c.key] ?? "—"}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const BRANCH_COL = { key: "branchName", label: "Branch",
  render: (r, masked) => <b>{displayName(r.branchName, masked)}</b> };

// Urgent-rule formatters keyed by `${module}/${rule}`
const URGENT_FORMATTERS = {
  "fridge/latest-in-range": [
    BRANCH_COL,
    { key: "fridgeName", label: "Fridge", render: r => r.fridgeName + (r.location ? ` (${r.location})` : "") },
    { key: "readingAt", label: "Last reading", render: r => fmtDateTime(r.readingAt) },
    { key: "temp", label: "Min / Max", render: r => `${r.minTemperature ?? "—"}°C / ${r.maxTemperature ?? "—"}°C` },
    { key: "recheckIn", label: "Recheck in (hrs)", render: r => r.recheckIn },
    { key: "comments", label: "Comments", render: r => r.comments }
  ],
  "fridge/recheck-overdue": [
    BRANCH_COL,
    { key: "fridgeName", label: "Fridge", render: r => r.fridgeName },
    { key: "flaggedAt", label: "Flagged at", render: r => fmtDateTime(r.flaggedAt) },
    { key: "recheckHours", label: "Should recheck within", render: r => `${r.recheckHours} hrs` },
    { key: "deadline", label: "Deadline was", render: r => fmtDateTime(r.deadline) },
    { key: "hoursOverdue", label: "Hours overdue", render: r =>
      <span style={S.urgent}>{r.hoursOverdue}</span> }
  ],
  "fridge/logged-today": [
    BRANCH_COL,
    { key: "fridgeName", label: "Fridge", render: r => r.fridgeName },
    { key: "location", label: "Location" }
  ],
  "cleaning/overdue": [
    BRANCH_COL,
    { key: "areaName", label: "Area" },
    { key: "nextDue", label: "Was due", render: r => fmtDate(r.nextDue) },
    { key: "daysOverdue", label: "Days overdue",
      render: r => <span style={S.urgent}>{r.daysOverdue}</span> }
  ],
  "cleaning/unlogged": [
    BRANCH_COL,
    { key: "areaName", label: "Area" },
    { key: "scheduledSince", label: "Scheduled since", render: r => fmtDate(r.scheduledSince) }
  ],
  "cleaning/on-time-pct-30d": [
    BRANCH_COL,
    { key: "areaName", label: "Area" },
    { key: "actualCleans", label: "Cleans (30d)" },
    { key: "onTimeCleans", label: "On-time" },
    { key: "lateCleans", label: "Late" },
    { key: "onTimePct", label: "On-time %", render: r => fmtPct(r.onTimePct) },
    { key: "threshold", label: "Threshold", render: r => fmtPct(r.threshold) }
  ],
  "handover/overdue": [
    BRANCH_COL,
    { key: "topic", label: "Topic" },
    { key: "dueDate", label: "Was due", render: r => fmtDate(r.dueDate) },
    { key: "daysOverdue", label: "Days overdue",
      render: r => <span style={S.urgent}>{r.daysOverdue}</span> },
    { key: "fao", label: "FAO" },
    { key: "patientName", label: "Patient" }
  ],
  "date-checking/overdue": [
    BRANCH_COL,
    { key: "areaName", label: "Area" },
    { key: "nextDue", label: "Was due", render: r => fmtDate(r.nextDue) },
    { key: "daysOverdue", label: "Days overdue",
      render: r => <span style={S.urgent}>{r.daysOverdue}</span> }
  ],
  "date-checking/unlogged": [
    BRANCH_COL,
    { key: "areaName", label: "Area" },
    { key: "scheduledSince", label: "Scheduled since", render: r => fmtDate(r.scheduledSince) }
  ],
  "cd-register/balance-check-overdue": [
    BRANCH_COL,
    { key: "registerName", label: "Register",
      render: r => r.registerName || r.drugName || String(r.registerId).slice(-6) },
    { key: "balanceCheckedAt", label: "Last checked",
      render: r => r.balanceCheckedAt ? fmtDate(r.balanceCheckedAt) : "never" },
    { key: "intervalDays", label: "Interval (days)" },
    { key: "daysOverdue", label: "Days overdue",
      render: r => <span style={S.urgent}>{r.daysOverdue}</span> }
  ],
  "cd-register/open-discrepancies": [
    BRANCH_COL,
    { key: "registerName", label: "Register",
      render: r => r.registerName || r.drugName || "—" },
    { key: "createdAt", label: "Opened", render: r => fmtDate(r.createdAt) },
    { key: "daysOpen", label: "Days open",
      render: r => <span style={S.urgent}>{r.daysOpen}</span> },
    { key: "notes", label: "Notes" }
  ],
  "accident-book/review-overdue": [
    BRANCH_COL,
    { key: "accidentDate", label: "Accident date",
      render: r => fmtDate(r.accidentDate) },
    { key: "injuredName", label: "Person" },
    { key: "daysOverdue", label: "Days overdue",
      render: r => <span style={S.urgent}>{r.daysOverdue}</span> },
    { key: "targetDays", label: "Target (days)" }
  ],
  "rp-signin/not-currently-covered": [
    BRANCH_COL,
    { key: "lastSignOutAt", label: "Last signed out",
      render: r => fmtDateTime(r.lastSignOutAt) },
    { key: "minutesUncovered", label: "Mins uncovered",
      render: r => <span style={S.urgent}>{r.minutesUncovered}</span> }
  ],
  "rp-signin/stale-open-sessions": [
    BRANCH_COL,
    { key: "signInTime", label: "Signed in", render: r => fmtDateTime(r.signInTime) },
    { key: "hoursOpen", label: "Hrs open",
      render: r => <span style={S.urgent}>{r.hoursOpen}</span> },
    { key: "sessionMaxHours", label: "Max (cfg)" }
  ]
};

// Snapshot formatters: take the per-branch fleetStats array (or messaging
// multi-snapshot object) and return JSX.

function SnapshotKVRow({ data, keys }) {
  const masked = useMaskNames();
  // Render a flat object as a label/value table given a key list:
  // [{key, label, fmt?}]
  return (
    <table style={S.table}>
      <thead>
        <tr>
          <th style={S.th}>Branch</th>
          {keys.map(k => <th key={k.key} style={S.th}>{k.label}</th>)}
        </tr>
      </thead>
      <tbody>
        {data.map((row, i) => (
          <tr key={i}>
            <td style={S.td}><b>{displayName(row.branchName, masked)}</b></td>
            {keys.map(k => (
              <td key={k.key} style={S.td}>
                {k.fmt ? k.fmt(row[k.key], row) : (row[k.key] ?? "—")}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// Open rounds — one row per ROUND, not per branch
// ---------------------------------------------------------------------------
// SnapshotKVRow renders one row per branch, which is right for a module whose
// snapshot is a set of per-branch totals. A round is not a branch-level fact:
// two drivers out of the same depot are two rows, and collapsing them would
// hide exactly the thing this card exists to show.
//
// `outstanding` is derived when the server does not send it, so the column is
// never blank — but the server should send it, because "outstanding" is a
// business rule (a cancelled or returned drop is neither delivered, failed nor
// outstanding) and subtraction here would quietly get that wrong.
// Reads the same live feed FleetMap uses (one call, every driver with their
// remaining stops nested) and indexes it by roundId so a round row can show
// its own stop list on demand instead of a second per-round endpoint. Only
// OUTSTANDING stops come back from this feed — a round that has finished
// delivering has nothing left to show here, which is why the caller falls
// back to the delivered/failed totals it already has for that case.
function useRoundStopFeed() {
  const [byRound, setByRound] = useState({});
  const [loadedAt, setLoadedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const t = localStorage.getItem("token") || localStorage.getItem("authToken")
        || localStorage.getItem("jwt") || "";
      const res = await fetch(`${API_BASE}/api/headoffice/map/live`, {
        headers: { Authorization: `Bearer ${t}` }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const map = {};
      (Array.isArray(data.drivers) ? data.drivers : []).forEach(d => {
        if (d.roundId !== undefined && d.roundId !== null) map[String(d.roundId)] = d;
      });
      setByRound(map);
      setLoadedAt(data.generatedAt || new Date().toISOString());
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  return { byRound, loadedAt, loading, err, load };
}

// One round's stop list, shown inline under its row. Outstanding stops come
// with an ETA (same estimate the fleet map shows); delivered/failed are
// already itemised nowhere on this feed, so they are summarised instead of
// faked into rows they don't have.
function RoundDropDetail({ delivered, failed, outstanding, live, loading, err, loadedAt, onRefresh, masked }) {
  const drops = live?.drops || [];

  return (
    <div style={{ padding: "6px 4px 10px" }} onClick={e => e.stopPropagation()}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11.5, color: "#374151" }}>
          <b>{delivered}</b> delivered · <b style={{ color: failed > 0 ? "#b91c1c" : undefined }}>{failed}</b> failed
          {" · "}<b>{outstanding}</b> still to go
        </span>
        {live?.driverName && (
          <span style={{ fontSize: 11, color: "#6b7280" }}>
            {displayName(live.driverName, masked)}
            {live.positionSource && live.positionSource !== "driver" && " · position not current"}
          </span>
        )}
        <div style={{ flex: 1 }} />
        {loadedAt && (
          <span style={{ fontSize: 10.5, color: "#9ca3af" }} title="When the stop list below was last fetched.">
            Stop list as of {fmtDateTime(loadedAt)}
          </span>
        )}
        <button
          onClick={onRefresh}
          style={{ fontSize: 11, padding: "2px 8px", cursor: "pointer",
                   border: "1px solid #d1d5db", borderRadius: 4, background: "#fff" }}>
          {loading ? "Refreshing…" : "\u21bb Refresh"}
        </button>
      </div>

      {loading && !live && !err && (
        <div style={{ fontSize: 12, color: "#6b7280" }}>Loading stop list…</div>
      )}

      {err && (
        <div style={{ fontSize: 12, color: "#b91c1c" }}>
          Couldn't load stop details ({err}).
        </div>
      )}

      {!loading && !err && outstanding === 0 && (
        <div style={{ fontSize: 12, color: "#6b7280" }}>
          Nothing left aboard — round is finished but not yet closed out.
        </div>
      )}

      {!loading && !err && outstanding > 0 && !live && (
        <div style={{ fontSize: 12, color: "#6b7280" }}>
          Not showing on the live map yet — the driver may not have reported a position.
        </div>
      )}

      {!loading && drops.length > 0 && (
        <table style={{ ...S.table, marginTop: 2 }}>
          <thead>
            <tr>
              <th style={S.th}>#</th>
              <th style={S.th}>Customer</th>
              <th style={S.th}>Parcels</th>
              <th style={S.th}>Status</th>
              <th style={S.th}>ETA</th>
            </tr>
          </thead>
          <tbody>
            {drops.map((d, i) => {
              const eta = fmtEta(d);
              return (
                <tr key={d.recipientId ?? i}>
                  <td style={S.td}>{d.dropsAway === null ? "—" : i + 1}</td>
                  <td style={S.td}>
                    {displayName(d.recipientName, masked)}
                    {(d.address || d.postcode) && (
                      <div style={{ color: "#6b7280", fontSize: 11 }}>
                        {[d.address, d.postcode].filter(Boolean).join(", ")}
                      </div>
                    )}
                  </td>
                  <td style={S.td}>{d.parcels ?? 1}</td>
                  <td style={S.td}>
                    <span style={{ background: "#fef3c7", color: "#92400e", borderRadius: 4,
                                    padding: "2px 6px", fontSize: 11, fontWeight: 700 }}>
                      Outstanding
                    </span>
                  </td>
                  <td style={S.td}>
                    {eta ? (
                      <span title={etaBasisText(d.etaBasis)}>{eta}</span>
                    ) : d.dropsAway === null ? (
                      <span style={{ color: "#6b7280" }}>Not sequenced</span>
                    ) : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function OpenRoundsTable({ rows }) {
  const masked = useMaskNames();
  const [expandedId, setExpandedId] = useState(null);
  const stopFeed = useRoundStopFeed();

  const toggleRow = (roundId) => {
    const key = String(roundId);
    const opening = expandedId !== key;
    setExpandedId(opening ? key : null);
    // Fetch (or refresh) the stop list the moment a row is opened, rather
    // than up front for every round — most rounds on this screen are never
    // clicked, and this feed is the same one FleetMap already polls, so
    // there is no reason to pay for it twice on page load.
    if (opening) stopFeed.load();
  };

  const sorted = [...rows].sort((a, b) => {
    const bn = String(a.branchName || "").localeCompare(String(b.branchName || ""));
    if (bn !== 0) return bn;
    return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
  });

  // How long the round has been open. The clock time answers "when did he
  // leave"; the elapsed figure answers "should I be worried", which is the
  // question someone opening this screen is actually asking.
  const since = (iso) => {
    const t = new Date(iso);
    if (!iso || Number.isNaN(t.getTime())) return null;
    const mins = Math.floor((Date.now() - t.getTime()) / 60000);
    if (mins < 0) return null;
    if (mins < 60) return `${mins}m`;
    return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  };

  const num = (v) => (typeof v === "number" ? v : Number(v) || 0);

  // Row tint, worst state first. A round can be both stalled and late, so the
  // order here is a precedence, not a set of independent tests — and a stalled
  // van is the one that needs a phone call, so it wins.
  //
  //   red    nothing delivered for 90 minutes
  //   amber  out past 6pm
  //   green  out, moving, drops still aboard
  //   plain  nothing left to deliver — the round is done but not closed, so
  //          green would claim a driver is working when he is on his way back
  const ROW_BG = { stalled: "#fee2e2", late: "#fef3c7", moving: "#dcfce7" };
  const rowState = (r, outstanding) =>
      r.noProgress ? "stalled"
    : r.openLate   ? "late"
    : outstanding > 0 ? "moving"
    : null;

  return (
    <table style={S.table}>
      <thead>
        <tr>
          <th style={S.th}>Branch</th>
          <th style={S.th}>Driver</th>
          <th style={S.th}>Started</th>
          <th style={S.th}>Drops</th>
          <th style={S.th}>Delivered</th>
          <th style={S.th}>Failed</th>
          <th style={S.th}>Outstanding</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((r, i) => {
          const drops     = num(r.totalDrops);
          const delivered = num(r.delivered);
          const failed    = num(r.failed);
          const outstanding = (r.outstanding === undefined || r.outstanding === null)
            ? Math.max(drops - delivered - failed, 0)
            : num(r.outstanding);
          const elapsed = since(r.createdAt);
          const state = rowState(r, outstanding);
          const bg = state ? ROW_BG[state] : undefined;
          const stateTip = state === "stalled" ? `No drop for ${num(r.quietMinutes)} minutes.`
            : state === "late"   ? "Still out after 6pm."
            : state === "moving" ? "Out and delivering."
            : "Nothing left aboard — round not yet closed.";
          const key = String(r.roundId ?? i);
          const isOpen = expandedId === key;

          return (
            <React.Fragment key={key}>
              <tr style={{ background: bg, cursor: "pointer" }} title={stateTip}
                  onClick={() => toggleRow(r.roundId ?? i)}>
                <td style={S.td}>
                  <span style={{ display: "inline-block", width: 12, color: "#6b7280" }}>
                    {isOpen ? "\u25be" : "\u25b8"}
                  </span>
                  <b>{displayName(r.branchName, masked)}</b>
                </td>
                <td style={S.td}>{displayName(r.driverName, masked) || "—"}</td>
                <td style={S.td}>
                  {fmtDateTime(r.createdAt)}
                  {elapsed && (
                    <span style={{ color: "#6b7280", fontSize: 11 }}>
                      {" \u00b7 "}{elapsed} ago
                    </span>
                  )}
                </td>
                <td style={S.td}>{drops}</td>
                <td style={S.td}>{delivered}</td>
                <td style={S.td}>
                  {failed > 0 ? <span style={S.urgent}>{failed}</span> : failed}
                </td>
                <td style={S.td}>{outstanding}</td>
              </tr>
              {isOpen && (
                <tr>
                  <td style={{ ...S.td, background: "#f9fafb" }} colSpan={7}>
                    <RoundDropDetail
                      delivered={delivered}
                      failed={failed}
                      outstanding={outstanding}
                      live={stopFeed.byRound[key]}
                      loading={stopFeed.loading}
                      err={stopFeed.err}
                      loadedAt={stopFeed.loadedAt}
                      onRefresh={stopFeed.load}
                      masked={masked}
                    />
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// Today's deliveries — one row per customer, searchable
// ---------------------------------------------------------------------------
// Filtering happens here rather than on the server. A day's work for one group
// is hundreds of rows, not millions, so a client-side filter is instant and
// needs no debounce, no re-fetch and no query plumbing. If a group ever gets
// big enough for that to stop being true, the fix is a `search` param on the
// endpoint — but building that now would be paying for a problem nobody has.
//
// Barcodes are matched but not shown. A bag reference identifies a PARCEL while
// a row is a CUSTOMER, so searching one has to surface the row holding it;
// printing every barcode in the row would swamp the name it is meant to find.
function TodayDeliveriesTable({ rows }) {
  const masked = useMaskNames();
  const [q, setQ] = useState("");

  const needle = q.trim().toLowerCase();
  const shown = !needle ? rows : rows.filter(r => {
    const hay = [
      r.recipientName, r.address, r.postcode, r.externalRef,
      ...(r.barcodes || [])
    ].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(needle);
  });

  const STATUS_BG = {
    delivered: "#dcfce7", failed: "#fee2e2", on_round: "#fef3c7",
    // A collection in the driver's van: out with a driver, a shade warmer.
    collected: "#ffedd5",
    pending: "#f3f4f6", returned: "#e0e7ff", cancelled: "#f3f4f6"
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Name, address, postcode, reference or bag barcode"
          style={{ flex: 1, maxWidth: 420, padding: "6px 8px", fontSize: 13,
                   border: "1px solid #d1d5db", borderRadius: 4 }}
        />
        <span style={{ fontSize: 12, color: "#6b7280" }}>
          {shown.length} of {rows.length} customer{rows.length === 1 ? "" : "s"}
        </span>
        {needle && (
          <button onClick={() => setQ("")}
                  style={{ fontSize: 12, padding: "4px 8px", cursor: "pointer" }}>
            Clear
          </button>
        )}
      </div>

      {shown.length === 0 ? (
        <div style={{ fontSize: 12, color: "#6b7280" }}>
          Nothing matches “{q}”.
        </div>
      ) : (
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Branch</th>
              <th style={S.th}>Customer</th>
              <th style={S.th}>Status</th>
              <th style={S.th}>Parcels</th>
              <th style={S.th}
                  title="How many stops the driver has before this one, and an estimate of when they will arrive. The estimate measures the remaining route from the driver's last reported position and allows two minutes at each door, so treat it as a guide rather than a booked time.">
                Drops away
              </th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r, i) => (
              <tr key={`${r.recipientId}-${i}`}>
                <td style={S.td}><b>{displayName(r.branchName, masked)}</b></td>
                <td style={S.td}>
                  {displayName(r.recipientName, masked)}
                  {(r.address || r.postcode) && (
                    <div style={{ color: "#6b7280", fontSize: 11 }}>
                      {[r.address, r.postcode].filter(Boolean).join(", ")}
                    </div>
                  )}
                </td>
                <td style={{ ...S.td, background: STATUS_BG[r.statusKey] }}>
                  {r.status}
                  {r.parcels > 1 && (r.delivered > 0 || r.failed > 0) && (
                    <div style={{ color: "#6b7280", fontSize: 11 }}>
                      {r.delivered} delivered, {r.failed} failed, {r.outstanding} to go
                    </div>
                  )}
                </td>
                <td style={S.td}>{r.parcels}</td>
                <td style={S.td}>
                  {r.outstanding === 0 ? "—"
                    : r.dropsAway === null ? <span style={{ color: "#6b7280" }}>Not sequenced</span>
                    : r.dropsAway === 0 ? <b>Next</b>
                    : r.dropsAway}
                  {/* The estimate sits UNDER the position rather than in its
                      own column: they answer one question between them, and a
                      row with no coordinates or no route position shows the
                      position line alone rather than an empty cell. */}
                  {r.outstanding > 0 && fmtEta(r) && (
                    <div style={{ color: "#6b7280", fontSize: 11 }}
                         title={etaBasisText(r.etaBasis)}>
                      {fmtEta(r)}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Exception queue — one row per parcel
// ---------------------------------------------------------------------------
// Per parcel, not per customer, unlike the other two grids. There the question
// is "who is waiting"; here it is "what do I do with this", and the answer is
// per bag — return this one, try that one again, cancel the third. Two bags for
// one address can need different outcomes and a grouped row would hide it.
function FailedQueueTable({ rows }) {
  const masked = useMaskNames();
  const [q, setQ] = useState("");

  const needle = q.trim().toLowerCase();
  const shown = !needle ? rows : rows.filter(r => {
    const hay = [
      r.recipientName, r.address, r.postcode, r.externalRef,
      r.barcode, r.failureReason, r.driverName
    ].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(needle);
  });

  // Precedence, not independent tests — a parcel can be both, and the phone
  // call outranks the wait.
  const ROW_BG = { call: "#fee2e2", stale: "#fef3c7" };
  const rowState = (r) => r.needsCall ? "call" : r.stale ? "stale" : null;

  const age = (hours) => {
    if (hours === null || hours === undefined) return "—";
    if (hours < 24) return `${hours}h`;
    return `${Math.floor(hours / 24)}d`;
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Name, address, postcode, bag barcode, reason or driver"
          style={{ flex: 1, maxWidth: 420, padding: "6px 8px", fontSize: 13,
                   border: "1px solid #d1d5db", borderRadius: 4 }}
        />
        <span style={{ fontSize: 12, color: "#6b7280" }}>
          {shown.length} of {rows.length} parcel{rows.length === 1 ? "" : "s"}
        </span>
        {needle && (
          <button onClick={() => setQ("")}
                  style={{ fontSize: 12, padding: "4px 8px", cursor: "pointer" }}>
            Clear
          </button>
        )}
      </div>

      {shown.length === 0 ? (
        <div style={{ fontSize: 12, color: "#6b7280" }}>Nothing matches “{q}”.</div>
      ) : (
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Branch</th>
              <th style={S.th}>Customer</th>
              <th style={S.th}>Bag</th>
              <th style={S.th}>Attempts</th>
              <th style={S.th}>Last tried</th>
              <th style={S.th}>Reason</th>
              <th style={S.th}>State</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => {
              const state = rowState(r);
              return (
                <tr key={r.deliveryId} style={{ background: state ? ROW_BG[state] : undefined }}>
                  <td style={S.td}><b>{displayName(r.branchName, masked)}</b></td>
                  <td style={S.td}>
                    {displayName(r.recipientName, masked)}
                    {(r.address || r.postcode) && (
                      <div style={{ color: "#6b7280", fontSize: 11 }}>
                        {[r.address, r.postcode].filter(Boolean).join(", ")}
                      </div>
                    )}
                    {r.phone && (
                      <div style={{ fontSize: 11 }}>
                        <a href={`tel:${r.phone}`}>{r.phone}</a>
                      </div>
                    )}
                  </td>
                  <td style={{ ...S.td, fontFamily: "monospace", fontSize: 11 }}>
                    {r.barcode || "—"}
                  </td>
                  <td style={S.td}>
                    {r.needsCall ? <span style={S.urgent}>{r.attempts}</span> : r.attempts}
                  </td>
                  <td style={S.td}>
                    {fmtDateTime(r.lastAttemptAt)}
                    <div style={{ color: "#6b7280", fontSize: 11 }}>{age(r.hoursSince)} ago</div>
                  </td>
                  <td style={S.td}>
                    {r.failureReason || <span style={{ color: "#6b7280" }}>Not given</span>}
                    {r.driverName && (
                      <div style={{ color: "#6b7280", fontSize: 11 }}>
                        {displayName(r.driverName, masked)}
                      </div>
                    )}
                  </td>
                  <td style={S.td}>{r.status}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Driver roster — one row per driver per branch
// ---------------------------------------------------------------------------
// Per branch rather than per person: a driver can be allocated to more than one,
// and a shift belongs to the branch it was worked at. Merging them would give a
// row whose hours belong to two places at once and reconcile against neither.
function DriverRosterTable({ rows }) {
  const masked = useMaskNames();
  const [q, setQ] = useState("");

  const needle = q.trim().toLowerCase();
  const shown = !needle ? rows : rows.filter(r =>
    [r.driverName, r.branchName].filter(Boolean).join(" ").toLowerCase().includes(needle));

  // Amber, not red: both of these are paperwork, not a van in a ditch. Nothing
  // on this card is an emergency, and colouring it as though it were would
  // teach people to ignore the ones that are.
  const rowState = (r) => r.staleOpen ? "open" : r.missingMileage > 0 ? "mileage" : null;
  const ROW_BG = { open: "#fef3c7", mileage: "#fffbeb" };

  const openFor = (since) => {
    const t = new Date(since);
    if (!since || Number.isNaN(t.getTime())) return null;
    const h = Math.floor((Date.now() - t.getTime()) / 3600000);
    return h < 24 ? `${h}h` : `${Math.floor(h / 24)}d`;
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Driver or branch"
          style={{ flex: 1, maxWidth: 320, padding: "6px 8px", fontSize: 13,
                   border: "1px solid #d1d5db", borderRadius: 4 }}
        />
        <span style={{ fontSize: 12, color: "#6b7280" }}>
          {shown.length} of {rows.length}
        </span>
        {needle && (
          <button onClick={() => setQ("")}
                  style={{ fontSize: 12, padding: "4px 8px", cursor: "pointer" }}>
            Clear
          </button>
        )}
      </div>

      {shown.length === 0 ? (
        <div style={{ fontSize: 12, color: "#6b7280" }}>Nothing matches “{q}”.</div>
      ) : (
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Branch</th>
              <th style={S.th}>Driver</th>
              <th style={S.th}>Now</th>
              <th style={S.th}>Shifts (7d)</th>
              <th style={S.th}>Hours (7d)</th>
              <th style={S.th}>Miles (7d)</th>
              <th style={S.th}>Paperwork</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r, i) => {
              const state = rowState(r);
              return (
                <tr key={`${r.branchName}-${r.driverName}-${i}`}
                    style={{ background: state ? ROW_BG[state] : undefined }}>
                  <td style={S.td}><b>{displayName(r.branchName, masked)}</b></td>
                  <td style={S.td}>{displayName(r.driverName, masked)}</td>
                  <td style={S.td}>
                    {r.onShiftNow ? (
                      <span style={{ color: "#15803d", fontWeight: 600 }}>
                        On shift
                        <span style={{ color: "#6b7280", fontWeight: 400, fontSize: 11 }}>
                          {" \u00b7 "}{openFor(r.openSince) || "—"}
                        </span>
                      </span>
                    ) : (
                      <span style={{ color: "#6b7280" }}>Off</span>
                    )}
                  </td>
                  <td style={S.td}>{r.shifts}</td>
                  {/* An open shift has no length, so a driver mid-shift shows
                      last week's hours and nothing for today. Saying so beats
                      leaving someone to wonder why the number looks short. */}
                  <td style={S.td}>
                    {r.hours}
                    {r.onShiftNow && (
                      <span style={{ color: "#6b7280", fontSize: 11 }}>
                        {" "}+ today
                      </span>
                    )}
                  </td>
                  <td style={S.td}>{r.miles}</td>
                  <td style={S.td}>
                    {r.staleOpen && (
                      <div style={{ color: "#b45309", fontWeight: 600 }}>
                        Shift left open{r.openShiftDate ? ` from ${fmtDate(r.openShiftDate)}` : ""}
                      </div>
                    )}
                    {r.missingMileage > 0 && (
                      <div>{r.missingMileage} shift{r.missingMileage === 1 ? "" : "s"} without mileage</div>
                    )}
                    {!r.staleOpen && r.missingMileage === 0 && (
                      <span style={{ color: "#6b7280" }}>—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

const SNAPSHOT_FORMATTERS = {
  drivers: (data) => {
    const rows = Array.isArray(data) ? data : [];
    if (!rows.length) {
      return (
        <div style={{ fontSize: 12, color: "#6b7280" }}>
          No shifts recorded in the last 7 days.
        </div>
      );
    }
    return <DriverRosterTable rows={rows} />;
  },
  "failed-attempts": (data) => {
    const rows = Array.isArray(data) ? data : [];
    if (!rows.length) {
      return (
        <div style={{ fontSize: 12, color: "#6b7280" }}>
          Nothing in the queue — every parcel has been delivered, returned or cancelled.
        </div>
      );
    }
    return <FailedQueueTable rows={rows} />;
  },
  "deliveries-today": (data) => {
    const rows = Array.isArray(data) ? data : [];
    if (!rows.length) {
      return (
        <div style={{ fontSize: 12, color: "#6b7280" }}>
          Nothing delivered, failed or outstanding today.
        </div>
      );
    }
    return <TodayDeliveriesTable rows={rows} />;
  },
  rounds: (data) => {
    const rows = Array.isArray(data) ? data : [];
    if (!rows.length) {
      return (
        <div style={{ fontSize: 12, color: "#6b7280" }}>
          No rounds open anywhere in the group.
        </div>
      );
    }
    return <OpenRoundsTable rows={rows} />;
  },
  handover: (data) => (
    <SnapshotKVRow data={data} keys={[
      { key: "totalPending", label: "Pending" },
      { key: "totalOverdue", label: "Overdue",
        fmt: v => v > 0 ? <span style={S.urgent}>{v}</span> : v },
      { key: "completedLast30d", label: "Completed (30d)" },
      { key: "completedOnTimeLast30d", label: "On-time" },
      { key: "completedLateLast30d", label: "Late" },
      { key: "onTimePctLast30d", label: "On-time %", fmt: v => fmtPct(v) }
    ]} />
  ),
  "date-checking": (data) => (
    <SnapshotKVRow data={data} keys={[
      { key: "currentlyExpired", label: "Expired on shelf",
        fmt: v => v > 0 ? <span style={S.urgent}>{v}</span> : v },
      { key: "expiringWithin30Days", label: "Expiring <30d" },
      { key: "expiringWithin90Days", label: "Expiring <90d" },
      { key: "drugsLoggedLast30d", label: "Logged (30d)" },
      { key: "drugsRemovedLast30d", label: "Removed (30d)" },
      { key: "drugsMarkedNotPresentLast30d", label: "Not present (30d)" }
    ]} />
  ),
  prescriptions: (data) => (
    <SnapshotKVRow data={data} keys={[
      { key: "onShelf", label: "On shelf",
        fmt: v => v ? ((v.collection?.total || 0) + (v.delivery?.total || 0)) : "—" },
      { key: "onShelf", label: ">2 months on shelf",
        fmt: v => v ? ((v.collection?.twoMonthsOrMore || 0) +
                       (v.delivery?.twoMonthsOrMore || 0)) : "—" },
      { key: "monthToDate", label: "Scanned (MTD)",
        fmt: v => v ? (v.scanned ?? 0) : "—" },
      { key: "monthToDate", label: "Collected",
        fmt: v => v ? (v.collected ?? 0) : "—" },
      { key: "monthToDate", label: "Delivered via app",
        fmt: v => v ? (v.deliveredViaApp ?? 0) : "—" },
      { key: "monthToDate", label: "Delivered no app",
        fmt: v => v ? (v.deliveredWithoutApp ?? 0) : "—" }
    ]} />
  ),
  "cd-register": (data) => (
    <SnapshotKVRow data={data} keys={[
      { key: "activeRegisters", label: "Active registers" },
      { key: "registersWithExpiredStock", label: "Expired stock",
        fmt: v => v > 0 ? <span style={S.urgent}>{v}</span> : v },
      { key: "registersOverdueBalanceCheck", label: "Balance overdue",
        fmt: v => v > 0 ? <span style={S.urgent}>{v}</span> : (v ?? 0) },
      { key: "openDiscrepancies", label: "Open discrepancies",
        fmt: v => v > 0 ? <span style={S.urgent}>{v}</span> : v }
    ]} />
  ),
  "patient-safety": (data) => (
    <SnapshotKVRow data={data} keys={[
      { key: "last30Days", label: "Total (30d)",
        fmt: v => v?.total ?? 0 },
      { key: "last30Days", label: "LFPSE submitted",
        fmt: v => fmtPct(v?.lfpseSubmittedPct) },
      { key: "last30Days", label: "RP notified",
        fmt: v => fmtPct(v?.rpNotifiedPct) }
    ]} />
  ),
  "accident-book": (data) => (
    <SnapshotKVRow data={data} keys={[
      { key: "last30Days", label: "Accidents (30d)",
        fmt: v => v?.accidents ?? 0 },
      { key: "reviewOverdueCount", label: "Reviews overdue",
        fmt: v => v > 0 ? <span style={S.urgent}>{v}</span> : (v ?? 0) },
      { key: "last30Days", label: "Reviewed %",
        fmt: v => fmtPct(v?.reviewedPct) }
    ]} />
  ),
  "rp-signin": (data) => (
    <SnapshotKVRow data={data} keys={[
      { key: "branchCurrentlyOpen", label: "Currently open",
        fmt: v => v ? "Yes" : "No" },
      { key: "currentlySignedIn", label: "RP signed in",
        fmt: v => v ? "Yes" : <span style={S.urgent}>No</span> },
      { key: "staleOpenSessionsCount", label: "Stale sessions",
        fmt: v => v > 0 ? <span style={S.urgent}>{v}</span> : (v ?? 0) },
      { key: "last30Days", label: "Sign-ins (30d)",
        fmt: v => v?.sessionsCount ?? "—" }
    ]} />
  ),
  "visitor-logs": (data) => (
    <SnapshotKVRow data={data} keys={[
      { key: "currentlyOnSite", label: "Currently on site",
        fmt: v => v > 0 ? <b>{v}</b> : v },
      { key: "last30Days", label: "Visits (30d)" }
    ]} />
  )
};


function PeriodicEntriesCard({ token, onOpen, onOpenOverdue }) {
  const [summary, setSummary] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const g = await apiGet("periodic-tasks/grid", token);
        if (!cancelled) setSummary(g?.summary || null);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const overdue = summary?.sitesOverdue ?? null;
  const missing = summary?.sitesMissing ?? null;
  const withEntries = summary?.sitesWithEntries ?? null;
  const total = summary?.totalSites ?? null;
  const hasOverdue = typeof overdue === "number" && overdue > 0;
  const hasMissing = typeof missing === "number" && missing > 0;

  const borderColor = !loaded ? "#f59e0b"
    : (hasOverdue || hasMissing) ? "#ef4444" : "#22c55e";

  return (
    <div style={{ ...S.card, border: `3px solid ${borderColor}` }}
         onClick={() => onOpen()}>
      <div style={S.cardTitle}>
        Periodic entries
        <span style={{ fontWeight: 400, color: "#6b7280" }}
              title="Pharmacies with at least one recorded periodic value, out of all sites.">
          {" "}({withEntries ?? "…"}/{total ?? "…"})
        </span>
      </div>
      <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 8, lineHeight: 1.4 }}>
        Group-defined recurring data items recorded per branch. Click to open
        the grid.
      </div>
      <div style={{ ...S.metaRow,
                    background: hasOverdue ? "#fde2e1" : (loaded ? "#dcfce7" : undefined),
                    borderRadius: 4, padding: "2px 6px", margin: "0 -6px",
                    cursor: "pointer" }}
           title="Pharmacies with at least one overdue periodic item. Click to view them."
           onClick={(e) => { e.stopPropagation(); onOpenOverdue(); }}>
        <span>Pharmacies with overdue items</span>
        <span style={hasOverdue ? S.urgent : S.ok}>
          {overdue ?? "…"}
        </span>
      </div>
      <div style={{ ...S.metaRow,
                    background: hasMissing ? "#fde2e1" : (loaded ? "#dcfce7" : undefined),
                    borderRadius: 4, padding: "2px 6px", margin: "0 -6px" }}
           title="Pharmacies with at least one task never recorded.">
        <span>Sites with missing entries</span>
        <span style={hasMissing ? S.urgent : S.ok}>
          {missing ?? "…"}
        </span>
      </div>
      {error && <div style={S.error}>{error}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Messaging card — bespoke summary with three clickable zones:
//   1) Patients signed up %  → opens the enrolment grid (ModuleDetail)
//   2) Today's messaging (SMS/EMAIL/FCM) — informational
//   3) Number of sites with SIM issues → opens the SIM detail (placeholder)
// Each line carries a styled hover tooltip explaining what it means.
// ---------------------------------------------------------------------------

function ModuleCard({ mod, token, onSelect }) {
  const [active, setActive] = useState(null);
  const [dormant, setDormant] = useState(null);   // v43: configured but quiet
  const [total, setTotal] = useState(null);
  const [urgent, setUrgent] = useState({});
  const [okCount, setOkCount] = useState(null);
  const [figures, setFigures] = useState({});
  const [rollup, setRollup] = useState(null);   // single cardRollup payload
  const [rollups, setRollups] = useState({});    // cardRollups: { [endpoint]: payload }
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);  // false until all data is in

  const load = useCallback(async () => {
    try {
      setError(null);
      setLoaded(false);

      // "Ever used / total" ratio (no date window). Defaults to {path}/ever-used,
      // but a module may point it at a combined endpoint via everUsedEndpoint.
      if (mod.everUsedRatio) {
        try {
          const ep = mod.everUsedEndpoint || "ever-used";
          const eu = await apiGet(`${mod.path}/${ep}`, token);
          setActive(typeof eu?.everUsed === "number" ? eu.everUsed : 0);
          setTotal(typeof eu?.total === "number" ? eu.total : null);
        } catch (e) {
          setActive(0); setTotal(null);
        }
      } else {
        // `active-branches` answers "has this branch used the module lately"
        // and returns every visible branch, so its length is the branch count.
        // A module whose headline number is a live count of something needs a
        // different endpoint; activeEndpoint names it. Mirrors everUsedEndpoint.
        const a = await apiGet(`${mod.path}/${mod.activeEndpoint || "active-branches"}`, token);
        setActive(Array.isArray(a) ? a.length : 0);
        // v43: sites that are set up but have logged nothing inside the
        // activity window. They stay IN every count above — this row exists
        // so "configured and failing" and "configured and abandoned" can be
        // told apart without either one dropping off the dashboard.
        setDormant(Array.isArray(a) ? a.filter(x => x && x.dormant).length : 0);

        if (mod.showActiveRatioInTitle) {
          try {
            const sz = await apiGet(`fleet-size`, token);
            setTotal(typeof sz?.total === "number" ? sz.total : null);
          } catch (e) {
            setTotal(null);
          }
        }
      }

      const ur = {};
      for (const r of mod.urgentRules) {
        try {
          const failing = await apiGet(`${mod.path}/${r.rule}`, token);
          ur[r.rule] = Array.isArray(failing) ? failing.length : 0;
        } catch (e) {
          ur[r.rule] = "?";
        }
      }
      setUrgent(ur);

      // Custom single-endpoint rollup row (e.g. RP coverage today).
      if (mod.cardRollup) {
        try {
          const r = await apiGet(`${mod.path}/${mod.cardRollup.endpoint}`, token);
          setRollup(r && typeof r === "object" ? r : null);
        } catch (e) {
          setRollup(null);
        }
      }

      // Multiple rollup rows, possibly sharing endpoints — fetch each endpoint once.
      if (Array.isArray(mod.cardRollups) && mod.cardRollups.length) {
        const endpoints = [...new Set(mod.cardRollups.map(c => c.endpoint))];
        const out = {};
        for (const ep of endpoints) {
          try {
            const r = await apiGet(`${mod.path}/${ep}`, token);
            out[ep] = r && typeof r === "object" ? r : null;
          } catch (e) {
            out[ep] = null;
          }
        }
        setRollups(out);
      }

      // Reassurance figure: sites checked today with all fridges OK.
      if (mod.okStatusRule) {
        try {
          const statuses = await apiGet(`${mod.path}/${mod.okStatusRule}`, token);
          const arr = Array.isArray(statuses) ? statuses : [];
          setOkCount(arr.filter(s => s.status === "ok").length);
          // Sum any configured per-branch figures across the fleet.
          if (mod.statusFigures) {
            const sums = {};
            for (const f of mod.statusFigures) {
              sums[f.field] = arr.reduce((t, s) => t + (Number(s[f.field]) || 0), 0);
            }
            setFigures(sums);
          }
        } catch (e) {
          setOkCount("?");
        }
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoaded(true);
    }
  }, [mod, token]);

  useEffect(() => { load(); }, [load]);

  // Severity-ranked border. A rule's `severity` is "fail" (red) or "warn"
  // (amber); unmarked rules default to "fail" so other modules keep their
  // red-on-any-issue behaviour. Worst active severity wins.
  const failFiring = mod.urgentRules.some(
    r => (r.severity ?? "fail") === "fail" && (urgent[r.rule] ?? 0) > 0);
  const dormantFiring = !!(mod.dormantRow && typeof dormant === "number" && dormant > 0);
  const warnFiring = dormantFiring || mod.urgentRules.some(
    r => r.severity === "warn" && (urgent[r.rule] ?? 0) > 0);

  // A configured rollup with a non-zero count also pushes the card red.
  const rollupFiring = !!(mod.cardRollup && rollup &&
    Number(rollup[mod.cardRollup.countField]) > 0);

  // Multi-rollup firing: a "fail"-tone rollup >0 is red, "warn"-tone >0 is amber.
  // A rollup marked `quiet` is excluded — a card whose border goes red because
  // one parcel out of four hundred failed is red every day, and a border that is
  // always red tells you nothing. Rules raise alarms; rollups report.
  const rollupsFail = Array.isArray(mod.cardRollups) && mod.cardRollups.some(c =>
    !c.quiet && (c.tone ?? "fail") === "fail" &&
    Number((rollups[c.endpoint] || {})[c.field]) > 0);
  const rollupsWarn = Array.isArray(mod.cardRollups) && mod.cardRollups.some(c =>
    !c.quiet && c.tone === "warn" &&
    Number((rollups[c.endpoint] || {})[c.field]) > 0);

  // Three-state border: amber while loading, then red for a real failure,
  // amber for a warning-only state, green if all clear. Wider + bolder than 1px.
  const borderColor = !loaded ? "#f59e0b"        // amber — still loading
    : (failFiring || rollupFiring || rollupsFail) ? "#ef4444"   // red — action needed
    : (warnFiring || rollupsWarn) ? "#f59e0b"     // amber — warning only
    : "#22c55e";                                  // green — all clear

  return (
    <div
      style={{ ...S.card, border: `3px solid ${borderColor}` }}
      onClick={() => onSelect(mod)}
    >
      <div style={S.cardTitle}>
        {mod.title}
        {mod.showActiveRatioInTitle && (
          <span style={{ fontWeight: 400, color: "#6b7280" }}
                title={mod.configuredRatio || "Pharmacies using this module recently, out of all sites in the group."}>
            {" "}({active ?? "…"}/{total ?? "…"})
          </span>
        )}
        {mod.everUsedRatio && (
          <span style={{ fontWeight: 400, color: "#6b7280" }}
                title="Pharmacies that have ever recorded an entry in this module, out of all sites in the group.">
            {" "}({active ?? "…"}/{total ?? "…"})
          </span>
        )}
      </div>
      {mod.blurb && (
        <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 8,
                      lineHeight: 1.4 }}>
          {mod.blurb}
        </div>
      )}
      {mod.dormantRow && (() => {
        const known = typeof dormant === "number";
        const has = known && dormant > 0;
        return (
          <div style={{ ...S.metaRow, background: has ? "#fef3c7" : undefined,
                        borderRadius: has ? 4 : undefined,
                        padding: has ? "2px 6px" : undefined,
                        margin: has ? "0 -6px" : undefined }}
               title={mod.dormantRow.hint || ""}>
            <span>{mod.dormantRow.label}</span>
            <span style={has ? { color: "#b45309", fontWeight: 600 } : S.ok}>
              {known ? dormant : "…"}
            </span>
          </div>
        );
      })()}
      {!mod.showActiveRatioInTitle && !mod.everUsedRatio && (
        <div style={S.metaRow}
             title={mod.activeHint
                 || "Branches that have used this module in the last 90 days."}>
          <span>{mod.activeLabel || "Active branches"}</span>
          <span>{active ?? "…"}</span>
        </div>
      )}
      {mod.cardRollup && (() => {
        const cfg = mod.cardRollup;
        const cnt = rollup ? Number(rollup[cfg.countField]) : null;
        const tot = rollup ? Number(rollup[cfg.totalField]) : null;
        const known = typeof cnt === "number" && !Number.isNaN(cnt);
        const has = known && cnt > 0;
        // Green when all covered (0 uncovered), red when any uncovered.
        const rowBg = !known ? undefined : (has ? "#fde2e1" : "#dcfce7");
        return (
          <div style={{ ...S.metaRow, background: rowBg,
                        borderRadius: rowBg ? 4 : undefined,
                        padding: rowBg ? "2px 6px" : undefined,
                        margin: rowBg ? "0 -6px" : undefined }}
               title={cfg.hint || ""}>
            <span>{cfg.label}</span>
            <span style={has ? S.urgent : S.ok}>
              {known ? `${cnt} / ${tot ?? "…"}` : "…"}
            </span>
          </div>
        );
      })()}
      {mod.urgentRules.map(r => {
        const n = urgent[r.rule] ?? 0;
        const sev = r.severity ?? "fail";
        // Tint the row background when it has entries: red for fail-level,
        // amber for warn-level. Zero-count rows stay plain.
        const rowBg = (typeof n === "number" && n > 0)
          ? (sev === "warn" ? "#fef3c7" : "#fde2e1")
          : undefined;
        return (
          <div key={r.rule}
               style={{ ...S.metaRow, background: rowBg,
                        borderRadius: rowBg ? 4 : undefined,
                        padding: rowBg ? "2px 6px" : undefined,
                        margin: rowBg ? "0 -6px" : undefined }}
               title={r.hint || ""}>
            <span>{r.label}</span>
            <span style={n > 0 ? S.urgent : S.ok}>
              {urgent[r.rule] ?? "…"}
            </span>
          </div>
        );
      })}
      {Array.isArray(mod.cardRollups) && mod.cardRollups.map((cfg, idx) => {
        const payload = rollups[cfg.endpoint];
        const cnt = payload ? Number(payload[cfg.field]) : null;
        const known = typeof cnt === "number" && !Number.isNaN(cnt);
        const has = known && cnt > 0;
        const tone = cfg.tone ?? "fail";
        // Tinted only when the count is above zero — "ok" is a reassurance row,
        // and a reassuring green zero is a lie. "plain" never tints: it is for a
        // total, which is a denominator rather than a verdict, and a red total
        // would put the card permanently on fire for doing business as usual.
        const TONE_BG   = { warn: "#fef3c7", fail: "#fde2e1", ok: "#dcfce7" };
        const TONE_TEXT = {
          warn:  { color: "#b45309", fontWeight: 600 },
          fail:  S.urgent,
          ok:    { color: "#15803d", fontWeight: 600 },
          plain: { fontWeight: 600 }
        };
        const rowBg = has ? TONE_BG[tone] : undefined;
        const numStyle = tone === "plain" ? TONE_TEXT.plain
          : !has ? S.ok
          : (TONE_TEXT[tone] || S.urgent);
        return (
          <div key={`rollup-${idx}`}
               style={{ ...S.metaRow, background: rowBg,
                        borderRadius: rowBg ? 4 : undefined,
                        padding: rowBg ? "2px 6px" : undefined,
                        margin: rowBg ? "0 -6px" : undefined }}
               title={cfg.hint || ""}>
            <span>{cfg.label}</span>
            <span style={numStyle}>{known ? cnt : "…"}</span>
          </div>
        );
      })}
      {(mod.statusFigures || []).map(f => {
        const v = figures[f.field];
        const known = typeof v === "number";
        const has = known && v > 0;
        const TONE_BG = { fail: "#fde2e1", warn: "#fef3c7", ok: "#dcfce7" };
        // Pick the tone: value > 0 uses f.tone; value 0 uses f.zeroTone if set.
        const tone = !known ? null : (v > 0 ? f.tone : (f.zeroTone || null));
        const bg = tone ? TONE_BG[tone] : undefined;
        // Number colour: green when it's a good state, red/urgent when it's a problem.
        const numStyle = (tone === "ok" || (!has && f.zeroTone === "ok")) ? S.ok
          : (has ? S.urgent : S.ok);
        return (
          <div key={f.field}
               style={{ ...S.metaRow, background: bg,
                        borderRadius: bg ? 4 : undefined,
                        padding: bg ? "2px 6px" : undefined,
                        margin: bg ? "0 -6px" : undefined }}
               title={f.hint || ""}>
            <span>{f.label}</span>
            <span style={numStyle}>{v ?? "…"}</span>
          </div>
        );
      })}
      {mod.okStatusRule && (
        <div style={{ ...S.metaRow,
                      background: (typeof okCount === "number" && okCount > 0) ? "#dcfce7" : undefined,
                      borderRadius: (typeof okCount === "number" && okCount > 0) ? 4 : undefined,
                      padding: (typeof okCount === "number" && okCount > 0) ? "2px 6px" : undefined,
                      margin: (typeof okCount === "number" && okCount > 0) ? "0 -6px" : undefined }}
             title="Pharmacies that are fully up to date on this module right now.">
          <span>Sites checked and OK</span>
          <span style={S.ok}>{okCount ?? "…"}</span>
        </div>
      )}
      {error && <div style={S.error}>{error}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Visitor logs inline expand — that branch's visitors, reverse-chronological.
// Stale (on site, before today) = pink; current (on site, today) = amber.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Patient safety inline expand — that branch's incidents, reverse-chrono.
// Columns: date/time, degree of harm, LFPSE, RP reporting.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// CD register inline expand — that branch's individual registers (balance
// checking) plus its open discrepancies. Rendered inside the accordion row.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Drill-in view — shows urgent rule offenders + raw snapshot per branch
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// AreaFleetGrid — "all branches" grid for area-based modules (cleaning and
// date-checking areas). One row per branch, INCLUDING branches not using the
// module (hidden behind a "Show inactive" toggle, amber when shown). Sortable
// columns: active areas, inactive areas, never cleaned/checked, up to date
// (logged & not overdue), overdue. Row colour: green when nothing overdue and
// every active area has been logged; pale red when anything is overdue or has
// never been logged; amber when the branch has no active areas at all.
// Click a branch → AreaHistoryDialog (areas left, per-area log history right).
// ---------------------------------------------------------------------------
const AF_RED = "#fde2e1", AF_AMBER = "#fef3c7", AF_GREEN = "#dcfce7";

function AreaFleetGrid({ mod, token, noun }) {
  const masked = useMaskNames();
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [sort, setSort] = useState({ key: "overdue", dir: "desc" });
  const [openBranch, setOpenBranch] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await apiGet(`${mod.path}/area-overview`, token);
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mod, token]);

  const all = rows || [];
  const inactiveCount = all.filter(r => !r.inUse).length;

  const q = query.trim().toLowerCase();
  const visible = all
    .filter(r => showInactive || r.inUse)
    .filter(r => !q || (r.branchName || "").toLowerCase().includes(q));

  // Branch row colour. Amber (inactive) wins so unused branches stand apart;
  // active branches are red on any overdue OR never-logged area, else green.
  const rowBg = (r) => !r.inUse ? AF_AMBER
    : (r.overdue > 0 || r.neverLogged > 0) ? AF_RED : AF_GREEN;

  const cols = [
    { key: "branch",      label: "Branch",        get: r => (r.branchName || "").toLowerCase() },
    { key: "activeAreas",   label: "Active areas",    get: r => r.activeAreas ?? 0 },
    { key: "inactiveAreas", label: "Inactive areas",  get: r => r.inactiveAreas ?? 0 },
    { key: "neverLogged",   label: `Never ${noun}`,   get: r => r.neverLogged ?? 0 },
    { key: "upToDate",      label: "Up to date",      get: r => r.upToDate ?? 0 },
    { key: "overdue",       label: "Overdue",         get: r => r.overdue ?? 0 }
  ];
  const dir = sort.dir === "desc" ? -1 : 1;
  const activeCol = cols.find(c => c.key === sort.key) || cols[5];
  const sorted = [...visible].sort((a, b) => {
    const av = activeCol.get(a), bv = activeCol.get(b);
    let cmp = (typeof av === "string") ? av.localeCompare(bv) : (av - bv);
    if (cmp === 0) cmp = (a.branchName || "").localeCompare(b.branchName || "");
    return cmp * dir;
  });
  const toggleSort = (key) => setSort(prev =>
    prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
                     : { key, dir: key === "branch" ? "asc" : "desc" });
  const arrow = (key) =>
    sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : "";

  const attention = all.filter(r => r.inUse && (r.overdue > 0 || r.neverLogged > 0)).length;
  const good = all.filter(r => r.inUse && r.overdue === 0 && r.neverLogged === 0).length;

  return (
    <div style={{ marginTop: 8, marginBottom: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "center", marginBottom: 6 }}>
        <div style={{ fontWeight: 600 }}>
          Branch status{" "}
          <span style={{ fontWeight: 400, color: "#6b7280", fontSize: 13 }}>
            ({all.length} branch{all.length === 1 ? "" : "es"},{" "}
            {inactiveCount} not using the module)
          </span>
        </div>
        <button style={S.btn} onClick={() => setConfigOpen(true)}
                title="View every branch's active areas and cascade an area to all branches">
          Configuration
        </button>
      </div>

      {loading && <div style={{ fontSize: 13, color: "#6b7280" }}>Loading branches…</div>}
      {error && <div style={S.error}>{error}</div>}

      {!loading && !error && (
        <>
          {/* Summary chips */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
            <span style={{ background: AF_RED, borderRadius: 6, padding: "3px 10px",
                           fontSize: 12, fontWeight: 600, color: "#374151" }}>
              Needs attention: {attention}
            </span>
            <span style={{ background: AF_GREEN, borderRadius: 6, padding: "3px 10px",
                           fontSize: 12, fontWeight: 600, color: "#374151" }}>
              All up to date: {good}
            </span>
            <span style={{ background: AF_AMBER, borderRadius: 6, padding: "3px 10px",
                           fontSize: 12, fontWeight: 600, color: "#374151" }}>
              Inactive: {inactiveCount}
            </span>
          </div>

          {/* Search + Show inactive toggle */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <button
              style={{ ...S.btnGhost,
                       background: showInactive ? AF_AMBER : "#f3f4f6" }}
              onClick={() => setShowInactive(v => !v)}
              title="Include branches with no active areas (shown amber)"
            >
              {showInactive ? "Hide inactive" : "Show inactive"}
              {inactiveCount > 0 && ` (${inactiveCount})`}
            </button>
            <input
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search branch name…"
              style={{ padding: "6px 10px", fontSize: 13, border: "1px solid #d1d5db",
                       borderRadius: 6, width: 260, outline: "none" }}
            />
            {query && (
              <button style={S.btnGhost} onClick={() => setQuery("")}>Clear</button>
            )}
            <span style={{ fontSize: 12, color: "#6b7280" }}>
              {query ? `${sorted.length} match${sorted.length === 1 ? "" : "es"}`
                     : `${sorted.length} branch${sorted.length === 1 ? "" : "es"}`}
            </span>
          </div>

          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>
            One row per branch. "Up to date" = active areas {noun} and not
            overdue. Click a branch to see its areas and each area's log history.
          </div>

          {sorted.length === 0 ? (
            <div style={{ fontSize: 13, color: "#6b7280" }}>
              {query ? `No branches match “${query}”.` : "No branches to show."}
            </div>
          ) : (
            <table style={S.table}>
              <thead>
                <tr>
                  {cols.map(c => (
                    <th key={c.key}
                        style={{ ...S.th, cursor: "pointer", userSelect: "none" }}
                        onClick={() => toggleSort(c.key)}
                        title={`Sort by ${c.label.toLowerCase()}`}>
                      {c.label}{arrow(c.key)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map(r => (
                  <tr key={String(r.branchId)}
                      onClick={() => setOpenBranch(r)}
                      style={{ cursor: "pointer", background: rowBg(r) }}
                      title="Click for this branch's areas and log history">
                    <td style={{ ...S.td, fontWeight: 600 }}>
                      {displayName(r.branchName, masked)}
                      {!r.inUse && (
                        <span style={{ fontWeight: 400, fontSize: 11,
                                       color: "#92400e", marginLeft: 6 }}>
                          (not using module)
                        </span>
                      )}
                    </td>
                    <td style={S.td}>{r.activeAreas ?? 0}</td>
                    <td style={S.td}>{r.inactiveAreas ?? 0}</td>
                    <td style={S.td}>{r.neverLogged ?? 0}</td>
                    <td style={S.td}>{r.upToDate ?? 0}</td>
                    <td style={S.td}>{r.overdue ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {openBranch && (
        <AreaHistoryDialog
          mod={mod}
          token={token}
          branch={openBranch}
          noun={noun}
          onClose={() => setOpenBranch(null)}
        />
      )}

      {configOpen && (
        <AreaConfigDialog
          mod={mod}
          token={token}
          noun={noun}
          onClose={() => setConfigOpen(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AreaHistoryDialog — popup for one branch. Left panel lists every area
// (active first; inactive areas amber, overdue / never-logged pale red,
// up-to-date green). Clicking an area loads that area's log history into the
// right panel (date, by, notes), newest first.
// ---------------------------------------------------------------------------
function AreaHistoryDialog({ mod, token, branch, noun, onClose }) {
  const masked = useMaskNames();
  const [areas, setAreas] = useState(null);
  const [loadingAreas, setLoadingAreas] = useState(true);
  const [selected, setSelected] = useState(null);       // area object
  const [logs, setLogs] = useState(null);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [error, setError] = useState(null);
  // Inactive areas are hidden by default; the toggle adds them to the left
  // panel (amber, labelled "Inactive"). They stay clickable — any historic
  // logs for an inactive area still load into the right panel.
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingAreas(true);
      try {
        const data = await apiGet(
          `${mod.path}/areas?branchId=${encodeURIComponent(String(branch.branchId))}`,
          token);
        if (!cancelled) setAreas(Array.isArray(data?.areas) ? data.areas : []);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoadingAreas(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mod, token, branch]);

  const selectArea = useCallback(async (area) => {
    setSelected(area);
    setLogs(null);
    setLoadingLogs(true);
    setError(null);
    try {
      const data = await apiGet(
        `${mod.path}/area-logs?branchId=${encodeURIComponent(String(branch.branchId))}` +
        `&areaId=${encodeURIComponent(area.areaId)}`, token);
      setLogs(Array.isArray(data?.logs) ? data.logs : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingLogs(false);
    }
  }, [mod, token, branch]);

  const areaBg = (a) => !a.active ? AF_AMBER
    : (a.overdue || a.neverLogged) ? AF_RED : AF_GREEN;
  const areaStatusText = (a) => !a.active ? "Inactive"
    : a.overdue ? "Overdue"
    : a.neverLogged ? `Never ${noun}`
    : "Up to date";

  const verbTitle = noun.charAt(0).toUpperCase() + noun.slice(1);

  const allAreas = areas || [];
  const inactiveAreaCount = allAreas.filter(a => !a.active).length;
  const visibleAreas = allAreas.filter(a => showInactive || a.active);

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="popup-dialog" style={{ maxWidth: 1050 }}
           onClick={e => e.stopPropagation()}>
        <div className="popup-header">
          <h3>
            {mod.title} areas — {displayName(branch.branchName, masked)}
          </h3>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              style={{ ...S.btnGhost,
                       background: showInactive ? AF_AMBER : "#f3f4f6" }}
              onClick={() => {
                setShowInactive(v => {
                  // Deselect a hidden area when toggling inactive areas off.
                  if (v && selected && !selected.active) {
                    setSelected(null); setLogs(null);
                  }
                  return !v;
                });
              }}
              title="Include inactive areas in the list (shown amber)"
            >
              {showInactive ? "Hide inactive" : "Show inactive"}
              {inactiveAreaCount > 0 && ` (${inactiveAreaCount})`}
            </button>
            <button onClick={onClose}>X</button>
          </div>
        </div>

        {error && <div style={S.error}>{error}</div>}

        <div style={{ display: "flex", gap: 14, alignItems: "stretch",
                      minHeight: 320 }}>
          {/* Left: area list */}
          <div style={{ width: 340, flexShrink: 0, overflowY: "auto",
                        maxHeight: "65vh", border: "1px solid #e5e7eb",
                        borderRadius: 6, padding: 8 }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
              Areas {areas && `(${visibleAreas.length}${
                !showInactive && inactiveAreaCount > 0
                  ? ` + ${inactiveAreaCount} inactive` : ""})`}
            </div>
            {loadingAreas && (
              <div style={{ fontSize: 12, color: "#6b7280" }}>Loading areas…</div>
            )}
            {!loadingAreas && areas && visibleAreas.length === 0 && (
              <div style={{ fontSize: 12, color: "#6b7280" }}>
                {allAreas.length === 0
                  ? "No areas configured at this branch."
                  : "No active areas — use Show inactive to see the rest."}
              </div>
            )}
            {!loadingAreas && visibleAreas.map(a => (
              <div key={a.areaId}
                   onClick={() => selectArea(a)}
                   style={{
                     background: areaBg(a), borderRadius: 6, padding: "8px 10px",
                     marginBottom: 6, cursor: "pointer",
                     border: selected?.areaId === a.areaId
                       ? "2px solid #1976d2" : "2px solid transparent"
                   }}
                   title="Click for this area's log history">
                <div style={{ fontWeight: 600, fontSize: 13 }}>{a.areaName}</div>
                <div style={{ fontSize: 11.5, color: "#374151", marginTop: 2 }}>
                  {areaStatusText(a)}
                  {a.frequency != null && ` · every ${a.frequency}d`}
                  {a.nextDue && ` · due ${fmtDate(a.nextDue)}`}
                </div>
                <div style={{ fontSize: 11.5, color: "#374151" }}>
                  Last {noun}: {a.lastLogged ? fmtDateTime(a.lastLogged) : "never"}
                </div>
              </div>
            ))}
          </div>

          {/* Right: log history for the selected area */}
          <div style={{ flex: 1, overflowY: "auto", maxHeight: "65vh",
                        border: "1px solid #e5e7eb", borderRadius: 6,
                        padding: 8 }}>
            {!selected ? (
              <div style={{ fontSize: 13, color: "#6b7280", padding: 8 }}>
                Select an area on the left to see its log history.
              </div>
            ) : (
              <>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
                  {selected.areaName} — log history
                  {logs && ` (${logs.length})`}
                </div>
                {loadingLogs && (
                  <div style={{ fontSize: 12, color: "#6b7280" }}>Loading history…</div>
                )}
                {!loadingLogs && logs && logs.length === 0 && (
                  <div style={{ fontSize: 12, color: "#6b7280" }}>
                    No logs recorded for this area.
                  </div>
                )}
                {!loadingLogs && logs && logs.length > 0 && (
                  <table style={S.table}>
                    <thead>
                      <tr>
                        <th style={S.th}>{verbTitle}</th>
                        <th style={S.th}>By</th>
                        <th style={S.th}>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((l, i) => (
                        <tr key={i}>
                          <td style={S.td}>{fmtDateTime(l.date)}</td>
                          <td style={S.td}>{l.by || "—"}</td>
                          <td style={S.td}>{l.notes || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AreaConfigDialog — "Configuration" view for area-based modules. One
// horizontal panel per branch: branch name on the left, that branch's
// ACTIVE areas on the right (frequency + next due). Each area carries a
// badge like 3/26 = how many branches in scope have an area of exactly the
// same name, plus a Cascade button which — after a confirmation warning —
// inserts-or-updates that area in EVERY branch of the current organisation.
// ---------------------------------------------------------------------------
function AreaConfigDialog({ mod, token, noun, onClose }) {
  const masked = useMaskNames();
  const [branches, setBranches] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Inactive areas are hidden by default; the toggle shows them amber (and
  // they stay editable — including flipping them back to active).
  const [showInactive, setShowInactive] = useState(false);
  // Pending cascade confirmation: { areaName, frequency } | null.
  const [confirmCascade, setConfirmCascade] = useState(null);
  const [cascadeBusy, setCascadeBusy] = useState(false);
  // Last cascade result banner: { areaName, updated, created, total, errors }.
  const [cascadeResult, setCascadeResult] = useState(null);
  // "Add area" form: { branchId, branchName, areaName, frequency, nextDue }
  // | null. nextDue defaults to a fortnight from today; created areas are
  // active by default.
  const [addArea, setAddArea] = useState(null);
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState(null);

  const openAddArea = (b) => {
    setAddError(null);
    setAddArea({
      branchId: String(b.branchId),
      branchName: b.branchName,
      areaName: "",
      frequency: "7",
      nextDue: new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10)
    });
  };

  // Edit form (click an area card): rename, change frequency / next due, or
  // set the status inactive. Inactive areas drop out of this active-areas
  // view but stay visible in the branch popup behind its Show inactive toggle.
  const [editArea, setEditArea] = useState(null);
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState(null);

  const openEditArea = (b, a) => {
    setEditError(null);
    setEditArea({
      branchId: String(b.branchId),
      branchName: b.branchName,
      areaId: a.areaId,
      areaName: a.areaName || "",
      frequency: a.frequency != null ? String(a.frequency) : "",
      nextDue: a.nextDue ? String(a.nextDue).slice(0, 10) : "",
      status: a.active !== false
    });
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiGet(`${mod.path}/area-config`, token);
      setBranches(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [mod, token]);

  useEffect(() => { load(); }, [load]);

  // Panels listed alphabetically by branch name.
  const all = [...(branches || [])].sort((a, b) =>
    (a.branchName || "").localeCompare(b.branchName || ""));
  const totalBranches = all.length;

  // Badge counts: normalised area name → number of branches with an ACTIVE
  // area of exactly that name (case/whitespace-insensitive).
  const norm = (s) => String(s || "").trim().toLowerCase();
  const nameCounts = new Map();
  for (const b of all) {
    const seen = new Set();
    for (const a of b.areas || []) {
      if (a.active === false) continue;
      const k = norm(a.areaName);
      if (!k || seen.has(k)) continue;
      seen.add(k);
      nameCounts.set(k, (nameCounts.get(k) || 0) + 1);
    }
  }

  const runCascade = useCallback(async () => {
    if (!confirmCascade) return;
    setCascadeBusy(true);
    setError(null);
    try {
      const res = await apiSend(`${mod.path}/cascade-area`, token, "POST", {
        areaName: confirmCascade.areaName,
        frequency: confirmCascade.frequency
      });
      setCascadeResult({ areaName: confirmCascade.areaName, ...res });
      setConfirmCascade(null);
      await load();                      // refresh badges + panels
    } catch (e) {
      setError(e.message);
      setConfirmCascade(null);
    } finally {
      setCascadeBusy(false);
    }
  }, [confirmCascade, mod, token, load]);

  const saveNewArea = useCallback(async () => {
    if (!addArea) return;
    const name = (addArea.areaName || "").trim();
    const freq = Number(addArea.frequency);
    if (!name) { setAddError("Area name is required."); return; }
    if (!Number.isFinite(freq) || freq <= 0) {
      setAddError("Frequency must be a number of days greater than 0.");
      return;
    }
    setAddBusy(true);
    setAddError(null);
    try {
      await apiSend(`${mod.path}/create-area`, token, "POST", {
        branchId: addArea.branchId,
        areaName: name,
        frequency: freq,
        nextDue: addArea.nextDue || null
      });
      setAddArea(null);
      await load();                      // refresh panels + badges
    } catch (e) {
      setAddError(e.message);
    } finally {
      setAddBusy(false);
    }
  }, [addArea, mod, token, load]);

  const saveEditArea = useCallback(async () => {
    if (!editArea) return;
    const name = (editArea.areaName || "").trim();
    const freq = Number(editArea.frequency);
    if (!name) { setEditError("Area name is required."); return; }
    if (!Number.isFinite(freq) || freq <= 0) {
      setEditError("Frequency must be a number of days greater than 0.");
      return;
    }
    setEditBusy(true);
    setEditError(null);
    try {
      await apiSend(`${mod.path}/update-area`, token, "POST", {
        branchId: editArea.branchId,
        areaId: editArea.areaId,
        areaName: name,
        frequency: freq,
        nextDue: editArea.nextDue || null,
        status: editArea.status
      });
      setEditArea(null);
      await load();                      // refresh panels + badges
    } catch (e) {
      setEditError(e.message);
    } finally {
      setEditBusy(false);
    }
  }, [editArea, mod, token, load]);

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="popup-dialog" style={{ maxWidth: 1200 }}
           onClick={e => e.stopPropagation()}>
        <div className="popup-header">
          <h3>{mod.title} — configuration</h3>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              style={{ ...S.btnGhost,
                       background: showInactive ? AF_AMBER : "#f3f4f6" }}
              onClick={() => setShowInactive(v => !v)}
              title="Include inactive areas in every branch's panel (shown amber)"
            >
              {showInactive ? "Hide inactive" : "Show inactive"}
            </button>
            <button onClick={onClose}>X</button>
          </div>
        </div>

        <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10 }}>
          Areas per branch{showInactive
            ? " (inactive areas shown amber)"
            : " (active only — use Show inactive to see the rest)"}.
          The badge shows how many of the{" "}
          {totalBranches} branch{totalBranches === 1 ? "" : "es"} have an
          active area with exactly the same name. Cascade copies an area to
          every branch in your organisation (existing areas of the same name
          are updated, missing ones are created). Click an area to edit it.
        </div>

        {error && <div style={S.error}>{error}</div>}

        {cascadeResult && (
          <div style={{ background: AF_GREEN, borderRadius: 6, padding: "8px 12px",
                        fontSize: 13, marginBottom: 10 }}>
            Cascaded “{cascadeResult.areaName}” — {cascadeResult.updated} branch
            {cascadeResult.updated === 1 ? "" : "es"} updated,{" "}
            {cascadeResult.created} created
            (of {cascadeResult.total}).
            {Array.isArray(cascadeResult.errors) && cascadeResult.errors.length > 0 && (
              <span style={{ color: "#b91c1c" }}>
                {" "}{cascadeResult.errors.length} failed:{" "}
                {cascadeResult.errors.map(e =>
                  displayName(e.branchName, masked)).join(", ")}.
              </span>
            )}
            <button style={{ ...S.btnGhost, marginLeft: 10, padding: "2px 8px" }}
                    onClick={() => setCascadeResult(null)}>Dismiss</button>
          </div>
        )}

        {loading && (
          <div style={{ fontSize: 13, color: "#6b7280" }}>Loading configuration…</div>
        )}

        {!loading && all.length === 0 && !error && (
          <div style={{ fontSize: 13, color: "#6b7280" }}>No branches in scope.</div>
        )}

        {!loading && all.map(b => {
          const branchAreas = b.areas || [];
          const activeCount = branchAreas.filter(a => a.active !== false).length;
          const inactiveCount = branchAreas.length - activeCount;
          const visibleAreas = branchAreas.filter(
            a => showInactive || a.active !== false);
          return (
          <div key={String(b.branchId)}
               style={{ display: "flex", gap: 14, alignItems: "flex-start",
                        border: "1px solid #e5e7eb", borderRadius: 6,
                        padding: 10, marginBottom: 8 }}>
            {/* Left: branch name */}
            <div style={{ width: 220, flexShrink: 0, fontWeight: 600,
                          fontSize: 13, paddingTop: 4 }}>
              {displayName(b.branchName, masked)}
              <div style={{ fontWeight: 400, fontSize: 11.5, color: "#6b7280" }}>
                {activeCount} active area{activeCount === 1 ? "" : "s"}
              </div>
              <div style={{ fontWeight: 400, fontSize: 11.5,
                            color: inactiveCount > 0 ? "#92400e" : "#6b7280" }}>
                {inactiveCount} inactive
              </div>
              <button
                style={{ ...S.btnGhost, padding: "3px 10px", fontSize: 11,
                         marginTop: 6 }}
                onClick={() => openAddArea(b)}
                title="Create a new area at this branch"
              >
                + Add area
              </button>
            </div>

            {/* Right: grid of areas */}
            <div style={{ flex: 1, display: "flex", flexWrap: "wrap", gap: 8 }}>
              {visibleAreas.length === 0 && (
                <div style={{ fontSize: 12, color: "#6b7280", paddingTop: 6 }}>
                  {branchAreas.length === 0
                    ? "No areas."
                    : "No active areas — use Show inactive to see the rest."}
                </div>
              )}
              {visibleAreas.map(a => {
                const isInactive = a.active === false;
                const count = nameCounts.get(norm(a.areaName)) || 0;
                const everywhere = count >= totalBranches && totalBranches > 0;
                return (
                  <div key={a.areaId}
                       onClick={() => openEditArea(b, a)}
                       style={{ border: "1px solid #d1d5db", borderRadius: 6,
                                padding: "6px 10px", fontSize: 12.5,
                                background: isInactive ? AF_AMBER : "#f9fafb",
                                minWidth: 200, cursor: "pointer" }}
                       title="Click to edit this area (name, frequency, next due, status)">
                    <div style={{ fontWeight: 600 }}>
                      {a.areaName}
                      {isInactive && (
                        <span style={{ fontWeight: 400, fontSize: 10.5,
                                       color: "#92400e", marginLeft: 6 }}>
                          Inactive
                        </span>
                      )}
                    </div>
                    <div style={{ color: "#374151", marginTop: 2 }}>
                      {a.frequency != null ? `Every ${a.frequency}d` : "No frequency"}
                      {" · "}
                      {a.nextDue ? `due ${fmtDate(a.nextDue)}` : "no due date"}
                    </div>
                    <div style={{ display: "flex", alignItems: "center",
                                  gap: 6, marginTop: 5 }}>
                      <span
                        title={`${count} of ${totalBranches} branches have an active area named “${a.areaName}”`}
                        style={{ background: everywhere ? AF_GREEN : "#e5e7eb",
                                 borderRadius: 10, padding: "1px 8px",
                                 fontSize: 11, fontWeight: 600 }}>
                        {count}/{totalBranches}
                      </span>
                      <button
                        style={{ ...S.btnGhost, padding: "2px 8px", fontSize: 11 }}
                        onClick={(e) => { e.stopPropagation();
                          setConfirmCascade({
                            areaName: a.areaName, frequency: a.frequency
                          }); }}
                        title={everywhere
                          ? "Update this area in every branch (syncs frequency and reactivates it)"
                          : "Create or update this area in every branch (reactivates inactive ones)"}
                      >
                        Cascade
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          );
        })}

        {/* "Add area" form — creates one area at one branch. Active by
            default; next due defaults to a fortnight from today. */}
        {addArea && (
          <div style={{ position: "fixed", inset: 0, zIndex: 1100,
                        background: "rgba(0,0,0,0.45)", display: "flex",
                        alignItems: "center", justifyContent: "center" }}
               onClick={() => !addBusy && setAddArea(null)}>
            <div style={{ background: "#fff", borderRadius: 8, padding: 20,
                          maxWidth: 440, width: "92%",
                          boxShadow: "0 0 15px rgba(0,0,0,0.3)" }}
                 onClick={e => e.stopPropagation()}>
              <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>
                Add {mod.title.toLowerCase()} area
              </div>
              <div style={{ fontSize: 12.5, color: "#6b7280", marginBottom: 14 }}>
                {displayName(addArea.branchName, masked)} — the new area is
                created active.
              </div>

              {addError && <div style={{ ...S.error, marginBottom: 8 }}>{addError}</div>}

              <label style={{ display: "block", fontSize: 12.5, fontWeight: 600,
                              marginBottom: 10 }}>
                Area name
                <input
                  autoFocus
                  type="text"
                  value={addArea.areaName}
                  onChange={e => setAddArea(prev => prev
                    ? { ...prev, areaName: e.target.value } : prev)}
                  placeholder="e.g. Dispensary shelf A"
                  style={{ display: "block", width: "100%", boxSizing: "border-box",
                           marginTop: 4, padding: "6px 10px", fontSize: 13,
                           fontWeight: 400, border: "1px solid #d1d5db",
                           borderRadius: 6, outline: "none" }}
                />
              </label>

              <label style={{ display: "block", fontSize: 12.5, fontWeight: 600,
                              marginBottom: 10 }}>
                Frequency (days)
                <input
                  type="number"
                  min="1"
                  value={addArea.frequency}
                  onChange={e => setAddArea(prev => prev
                    ? { ...prev, frequency: e.target.value } : prev)}
                  style={{ display: "block", width: 120, marginTop: 4,
                           padding: "6px 10px", fontSize: 13, fontWeight: 400,
                           border: "1px solid #d1d5db", borderRadius: 6,
                           outline: "none" }}
                />
              </label>

              <label style={{ display: "block", fontSize: 12.5, fontWeight: 600,
                              marginBottom: 16 }}>
                Next due
                <input
                  type="date"
                  value={addArea.nextDue}
                  onChange={e => setAddArea(prev => prev
                    ? { ...prev, nextDue: e.target.value } : prev)}
                  style={{ display: "block", width: 170, marginTop: 4,
                           padding: "6px 10px", fontSize: 13, fontWeight: 400,
                           border: "1px solid #d1d5db", borderRadius: 6,
                           outline: "none" }}
                />
                <span style={{ fontWeight: 400, fontSize: 11.5, color: "#6b7280" }}>
                  Defaults to a fortnight from today.
                </span>
              </label>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <button style={S.btnGhost} disabled={addBusy}
                        onClick={() => setAddArea(null)}>
                  Cancel
                </button>
                <button style={{ ...S.btn,
                                 opacity: addBusy ? 0.6 : 1,
                                 cursor: addBusy ? "default" : "pointer" }}
                        disabled={addBusy}
                        onClick={saveNewArea}>
                  {addBusy ? "Creating…" : "Create area"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* "Edit area" form — rename, change frequency / next due, or set
            the status inactive. */}
        {editArea && (
          <div style={{ position: "fixed", inset: 0, zIndex: 1100,
                        background: "rgba(0,0,0,0.45)", display: "flex",
                        alignItems: "center", justifyContent: "center" }}
               onClick={() => !editBusy && setEditArea(null)}>
            <div style={{ background: "#fff", borderRadius: 8, padding: 20,
                          maxWidth: 440, width: "92%",
                          boxShadow: "0 0 15px rgba(0,0,0,0.3)" }}
                 onClick={e => e.stopPropagation()}>
              <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>
                Edit {mod.title.toLowerCase()} area
              </div>
              <div style={{ fontSize: 12.5, color: "#6b7280", marginBottom: 14 }}>
                {displayName(editArea.branchName, masked)}
              </div>

              {editError && <div style={{ ...S.error, marginBottom: 8 }}>{editError}</div>}

              <label style={{ display: "block", fontSize: 12.5, fontWeight: 600,
                              marginBottom: 10 }}>
                Area name
                <input
                  autoFocus
                  type="text"
                  value={editArea.areaName}
                  onChange={e => setEditArea(prev => prev
                    ? { ...prev, areaName: e.target.value } : prev)}
                  style={{ display: "block", width: "100%", boxSizing: "border-box",
                           marginTop: 4, padding: "6px 10px", fontSize: 13,
                           fontWeight: 400, border: "1px solid #d1d5db",
                           borderRadius: 6, outline: "none" }}
                />
              </label>

              <label style={{ display: "block", fontSize: 12.5, fontWeight: 600,
                              marginBottom: 10 }}>
                Frequency (days)
                <input
                  type="number"
                  min="1"
                  value={editArea.frequency}
                  onChange={e => setEditArea(prev => prev
                    ? { ...prev, frequency: e.target.value } : prev)}
                  style={{ display: "block", width: 120, marginTop: 4,
                           padding: "6px 10px", fontSize: 13, fontWeight: 400,
                           border: "1px solid #d1d5db", borderRadius: 6,
                           outline: "none" }}
                />
              </label>

              <label style={{ display: "block", fontSize: 12.5, fontWeight: 600,
                              marginBottom: 10 }}>
                Next due
                <input
                  type="date"
                  value={editArea.nextDue}
                  onChange={e => setEditArea(prev => prev
                    ? { ...prev, nextDue: e.target.value } : prev)}
                  style={{ display: "block", width: 170, marginTop: 4,
                           padding: "6px 10px", fontSize: 13, fontWeight: 400,
                           border: "1px solid #d1d5db", borderRadius: 6,
                           outline: "none" }}
                />
              </label>

              <label style={{ display: "block", fontSize: 12.5, fontWeight: 600,
                              marginBottom: 16 }}>
                Status
                <select
                  value={editArea.status ? "active" : "inactive"}
                  onChange={e => setEditArea(prev => prev
                    ? { ...prev, status: e.target.value === "active" } : prev)}
                  style={{ display: "block", width: 170, marginTop: 4,
                           padding: "6px 10px", fontSize: 13, fontWeight: 400,
                           border: "1px solid #d1d5db", borderRadius: 6,
                           outline: "none", background: "#fff" }}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                {!editArea.status && (
                  <span style={{ fontWeight: 400, fontSize: 11.5, color: "#92400e" }}>
                    Inactive areas leave this view; find them in the branch's
                    areas popup under Show inactive.
                  </span>
                )}
              </label>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <button style={S.btnGhost} disabled={editBusy}
                        onClick={() => setEditArea(null)}>
                  Cancel
                </button>
                <button style={{ ...S.btn,
                                 opacity: editBusy ? 0.6 : 1,
                                 cursor: editBusy ? "default" : "pointer" }}
                        disabled={editBusy}
                        onClick={saveEditArea}>
                  {editBusy ? "Saving…" : "Save changes"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Cascade confirmation warning */}
        {confirmCascade && (
          <div style={{ position: "fixed", inset: 0, zIndex: 1100,
                        background: "rgba(0,0,0,0.45)", display: "flex",
                        alignItems: "center", justifyContent: "center" }}
               onClick={() => !cascadeBusy && setConfirmCascade(null)}>
            <div style={{ background: "#fff", borderRadius: 8, padding: 20,
                          maxWidth: 440, width: "92%",
                          boxShadow: "0 0 15px rgba(0,0,0,0.3)" }}
                 onClick={e => e.stopPropagation()}>
              <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 8 }}>
                Cascade “{confirmCascade.areaName}”
              </div>
              <div style={{ fontSize: 13, color: "#374151", marginBottom: 16 }}>
                This will create the same entry in all branches. Continue?
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <button style={S.btnGhost} disabled={cascadeBusy}
                        onClick={() => setConfirmCascade(null)}>
                  Cancel
                </button>
                <button style={{ ...S.btn,
                                 opacity: cascadeBusy ? 0.6 : 1,
                                 cursor: cascadeBusy ? "default" : "pointer" }}
                        disabled={cascadeBusy}
                        onClick={runCascade}>
                  {cascadeBusy ? "Cascading…" : "Continue"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ModuleDetail({ mod, token, onClose }) {
  const masked = useMaskNames();
  const [urgent, setUrgent] = useState({});
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fridge-only: per-branch rolled-up status for the colour-coded grid.
  const [fleetStatus, setFleetStatus] = useState(null);
  const [statusQuery, setStatusQuery] = useState("");
  // Messaging: toggle to show only non-engaged sites (SMS+EMAIL < 50).
  const [nonEngagedOnly, setNonEngagedOnly] = useState(false);
  // Status grid column sort. key: "branch" | "status" | "count". Default
  // sorts by worst status first (handled specially when key === "status").
  const [statusSort, setStatusSort] = useState({ key: "status", dir: "asc" });
  // Fridge-only: history popup payload (mirrors Mockupp.js fridge popup).
  const [popupData, setPopupData] = useState(null);
  const [popupLoading, setPopupLoading] = useState(false);
  // Removed-drugs year export (date-checking view).
  const [removedExportYear, setRemovedExportYear] = useState(
    () => new Date().getFullYear());
  const [removedExportBusy, setRemovedExportBusy] = useState(false);
  // Date-checking detail tabs: "drugs" (existing expiry grid) | "areas"
  // (all-branches date-checking areas grid, same style as cleaning).
  const [dcTab, setDcTab] = useState("drugs");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const next = {};
        if (!mod.urgentOnCardOnly) {
          for (const r of mod.urgentRules) {
            next[r.rule] = await apiGet(`${mod.path}/${r.rule}`, token);
          }
        }
        if (!cancelled) setUrgent(next);

        if (mod.statusDetail) {
          const fs = await apiGet(`${mod.path}/${mod.statusDetail.endpoint}`, token);
          if (!cancelled) setFleetStatus(Array.isArray(fs) ? fs : []);
        }

        if (mod.snapshot) {
          const snap = await apiGet(`${mod.path}/${mod.snapshot}`, token);
          if (!cancelled) setSnapshot(snap);
        } else if (mod.snapshots) {
          const multi = {};
          for (const s of mod.snapshots) {
            multi[s] = await apiGet(`${mod.path}/${s}`, token);
          }
          if (!cancelled) setSnapshot(multi);
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mod, token]);

  // Open the history popup for a site. Mirrors the Mockupp.js wiring exactly,
  // parameterised by the module's statusDetail config so fridge and cleaning
  // (and any future module) share one implementation.
  const openStatusPopup = useCallback(async (branchName) => {
    const sd = mod.statusDetail;
    if (!sd) return;
    setPopupLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/${sd.fetchPath}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ branchName })
      });
      if (!res.ok) {
        // Surface the server message (e.g. name lookup 404) instead of failing silently.
        let msg = `Could not load details for ${branchName} (HTTP ${res.status}).`;
        try { const j = await res.json(); if (j?.message) msg += ` ${j.message}`; } catch (_) {}
        throw new Error(msg);
      }
      const result = await res.json();
      // PopupContent's cleaning branch maps the array unconditionally, so make
      // sure we always hand it an array even if the site has no items.
      const payload = Array.isArray(result?.[sd.logsKey]) ? result[sd.logsKey] : [];
      setPopupData({
        column: sd.popupColumn,
        branchName,
        [sd.popupDataKey]: payload,
        [sd.popupFlag]: true
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setPopupLoading(false);
    }
  }, [token, mod]);

  // Handover-specific grouped popup (overdue → pending → completed today).
  // Fetched by branchId from the head-office items endpoint.
  const [groupedPopup, setGroupedPopup] = useState(null);
  const openHandoverPopup = useCallback(async (row) => {
    setPopupLoading(true);
    setError(null);
    try {
      const data = await apiGet(
        `handover/items?branchId=${encodeURIComponent(String(row.branchId))}`,
        token);
      setGroupedPopup({
        branchName: row.branchName,
        overdue: data?.overdue || [],
        pending: data?.pending || [],
        completedToday: data?.completedToday || []
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setPopupLoading(false);
    }
  }, [token]);

  // Date-checking: drugs behind one clicked cell (name/location/expiry).
  const [drugPopup, setDrugPopup] = useState(null);
  // Removed-drugs paged history popup: ALL removed drugs, fetched two months
  // at a time; scrolling near the bottom pulls the next two months.
  const [removedPopup, setRemovedPopup] = useState(null);
  const openDrugCell = useCallback(async (row, cell, cellLabel, tone) => {
    setPopupLoading(true);
    setError(null);
    try {
      if (cell === "removed") {
        const data = await apiGet(
          `date-checking/removed-paged?branchId=${encodeURIComponent(String(row.branchId))}`,
          token);
        setRemovedPopup({
          branchId: String(row.branchId),
          branchName: row.branchName,
          tone,
          rows: data?.rows || [],
          nextBefore: data?.windowStart || null,   // cursor for the next (older) window
          hasMore: !!data?.hasMore,
          loadingMore: false,
          sort: { key: "date", dir: -1 },
          editing: null                             // { drugId, text } — unit price edit
        });
        return;
      }
      const data = await apiGet(
        `date-checking/drugs?branchId=${encodeURIComponent(String(row.branchId))}&cell=${cell}`,
        token);
      setDrugPopup({
        branchName: row.branchName,
        cellLabel, tone,
        drugs: Array.isArray(data) ? data : []
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setPopupLoading(false);
    }
  }, [token]);

  // Pull the next two-month window when the list is scrolled near its end.
  const loadMoreRemoved = useCallback(async () => {
    // Read the current popup state without holding it in a ref.
    const cur = await new Promise(res => setRemovedPopup(p => { res(p); return p; }));
    if (!cur || !cur.hasMore || cur.loadingMore || !cur.nextBefore) return;
    setRemovedPopup(prev => prev ? { ...prev, loadingMore: true } : prev);
    try {
      const data = await apiGet(
        `date-checking/removed-paged?branchId=${encodeURIComponent(cur.branchId)}` +
        `&before=${encodeURIComponent(cur.nextBefore)}`, token);
      setRemovedPopup(prev => prev ? {
        ...prev,
        rows: [...prev.rows, ...(data?.rows || [])],
        nextBefore: data?.windowStart || null,
        hasMore: !!data?.hasMore,
        loadingMore: false
      } : prev);
    } catch (e) {
      setError(e.message);
      setRemovedPopup(prev => prev ? { ...prev, loadingMore: false } : prev);
    }
  }, [token]);

  // Save a manual price for one drug, then refresh every window already
  // loaded (one call spanning [earliest windowStart, now]) so the popup
  // reflects the stored value.
  const saveRemovedPrice = useCallback(async (drugId, pricePence) => {
    const cur = await new Promise(res => setRemovedPopup(p => { res(p); return p; }));
    if (!cur) return;
    try {
      await apiSend("date-checking/drug-price", token, "POST", {
        branchId: cur.branchId, drugId, pricePence
      });
      const from = cur.nextBefore;   // earliest loaded window start (null → default 2 months)
      const data = await apiGet(
        `date-checking/removed-paged?branchId=${encodeURIComponent(cur.branchId)}` +
        (from ? `&from=${encodeURIComponent(from)}` : ""), token);
      setRemovedPopup(prev => prev ? {
        ...prev,
        rows: data?.rows || [],
        nextBefore: data?.windowStart || prev.nextBefore,
        hasMore: !!data?.hasMore,
        editing: null
      } : prev);
    } catch (e) {
      setError(e.message);
      setRemovedPopup(prev => prev ? { ...prev, editing: null } : prev);
    }
  }, [token]);

  // Removed-drugs year export: pull the whole year for every in-scope branch
  // from the backend (price-enriched from dmddata), then build the workbook
  // client-side (summary grid + one tab per branch).

  // caches its per-register + discrepancy detail keyed by branchId.
  const [cdExpanded, setCdExpanded] = useState(null);          // branchId | null
  const [cdDetail, setCdDetail] = useState({});                // { [branchId]: { registers, discrepancies } }
  const [cdLoadingId, setCdLoadingId] = useState(null);        // branchId being fetched
  const toggleCdRow = useCallback(async (row) => {
    const id = String(row.branchId);
    // Collapse if already open.
    if (cdExpanded === id) { setCdExpanded(null); return; }
    setCdExpanded(id);
    if (cdDetail[id]) return;                                   // already cached
    setCdLoadingId(id);
    setError(null);
    try {
      const data = await apiGet(
        `cd-register/registers?branchId=${encodeURIComponent(id)}`, token);
      setCdDetail(prev => ({ ...prev, [id]: {
        registers: Array.isArray(data?.registers) ? data.registers : [],
        discrepancies: Array.isArray(data?.discrepancies) ? data.discrepancies : []
      } }));
    } catch (e) {
      setError(e.message);
      setCdExpanded(null);
    } finally {
      setCdLoadingId(null);
    }
  }, [token, cdExpanded, cdDetail]);

  // Patient safety: inline accordion expand. Tracks the open branch row and
  // caches its incident list (reverse-chronological) keyed by branchId.
  const [psExpanded, setPsExpanded] = useState(null);
  const [psDetail, setPsDetail] = useState({});
  const [psLoadingId, setPsLoadingId] = useState(null);
  const togglePsRow = useCallback(async (row) => {
    const id = String(row.branchId);
    if (psExpanded === id) { setPsExpanded(null); return; }
    setPsExpanded(id);
    if (psDetail[id]) return;
    setPsLoadingId(id);
    setError(null);
    try {
      const data = await apiGet(
        `patient-safety/incidents?branchId=${encodeURIComponent(id)}`, token);
      setPsDetail(prev => ({ ...prev, [id]: Array.isArray(data) ? data : [] }));
    } catch (e) {
      setError(e.message);
      setPsExpanded(null);
    } finally {
      setPsLoadingId(null);
    }
  }, [token, psExpanded, psDetail]);

  // Visitor logs: inline accordion expand. Caches each branch's visitor list
  // (reverse-chronological) keyed by branchId.
  const [vlExpanded, setVlExpanded] = useState(null);
  const [vlDetail, setVlDetail] = useState({});
  const [vlLoadingId, setVlLoadingId] = useState(null);
  const toggleVlRow = useCallback(async (row) => {
    const id = String(row.branchId);
    if (vlExpanded === id) { setVlExpanded(null); return; }
    setVlExpanded(id);
    if (vlDetail[id]) return;
    setVlLoadingId(id);
    setError(null);
    try {
      const data = await apiGet(
        `visitor-logs/visitors?branchId=${encodeURIComponent(id)}`, token);
      setVlDetail(prev => ({ ...prev, [id]: Array.isArray(data) ? data : [] }));
    } catch (e) {
      setError(e.message);
      setVlExpanded(null);
    } finally {
      setVlLoadingId(null);
    }
  }, [token, vlExpanded, vlDetail]);

  return (
    <div style={S.drill}>
      <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "center", marginBottom: 12 }}>
        <div style={S.drillTitle}>{mod.title}</div>
        <button style={S.btnGhost} onClick={onClose}>← Back</button>
      </div>

      {loading && <LoadingOverlay label={`Loading ${mod.title.toLowerCase()}…`} />}
      {error && <div style={S.error}>{error}</div>}

      {/* Cleaning: all-branches area grid (replaces the shared status grid). */}
      {mod.key === "cleaning" && mod.areaFleet && (
        <AreaFleetGrid mod={mod} token={token} noun={mod.areaFleet.noun} />
      )}

      {!mod.statusDetail && !mod.areaFleet && !mod.urgentOnCardOnly && mod.urgentRules.map(r => {
        const results = urgent[r.rule] || [];
        const rows = flattenOffenders(results);
        const formatter = URGENT_FORMATTERS[`${mod.path}/${r.rule}`];
        return (
          <div key={r.rule} style={{ marginBottom: 18 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>
              {r.label}{" "}
              <span style={rows.length > 0 ? S.urgent : S.ok}>
                ({results.length} branch{results.length === 1 ? "" : "ies"}, {rows.length} item{rows.length === 1 ? "" : "s"})
              </span>
            </div>
            {formatter ? (
              <GenericTable rows={rows} columns={formatter} />
            ) : (
              rows.length > 0 && (
                <pre style={{ background: "#f9fafb", padding: 8, fontSize: 11,
                              borderRadius: 4 }}>
                  {JSON.stringify(rows, null, 2)}
                </pre>
              )
            )}
          </div>
        );
      })}

      {/* Colour-coded per-branch status grid (worst-state wins).
          Green = checked & OK, amber = not yet checked, pink = needs attention.
          Click a row for that site's history popup. Shared by fridge + cleaning
          (and any module declaring statusDetail). */}
      {mod.statusDetail && mod.key !== "handover" && mod.key !== "dateChecking" && mod.key !== "cdRegister" && (() => {
        const sd = mod.statusDetail;
        const STATUS = {
          ok:        { bg: "#dcfce7", label: "Checked & OK" },      // light green
          unchecked: { bg: "#fef3c7", label: "Not yet checked" },  // amber
          fail:      { bg: "#fde2e1", label: "Needs attention" }    // light pink/ochre
        };
        const all = (fleetStatus || []);
        const q = statusQuery.trim().toLowerCase();
        const rows = q
          ? all.filter(s => (s.branchName || "").toLowerCase().includes(q))
          : all;

        // Count column: handover shows outstanding counts; others show a
        // "checked / total" ratio. countValue is the numeric key for sorting.
        const isHandover = sd.countMode === "handover";
        const countLabel = isHandover ? "Outstanding" : (sd.checkedLabel || "Checked");
        const countDisplay = (s) => isHandover
          ? `${s.overdueCount ?? 0} overdue, ${s.pendingCount ?? 0} pending`
          : `${s[sd.checkedField] ?? 0} / ${s[sd.totalField] ?? 0}`;
        const countValue = (s) => isHandover
          ? ((s.overdueCount ?? 0) * 1000 + (s.pendingCount ?? 0))  // overdue dominates
          : (s[sd.checkedField] ?? 0);

        // Sort. Default "status" key uses worst-state-first ranking; explicit
        // column sorts use the chosen field + direction.
        const rank = { fail: 0, unchecked: 1, ok: 2 };
        const dir = statusSort.dir === "desc" ? -1 : 1;
        const sorted = [...rows].sort((a, b) => {
          let cmp = 0;
          if (statusSort.key === "branch") {
            cmp = (a.branchName || "").localeCompare(b.branchName || "");
          } else if (statusSort.key === "count") {
            cmp = countValue(a) - countValue(b);
          } else { // "status"
            cmp = (rank[a.status] ?? 3) - (rank[b.status] ?? 3);
          }
          // Stable tiebreak on branch name.
          if (cmp === 0) cmp = (a.branchName || "").localeCompare(b.branchName || "");
          return cmp * dir;
        });

        const toggleSort = (key) => setStatusSort(prev =>
          prev.key === key
            ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
            : { key, dir: "asc" });
        const arrow = (key) =>
          statusSort.key === key ? (statusSort.dir === "asc" ? " ▲" : " ▼") : "";

        const count = (st) => all.filter(s => s.status === st).length;

        return (
        <div style={{ marginTop: 8, marginBottom: 18 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            Site status{" "}
            <span style={{ fontWeight: 400, color: "#6b7280", fontSize: 13 }}>
              ({all.length} active site{all.length === 1 ? "" : "s"})
            </span>
          </div>

          {/* Summary chips */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
            {["fail", "unchecked", "ok"].map(st => (
              <span key={st} style={{
                background: STATUS[st].bg, borderRadius: 6, padding: "3px 10px",
                fontSize: 12, fontWeight: 600, color: "#374151"
              }}>
                {STATUS[st].label}: {count(st)}
              </span>
            ))}
          </div>

          {/* Search */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <input
              type="text"
              value={statusQuery}
              onChange={e => setStatusQuery(e.target.value)}
              placeholder="Search branch name…"
              style={{
                padding: "6px 10px", fontSize: 13, border: "1px solid #d1d5db",
                borderRadius: 6, width: 260, outline: "none"
              }}
            />
            {statusQuery && (
              <button style={S.btnGhost} onClick={() => setStatusQuery("")}>Clear</button>
            )}
            <span style={{ fontSize: 12, color: "#6b7280" }}>
              {statusQuery ? `${sorted.length} match${sorted.length === 1 ? "" : "es"}`
                           : `${sorted.length} site${sorted.length === 1 ? "" : "s"}`}
            </span>
          </div>

          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>
            One row per branch. Click a row to see that site's full detail
            history.
          </div>

          {sorted.length === 0 ? (
            <div style={{ fontSize: 13, color: "#6b7280" }}>
              {statusQuery ? `No branches match “${statusQuery}”.` : "No active sites."}
            </div>
          ) : (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={{ ...S.th, cursor: "pointer", userSelect: "none" }}
                      onClick={() => toggleSort("branch")}
                      title="Sort by branch name">
                    Branch{arrow("branch")}
                  </th>
                  <th style={{ ...S.th, cursor: "pointer", userSelect: "none" }}
                      onClick={() => toggleSort("status")}
                      title="Sort by status (worst first)">
                    Status{arrow("status")}
                  </th>
                  <th style={{ ...S.th, cursor: "pointer", userSelect: "none" }}
                      onClick={() => toggleSort("count")}
                      title={`Sort by ${countLabel.toLowerCase()}`}>
                    {countLabel}{arrow("count")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(s => {
                  const sc = STATUS[s.status] || { bg: "#fff", label: s.status };
                  const note = (s.reasons && s.reasons.length)
                    ? s.reasons.join("; ")
                    : "All up to date.";
                  return (
                    <tr
                      key={String(s.branchId)}
                      onClick={() => openStatusPopup(s.branchName)}
                      style={{ cursor: "pointer", background: sc.bg }}
                      title={`${note} — click for history`}
                    >
                      <td style={{ ...S.td, fontWeight: 600 }}>{displayName(s.branchName, masked)}</td>
                      <td style={S.td}>{sc.label}</td>
                      <td style={S.td}>{countDisplay(s)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {popupLoading && (
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>
              Loading history…
            </div>
          )}
        </div>
        );
      })()}

      {/* Handover: dedicated per-branch grid with sortable columns, row
          colouring (overdue→red, pending→amber, else green) and a grouped
          click-through popup. Replaces both the shared status grid and the
          snapshot KV table for this module. */}
      {mod.key === "handover" && (() => {
        const all = (fleetStatus || []);
        const q = statusQuery.trim().toLowerCase();
        const rows = q
          ? all.filter(s => (s.branchName || "").toLowerCase().includes(q))
          : all;

        const STATUS_LABEL = { ok: "Checked & OK", unchecked: "Pending", fail: "Needs attention" };
        // Row background: overdue>0 → red, else pending>0 → amber, else green.
        const rowBg = (s) => (s.overdueCount > 0) ? "#fde2e1"
          : (s.pendingCount > 0) ? "#fef3c7" : "#dcfce7";

        const cols = [
          { key: "branch",  label: "Branch",       get: s => (s.branchName || "").toLowerCase(), align: "left" },
          { key: "status",    label: "Status",         get: s => ({ fail: 0, unchecked: 1, ok: 2 }[s.status] ?? 3) },
          { key: "overdue",   label: "Overdue",        get: s => s.overdueCount ?? 0 },
          { key: "pending",   label: "Pending",        get: s => s.pendingCount ?? 0 },
          { key: "completed", label: "Completed (30d)", get: s => s.completedLast30d ?? 0 },
          { key: "ontime",    label: "On-time (%)",    get: s => s.onTimeLast30d ?? 0 }
        ];
        const dir = statusSort.dir === "desc" ? -1 : 1;
        const activeCol = cols.find(c => c.key === statusSort.key) || cols[1];
        const sorted = [...rows].sort((a, b) => {
          const av = activeCol.get(a), bv = activeCol.get(b);
          let cmp = (typeof av === "string") ? av.localeCompare(bv) : (av - bv);
          if (cmp === 0) cmp = (a.branchName || "").localeCompare(b.branchName || "");
          return cmp * dir;
        });
        const toggleSort = (key) => setStatusSort(prev =>
          prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
                           : { key, dir: "asc" });
        const arrow = (key) =>
          statusSort.key === key ? (statusSort.dir === "asc" ? " ▲" : " ▼") : "";

        const count = (st) => all.filter(s => s.status === st).length;

        return (
        <div style={{ marginTop: 8, marginBottom: 18 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            Site status{" "}
            <span style={{ fontWeight: 400, color: "#6b7280", fontSize: 13 }}>
              ({all.length} active site{all.length === 1 ? "" : "s"})
            </span>
          </div>

          {/* Search */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <input
              type="text"
              value={statusQuery}
              onChange={e => setStatusQuery(e.target.value)}
              placeholder="Search branch name…"
              style={{ padding: "6px 10px", fontSize: 13, border: "1px solid #d1d5db",
                       borderRadius: 6, width: 260, outline: "none" }}
            />
            {statusQuery && (
              <button style={S.btnGhost} onClick={() => setStatusQuery("")}>Clear</button>
            )}
            <span style={{ fontSize: 12, color: "#6b7280" }}>
              {statusQuery ? `${sorted.length} match${sorted.length === 1 ? "" : "es"}`
                           : `${sorted.length} site${sorted.length === 1 ? "" : "s"}`}
            </span>
          </div>

          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>
            One row per branch. Click a branch to see its handover items
            grouped overdue → pending → completed today.
          </div>

          {sorted.length === 0 ? (
            <div style={{ fontSize: 13, color: "#6b7280" }}>
              {statusQuery ? `No branches match “${statusQuery}”.` : "No active sites."}
            </div>
          ) : (
            <table style={S.table}>
              <thead>
                <tr>
                  {cols.map(c => (
                    <th key={c.key}
                        style={{ ...S.th, cursor: "pointer", userSelect: "none" }}
                        onClick={() => toggleSort(c.key)}
                        title={`Sort by ${c.label.toLowerCase()}`}>
                      {c.label}{arrow(c.key)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map(s => (
                  <tr key={String(s.branchId)}
                      onClick={() => openHandoverPopup(s)}
                      style={{ cursor: "pointer", background: rowBg(s) }}
                      title="Click for this site's handover items">
                    <td style={{ ...S.td, fontWeight: 600 }}>{displayName(s.branchName, masked)}</td>
                    <td style={S.td}>{STATUS_LABEL[s.status] || s.status}</td>
                    <td style={S.td}>{s.overdueCount ?? 0}</td>
                    <td style={S.td}>{s.pendingCount ?? 0}</td>
                    <td style={S.td}>{s.completedLast30d ?? 0}</td>
                    <td style={S.td}>
                      {(s.onTimeLast30d ?? 0)}
                      {s.onTimePctLast30d != null && ` (${s.onTimePctLast30d}%)`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {popupLoading && (
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>
              Loading handover items…
            </div>
          )}
        </div>
        );
      })()}

      {/* Date checking: two tabs — the existing drug expiry grid and the
          all-branches date-checking AREAS grid (same style as cleaning). */}
      {mod.key === "dateChecking" && (
        <div style={{ display: "flex", gap: 6, marginBottom: 12,
                      borderBottom: "1px solid #e5e7eb" }}>
          {[{ key: "drugs", label: "Drugs" },
            { key: "areas", label: "Date checking areas" }].map(t => (
            <button key={t.key}
              onClick={() => setDcTab(t.key)}
              style={{
                background: "none", border: "none", cursor: "pointer",
                fontSize: 14, padding: "8px 14px",
                fontWeight: dcTab === t.key ? 600 : 400,
                color: dcTab === t.key ? "#1976d2" : "#374151",
                borderBottom: dcTab === t.key
                  ? "2px solid #1976d2" : "2px solid transparent",
                marginBottom: -1
              }}>
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* Tab 2: all-branches date-checking areas grid. */}
      {mod.key === "dateChecking" && dcTab === "areas" && mod.areaFleet && (
        <AreaFleetGrid mod={mod} token={token} noun={mod.areaFleet.noun} />
      )}

      {/* Tab 1 — Date checking drugs: per-branch grid with sortable columns
          + search. Columns: Branch, Status, Expired, Expiring <1m/<2m/<3m.
          Row bg: expired>0 → red, else expiring<1m>0 → amber, else none. */}
      {mod.key === "dateChecking" && dcTab === "drugs" && (() => {
        const all = (fleetStatus || []);
        const q = statusQuery.trim().toLowerCase();
        const rows = q
          ? all.filter(s => (s.branchName || "").toLowerCase().includes(q))
          : all;

        const RED = "#fde2e1", AMBER = "#fef3c7", GREEN = "#dcfce7";
        // Worst severity for the branch-name cell: expired → red,
        // else expiring<1m → amber, else green.
        const nameBg = (s) => (s.expired > 0) ? RED
          : (s.expiring1m > 0) ? AMBER : GREEN;

        // Data cell definitions: field, label, urgency tone, cell key for the
        // drug popup. Expired=red, <1m=amber, <2m/<3m=none, removed/notPresent=none.
        const dataCols = [
          { key: "expired",    field: "expired",          label: "Expired",            tone: RED,   cell: "expired" },
          { key: "exp1m",      field: "expiring1m",       label: "Expiring <1mth",     tone: AMBER, cell: "exp1m" },
          { key: "exp2m",      field: "expiring2m",       label: "Expiring <2mth",     tone: null,  cell: "exp2m" },
          { key: "exp3m",      field: "expiring3m",       label: "Expiring <3mth",     tone: null,  cell: "exp3m" },
          { key: "removed",    field: "removedLast60d",   label: "Removed (60d)",      tone: null,  cell: "removed" },
          { key: "notPresent", field: "notPresentLast60d", label: "Not present (60d)", tone: null,  cell: "notPresent" }
        ];

        // Sort: branch name or any data column.
        const sortGetters = {
          branch: s => (s.branchName || "").toLowerCase(),
          ...Object.fromEntries(dataCols.map(c => [c.key, s => s[c.field] ?? 0]))
        };
        const dir = statusSort.dir === "desc" ? -1 : 1;
        const getter = sortGetters[statusSort.key] || sortGetters.expired;
        const sorted = [...rows].sort((a, b) => {
          const av = getter(a), bv = getter(b);
          let cmp = (typeof av === "string") ? av.localeCompare(bv) : (av - bv);
          if (cmp === 0) cmp = (a.branchName || "").localeCompare(b.branchName || "");
          return cmp * dir;
        });
        const toggleSort = (key) => setStatusSort(prev =>
          prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
                           : { key, dir: "asc" });
        const arrow = (key) =>
          statusSort.key === key ? (statusSort.dir === "asc" ? " ▲" : " ▼") : "";

        return (
        <div style={{ marginTop: 8, marginBottom: 18 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            Site status{" "}
            <span style={{ fontWeight: 400, color: "#6b7280", fontSize: 13 }}>
              ({all.length} active site{all.length === 1 ? "" : "s"})
            </span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <input
              type="text"
              value={statusQuery}
              onChange={e => setStatusQuery(e.target.value)}
              placeholder="Search branch name…"
              style={{ padding: "6px 10px", fontSize: 13, border: "1px solid #d1d5db",
                       borderRadius: 6, width: 260, outline: "none" }}
            />
            {statusQuery && (
              <button style={S.btnGhost} onClick={() => setStatusQuery("")}>Clear</button>
            )}
            <span style={{ fontSize: 12, color: "#6b7280" }}>
              {statusQuery ? `${sorted.length} match${sorted.length === 1 ? "" : "es"}`
                           : `${sorted.length} site${sorted.length === 1 ? "" : "s"}`}
            </span>

            {/* Removed-drugs year export. Pick a year, then export a workbook
                with a summary grid + one tab per branch for the whole year. */}
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
              <select
                value={removedExportYear}
                onChange={e => setRemovedExportYear(Number(e.target.value))}
                disabled={removedExportBusy}
                title="Year to export"
                style={{ padding: "6px 8px", fontSize: 13, border: "1px solid #d1d5db",
                         borderRadius: 6, outline: "none", background: "#fff" }}
              >
                {Array.from(
                  { length: (new Date().getFullYear() - 2021) + 1 },
                  (_, k) => new Date().getFullYear() - k
                ).map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>

          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>
            Expiry bands are cumulative (under 2 months includes under 1 month).
            Click any number to see the drugs behind it.
          </div>

          {sorted.length === 0 ? (
            <div style={{ fontSize: 13, color: "#6b7280" }}>
              {statusQuery ? `No branches match “${statusQuery}”.` : "No active sites."}
            </div>
          ) : (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={{ ...S.th, cursor: "pointer", userSelect: "none" }}
                      onClick={() => toggleSort("branch")}
                      title="Sort by branch name">
                    Branch{arrow("branch")}
                  </th>
                  {dataCols.map(c => (
                    <th key={c.key}
                        style={{ ...S.th, cursor: "pointer", userSelect: "none" }}
                        onClick={() => toggleSort(c.key)}
                        title={`Sort by ${c.label.toLowerCase()}`}>
                      {c.label}{arrow(c.key)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map(s => (
                  <tr key={String(s.branchId)}>
                    <td style={{ ...S.td, fontWeight: 600, background: nameBg(s) }}
                        title={(s.reasons || []).join("; ")}>
                      {displayName(s.branchName, masked)}
                    </td>
                    {dataCols.map(c => {
                      const v = s[c.field] ?? 0;
                      // Tone only tints the cell when it has entries.
                      const bg = (v > 0 && c.tone) ? c.tone : undefined;
                      return (
                        <td key={c.key}
                            onClick={(e) => { e.stopPropagation();
                              if (v > 0) openDrugCell(s, c.cell, c.label, c.tone); }}
                            style={{ ...S.td, background: bg,
                                     cursor: v > 0 ? "pointer" : "default",
                                     textDecoration: v > 0 ? "underline" : "none" }}
                            title={v > 0 ? "Click to see drugs" : ""}>
                          {v}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {popupLoading && (
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>
              Loading drugs…
            </div>
          )}
        </div>
        );
      })()}

      {/* CD register: one row per branch with two parameter groups —
          balance checking (checked / total + overdue) and discrepancies
          (open count). Worst-state row colour. Click a row to expand inline
          and see that site's individual registers and open discrepancies. */}
      {mod.key === "cdRegister" && (() => {
        const all = (fleetStatus || []);
        const q = statusQuery.trim().toLowerCase();
        const rows = q
          ? all.filter(s => (s.branchName || "").toLowerCase().includes(q))
          : all;

        const RED = "#fde2e1", AMBER = "#fef3c7", GREEN = "#dcfce7";
        // Row tone: any overdue or open discrepancy → red; else never-checked
        // → amber; else green.
        const rowBg = (s) => (s.overdueCount > 0 || s.openDiscrepancyCount > 0) ? RED
          : (s.neverCheckedCount > 0) ? AMBER : GREEN;

        const STATUS_LABEL = { ok: "All up to date", unchecked: "Checks pending", fail: "Needs attention" };

        // Sort getters across both parameter groups.
        const sortGetters = {
          branch:   s => (s.branchName || "").toLowerCase(),
          status:     s => ({ fail: 0, unchecked: 1, ok: 2 }[s.status] ?? 3),
          checked:    s => s.checkedCount ?? 0,
          overdue:    s => s.overdueCount ?? 0,
          registers:  s => s.registerCount ?? 0,
          discrep:    s => s.openDiscrepancyCount ?? 0
        };
        const dir = statusSort.dir === "desc" ? -1 : 1;
        const getter = sortGetters[statusSort.key] || sortGetters.status;
        const sorted = [...rows].sort((a, b) => {
          const av = getter(a), bv = getter(b);
          let cmp = (typeof av === "string") ? av.localeCompare(bv) : (av - bv);
          if (cmp === 0) cmp = (a.branchName || "").localeCompare(b.branchName || "");
          return cmp * dir;
        });
        const toggleSort = (key) => setStatusSort(prev =>
          prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
                           : { key, dir: "asc" });
        const arrow = (key) =>
          statusSort.key === key ? (statusSort.dir === "asc" ? " ▲" : " ▼") : "";

        const count = (st) => all.filter(s => s.status === st).length;

        // Sub-header styling for the two grouped parameter bands.
        const groupTh = (bg, color) => ({ ...S.th, background: bg, color,
          textAlign: "center", fontWeight: 700, fontSize: 12 });

        return (
        <div style={{ marginTop: 8, marginBottom: 18 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            Site status{" "}
            <span style={{ fontWeight: 400, color: "#6b7280", fontSize: 13 }}>
              ({all.length} active site{all.length === 1 ? "" : "s"})
            </span>
          </div>

          {/* Summary chips */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
            {["fail", "unchecked", "ok"].map(st => (
              <span key={st} style={{
                background: st === "fail" ? RED : st === "unchecked" ? AMBER : GREEN,
                borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 600,
                color: "#374151"
              }}>
                {STATUS_LABEL[st]}: {count(st)}
              </span>
            ))}
          </div>

          {/* Search */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <input
              type="text"
              value={statusQuery}
              onChange={e => setStatusQuery(e.target.value)}
              placeholder="Search branch name…"
              style={{ padding: "6px 10px", fontSize: 13, border: "1px solid #d1d5db",
                       borderRadius: 6, width: 260, outline: "none" }}
            />
            {statusQuery && (
              <button style={S.btnGhost} onClick={() => setStatusQuery("")}>Clear</button>
            )}
            <span style={{ fontSize: 12, color: "#6b7280" }}>
              {statusQuery ? `${sorted.length} match${sorted.length === 1 ? "" : "es"}`
                           : `${sorted.length} site${sorted.length === 1 ? "" : "s"}`}
            </span>
          </div>

          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>
            One row per branch. Click a row to expand its individual registers
            and any open discrepancies.
          </div>

          {sorted.length === 0 ? (
            <div style={{ fontSize: 13, color: "#6b7280" }}>
              {statusQuery ? `No branches match “${statusQuery}”.` : "No active sites."}
            </div>
          ) : (
            <table style={S.table}>
              <thead>
                {/* Grouped parameter bands */}
                <tr>
                  <th style={{ ...S.th, background: "#f9fafb" }} rowSpan={2}
                      onClick={() => toggleSort("branch")}
                      title="Sort by branch name">
                    <span style={{ cursor: "pointer", userSelect: "none" }}>
                      Branch{arrow("branch")}
                    </span>
                  </th>
                  <th style={{ ...S.th, background: "#f9fafb" }} rowSpan={2}
                      onClick={() => toggleSort("status")}
                      title="Sort by status (worst first)">
                    <span style={{ cursor: "pointer", userSelect: "none" }}>
                      Status{arrow("status")}
                    </span>
                  </th>
                  <th colSpan={3} style={groupTh("#eef5ff", "#1e3a5f")}>
                    Balance checking
                  </th>
                  <th colSpan={1} style={groupTh("#fff4ed", "#9a3412")}>
                    Discrepancies
                  </th>
                </tr>
                <tr>
                  <th style={{ ...S.th, cursor: "pointer", userSelect: "none",
                               background: "#f5f9ff", textAlign: "center" }}
                      onClick={() => toggleSort("checked")}
                      title="Registers checked within the interval, out of total active registers">
                    Checked / total{arrow("checked")}
                  </th>
                  <th style={{ ...S.th, cursor: "pointer", userSelect: "none",
                               background: "#f5f9ff", textAlign: "center" }}
                      onClick={() => toggleSort("overdue")}
                      title="Registers past their balance-check interval">
                    Overdue{arrow("overdue")}
                  </th>
                  <th style={{ ...S.th, background: "#f5f9ff", textAlign: "center" }}
                      title="Registers never balance-checked">
                    Never checked
                  </th>
                  <th style={{ ...S.th, cursor: "pointer", userSelect: "none",
                               background: "#fff8f3", textAlign: "center" }}
                      onClick={() => toggleSort("discrep")}
                      title="Unresolved discrepancies open right now">
                    Open{arrow("discrep")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(s => {
                  const id = String(s.branchId);
                  const isOpen = cdExpanded === id;
                  const detail = cdDetail[id];
                  const checkedRatio = `${s.checkedCount ?? 0} / ${s.registerCount ?? 0}`;
                  return (
                    <React.Fragment key={id}>
                      <tr
                        onClick={() => toggleCdRow(s)}
                        style={{ cursor: "pointer", background: rowBg(s) }}
                        title={(s.reasons || []).join("; ") || "All up to date — click to view registers"}
                      >
                        <td style={{ ...S.td, fontWeight: 600 }}>
                          <span style={{ display: "inline-block", width: 14,
                                         color: "#6b7280" }}>
                            {isOpen ? "▾" : "▸"}
                          </span>
                          {displayName(s.branchName, masked)}
                        </td>
                        <td style={S.td}>{STATUS_LABEL[s.status] || s.status}</td>
                        <td style={{ ...S.td, textAlign: "center" }}>{checkedRatio}</td>
                        <td style={{ ...S.td, textAlign: "center",
                                     color: (s.overdueCount > 0) ? "#b91c1c" : "#374151",
                                     fontWeight: (s.overdueCount > 0) ? 600 : 400 }}>
                          {s.overdueCount ?? 0}
                        </td>
                        <td style={{ ...S.td, textAlign: "center",
                                     color: (s.neverCheckedCount > 0) ? "#b45309" : "#374151" }}>
                          {s.neverCheckedCount ?? 0}
                        </td>
                        <td style={{ ...S.td, textAlign: "center",
                                     color: (s.openDiscrepancyCount > 0) ? "#b91c1c" : "#374151",
                                     fontWeight: (s.openDiscrepancyCount > 0) ? 600 : 400 }}>
                          {s.openDiscrepancyCount ?? 0}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={6} style={{ ...S.td, background: "#fafafa",
                                                   padding: 12 }}>
                            {cdLoadingId === id ? (
                              <div style={{ fontSize: 13, color: "#6b7280" }}>
                                Loading registers…
                              </div>
                            ) : !detail ? (
                              <div style={{ fontSize: 13, color: "#6b7280" }}>
                                No detail available.
                              </div>
                            ) : (
                              <div style={{ fontSize: 13, color: "#6b7280" }}>No expanded view for this module.</div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        );
      })()}

      {/* Visitor logs: per-branch snapshot. Currently on site (amber if >0),
          all-time Total visitors, and Stale (on site but signed in before
          today). Click a row to expand visitors reverse-chronologically:
          stale = pink, current-today = amber. */}
      {mod.key === "visitorLogs" && Array.isArray(snapshot) && (() => {
        const all = snapshot || [];
        const q = statusQuery.trim().toLowerCase();
        const rows = q
          ? all.filter(s => (s.branchName || "").toLowerCase().includes(q))
          : all;

        const AMBER = "#fef3c7", PINK = "#fde2e1";

        const sortGetters = {
          branch: s => (s.branchName || "").toLowerCase(),
          current:  s => s.currentOnSite ?? 0,
          total:    s => s.totalVisitors ?? 0,
          stale:    s => s.staleOnSite ?? 0
        };
        const dir = statusSort.dir === "desc" ? -1 : 1;
        const getter = sortGetters[statusSort.key] || sortGetters.branch;
        const sorted = [...rows].sort((a, b) => {
          const av = getter(a), bv = getter(b);
          let cmp = (typeof av === "string") ? av.localeCompare(bv) : (av - bv);
          if (cmp === 0) cmp = (a.branchName || "").localeCompare(b.branchName || "");
          return cmp * dir;
        });
        const toggleSort = (key) => setStatusSort(prev =>
          prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
                           : { key, dir: "asc" });
        const arrow = (key) =>
          statusSort.key === key ? (statusSort.dir === "asc" ? " ▲" : " ▼") : "";

        return (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Snapshot</div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <input
              type="text"
              value={statusQuery}
              onChange={e => setStatusQuery(e.target.value)}
              placeholder="Search branch name…"
              style={{ padding: "6px 10px", fontSize: 13, border: "1px solid #d1d5db",
                       borderRadius: 6, width: 260, outline: "none" }}
            />
            {statusQuery && (
              <button style={S.btnGhost} onClick={() => setStatusQuery("")}>Clear</button>
            )}
          </div>

          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>
            Click a branch to expand its visitor history. Stale = on site but
            signed in before today (likely not signed out).
          </div>

          <table style={S.table}>
            <thead>
              <tr>
                <th style={{ ...S.th, cursor: "pointer", userSelect: "none" }}
                    onClick={() => toggleSort("branch")}>
                  Branch{arrow("branch")}
                </th>
                <th style={{ ...S.th, cursor: "pointer", userSelect: "none", textAlign: "center" }}
                    onClick={() => toggleSort("current")}
                    title="Visitors signed in today and not yet signed out">
                  Currently on site{arrow("current")}
                </th>
                <th style={{ ...S.th, cursor: "pointer", userSelect: "none", textAlign: "center" }}
                    onClick={() => toggleSort("total")}
                    title="All-time count of visitor entries">
                  Total visitors{arrow("total")}
                </th>
                <th style={{ ...S.th, cursor: "pointer", userSelect: "none", textAlign: "center" }}
                    onClick={() => toggleSort("stale")}
                    title="On site but signed in before today — likely not signed out">
                  Stale{arrow("stale")}
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(s => {
                const id = String(s.branchId);
                const isOpen = vlExpanded === id;
                const visitors = vlDetail[id];
                const cur = s.currentOnSite ?? 0;
                const stale = s.staleOnSite ?? 0;
                return (
                  <React.Fragment key={id}>
                    <tr onClick={() => toggleVlRow(s)}
                        style={{ cursor: "pointer" }}
                        title="Click to view visitor history">
                      <td style={{ ...S.td, fontWeight: 600 }}>
                        <span style={{ display: "inline-block", width: 14,
                                       color: "#6b7280" }}>
                          {isOpen ? "▾" : "▸"}
                        </span>
                        {displayName(s.branchName, masked)}
                      </td>
                      <td style={{ ...S.td, textAlign: "center",
                                   background: cur > 0 ? AMBER : undefined,
                                   fontWeight: cur > 0 ? 600 : 400,
                                   color: cur > 0 ? "#b45309" : "#374151" }}>
                        {cur}
                      </td>
                      <td style={{ ...S.td, textAlign: "center" }}>{s.totalVisitors ?? 0}</td>
                      <td style={{ ...S.td, textAlign: "center",
                                   background: stale > 0 ? PINK : undefined,
                                   fontWeight: stale > 0 ? 600 : 400,
                                   color: stale > 0 ? "#b91c1c" : "#374151" }}>
                        {stale}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={4} style={{ ...S.td, background: "#fafafa",
                                                 padding: 12 }}>
                          {vlLoadingId === id ? (
                            <div style={{ fontSize: 13, color: "#6b7280" }}>
                              Loading visitors…
                            </div>
                          ) : !visitors ? (
                            <div style={{ fontSize: 13, color: "#6b7280" }}>
                              No detail available.
                            </div>
                          ) : (
                            <div style={{ fontSize: 13, color: "#6b7280" }}>No expanded view for this module.</div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        );
      })()}

      {/* Patient safety: per-branch snapshot with an all-time "Total
          submitted" column. Click a row to expand its incidents inline
          (reverse-chronological): date/time, harm, LFPSE, RP reporting. */}
      {mod.key === "patientSafety" && Array.isArray(snapshot) && (() => {
        const all = snapshot || [];
        const q = statusQuery.trim().toLowerCase();
        const rows = q
          ? all.filter(s => (s.branchName || "").toLowerCase().includes(q))
          : all;

        // Sortable columns across the snapshot figures.
        const sortGetters = {
          branch:  s => (s.branchName || "").toLowerCase(),
          total30:   s => s.last30Days?.total ?? 0,
          submitted: s => s.lfpseSubmittedTotal ?? 0,
          lfpse:     s => s.last30Days?.lfpseSubmittedPct ?? -1,
          rp:        s => s.last30Days?.rpNotifiedPct ?? -1
        };
        const dir = statusSort.dir === "desc" ? -1 : 1;
        const getter = sortGetters[statusSort.key] || sortGetters.branch;
        const sorted = [...rows].sort((a, b) => {
          const av = getter(a), bv = getter(b);
          let cmp = (typeof av === "string") ? av.localeCompare(bv) : (av - bv);
          if (cmp === 0) cmp = (a.branchName || "").localeCompare(b.branchName || "");
          return cmp * dir;
        });
        const toggleSort = (key) => setStatusSort(prev =>
          prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
                           : { key, dir: "asc" });
        const arrow = (key) =>
          statusSort.key === key ? (statusSort.dir === "asc" ? " ▲" : " ▼") : "";

        return (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Snapshot</div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <input
              type="text"
              value={statusQuery}
              onChange={e => setStatusQuery(e.target.value)}
              placeholder="Search branch name…"
              style={{ padding: "6px 10px", fontSize: 13, border: "1px solid #d1d5db",
                       borderRadius: 6, width: 260, outline: "none" }}
            />
            {statusQuery && (
              <button style={S.btnGhost} onClick={() => setStatusQuery("")}>Clear</button>
            )}
          </div>

          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>
            Click a branch to expand its incident history.
          </div>

          <table style={S.table}>
            <thead>
              <tr>
                <th style={{ ...S.th, cursor: "pointer", userSelect: "none" }}
                    onClick={() => toggleSort("branch")}>
                  Branch{arrow("branch")}
                </th>
                <th style={{ ...S.th, cursor: "pointer", userSelect: "none" }}
                    onClick={() => toggleSort("total30")}
                    title="Incidents recorded in the last 30 days">
                  Total (30d){arrow("total30")}
                </th>
                <th style={{ ...S.th, cursor: "pointer", userSelect: "none" }}
                    onClick={() => toggleSort("submitted")}
                    title="All-time count of incidents submitted to LFPSE">
                  Total submitted{arrow("submitted")}
                </th>
                <th style={{ ...S.th, cursor: "pointer", userSelect: "none" }}
                    onClick={() => toggleSort("lfpse")}
                    title="Percentage of last-30-day incidents submitted to LFPSE">
                  LFPSE submitted{arrow("lfpse")}
                </th>
                <th style={{ ...S.th, cursor: "pointer", userSelect: "none" }}
                    onClick={() => toggleSort("rp")}
                    title="Percentage of last-30-day incidents where the RP was notified">
                  RP notified{arrow("rp")}
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(s => {
                const id = String(s.branchId);
                const isOpen = psExpanded === id;
                const incidents = psDetail[id];
                const d = s.last30Days || {};
                const incomplete = (s.lfpseIncompleteCount ?? 0) > 0;
                return (
                  <React.Fragment key={id}>
                    <tr onClick={() => togglePsRow(s)}
                        style={{ cursor: "pointer",
                                 background: incomplete ? "#fde2e1" : undefined }}
                        title={incomplete
                          ? `${s.lfpseIncompleteCount} incident(s) with LFPSE ticked but no reference — click to view`
                          : "Click to view incident history"}>
                      <td style={{ ...S.td, fontWeight: 600 }}>
                        <span style={{ display: "inline-block", width: 14,
                                       color: "#6b7280" }}>
                          {isOpen ? "▾" : "▸"}
                        </span>
                        {displayName(s.branchName, masked)}
                        {incomplete && (
                          <span style={{ marginLeft: 6, fontSize: 11, color: "#b91c1c",
                                         fontWeight: 600 }}
                                title="Incomplete LFPSE entries">
                            ⚠ {s.lfpseIncompleteCount}
                          </span>
                        )}
                      </td>
                      <td style={S.td}>{d.total ?? 0}</td>
                      <td style={S.td}>{s.lfpseSubmittedTotal ?? 0}</td>
                      <td style={S.td}>{fmtPct(d.lfpseSubmittedPct)}</td>
                      <td style={S.td}>{fmtPct(d.rpNotifiedPct)}</td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={5} style={{ ...S.td, background: "#fafafa",
                                                 padding: 12 }}>
                          {psLoadingId === id ? (
                            <div style={{ fontSize: 13, color: "#6b7280" }}>
                              Loading incidents…
                            </div>
                          ) : !incidents ? (
                            <div style={{ fontSize: 13, color: "#6b7280" }}>
                              No detail available.
                            </div>
                          ) : (
                            <div style={{ fontSize: 13, color: "#6b7280" }}>No expanded view for this module.</div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        );
      })()}

      {/* Messaging: per-branch patient enrolment. Fixed mode columns
          (SMS, FCM, EMAIL, DECLINED) — None/unset modes are ignored. Total
          patients is shaded by sign-up rate (signed-up modes / total); the
          on-shelf-messaged % is shaded green→red. Non-engaged sites
          (SMS+EMAIL < 50) are highlighted red and can be filtered to. */}
      {mod.key === "messaging" && snapshot && typeof snapshot === "object" && (() => {
        const enrol = snapshot["patient-enrolment"] || [];
        const rate = snapshot["prescription-message-rate"] || [];

        const rateById = {};
        for (const r of rate) rateById[String(r.branchId)] = r;

        // Fixed columns and the keys that count as "signed up".
        const MODE_COLS = [
          { key: "SMS",      label: "SMS" },
          { key: "FCM",      label: "FCM" },
          { key: "EMAIL",    label: "EMAIL" },
          { key: "DECLINED", label: "DECLINED" }
        ];
        const SIGNED_UP_KEYS = ["SMS", "FCM", "EMAIL", "DECLINED"];

        const modeVal = (e, k) => e.current?.byMode?.[k] ?? 0;
        const signedUp = (e) => SIGNED_UP_KEYS.reduce((t, k) => t + modeVal(e, k), 0);
        const signUpPct = (e) => {
          const tot = e.current?.total ?? 0;
          return tot > 0 ? Math.round((signedUp(e) / tot) * 10000) / 100 : null;
        };
        // Non-engaged = SMS + EMAIL < 50.
        const isNonEngaged = (e) => (modeVal(e, "SMS") + modeVal(e, "EMAIL")) < 50;

        const q = statusQuery.trim().toLowerCase();
        let rows = q
          ? enrol.filter(e => (e.branchName || "").toLowerCase().includes(q))
          : enrol;
        if (nonEngagedOnly) rows = rows.filter(isNonEngaged);

        const sortGetters = {
          branch: e => (e.branchName || "").toLowerCase(),
          total:    e => e.current?.total ?? 0,
          pct:      e => {
            const r = rateById[String(e.branchId)];
            return r && r.messagePct != null ? r.messagePct : -1;
          },
          ...Object.fromEntries(MODE_COLS.map(c =>
            [`mode:${c.key}`, e => modeVal(e, c.key)]))
        };
        const dir = statusSort.dir === "desc" ? -1 : 1;
        const getter = sortGetters[statusSort.key] || sortGetters.branch;
        const sorted = [...rows].sort((a, b) => {
          const av = getter(a), bv = getter(b);
          let cmp = (typeof av === "string") ? av.localeCompare(bv) : (av - bv);
          if (cmp === 0) cmp = (a.branchName || "").localeCompare(b.branchName || "");
          return cmp * dir;
        });
        const toggleSort = (key) => setStatusSort(prev =>
          prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
                           : { key, dir: "asc" });
        const arrow = (key) =>
          statusSort.key === key ? (statusSort.dir === "asc" ? " ▲" : " ▼") : "";

        const NON_ENGAGED_BG = "#fde2e1";
        const nonEngagedCount = enrol.filter(isNonEngaged).length;

        return (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: "flex", alignItems: "center",
                        justifyContent: "space-between", marginBottom: 6 }}>
            <div style={{ fontWeight: 600 }}>
              Patient enrolment by preferred contact method
            </div>
            <button
              style={nonEngagedOnly ? S.btn : S.btnGhost}
              onClick={() => setNonEngagedOnly(v => !v)}
              title="Non-engaged = fewer than 50 patients on SMS + EMAIL combined">
              {nonEngagedOnly
                ? "Show all sites"
                : `Show non-engaged sites only${nonEngagedCount ? ` (${nonEngagedCount})` : ""}`}
            </button>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <input
              type="text"
              value={statusQuery}
              onChange={e => setStatusQuery(e.target.value)}
              placeholder="Search branch name…"
              style={{ padding: "6px 10px", fontSize: 13, border: "1px solid #d1d5db",
                       borderRadius: 6, width: 260, outline: "none" }}
            />
            {statusQuery && (
              <button style={S.btnGhost} onClick={() => setStatusQuery("")}>Clear</button>
            )}
            <span style={{ fontSize: 12, color: "#6b7280" }}>
              {`${sorted.length} site${sorted.length === 1 ? "" : "s"}`}
            </span>
          </div>

          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>
            Each mode column is the <b>number of patients on the books who have
            chosen that contact method</b> as their preference — not messages
            sent, and not time-bounded. Total patients is shaded by sign-up rate
            (SMS + FCM + EMAIL + DECLINED, over all patients). "On-shelf messaged"
            is bags on shelf with a message sent, as a share of all bags on shelf,
            shaded green (high) to red (low). Rows in red are non-engaged sites
            (under 50 patients on SMS + EMAIL).
          </div>

          <div style={{ overflowX: "auto" }}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={{ ...S.th, background: "#f9fafb" }} rowSpan={2}
                    onClick={() => toggleSort("branch")}>
                  <span style={{ cursor: "pointer", userSelect: "none" }}>
                    Branch{arrow("branch")}
                  </span>
                </th>
                <th style={{ ...S.th, background: "#f9fafb", textAlign: "center" }} rowSpan={2}
                    onClick={() => toggleSort("total")}
                    title="Total enrolled patients (shaded by sign-up rate)">
                  <span style={{ cursor: "pointer", userSelect: "none" }}>
                    Total patients{arrow("total")}
                  </span>
                </th>
                <th colSpan={MODE_COLS.length}
                    style={{ ...S.th, background: "#eef5ff", color: "#1e3a5f",
                             textAlign: "center", fontWeight: 700, fontSize: 12 }}
                    title="Number of patients on the books who chose each method as their preference — not messages sent">
                  Patients enrolled, by preferred contact method
                </th>
                <th style={{ ...S.th, background: "#f0f6ff", color: "#1e3a5f",
                             textAlign: "center" }} rowSpan={2}
                    onClick={() => toggleSort("pct")}
                    title="Bags currently on shelf with a message sent, as a % of all bags on shelf">
                  <span style={{ cursor: "pointer", userSelect: "none" }}>
                    Bags on shelf — messages sent{arrow("pct")}
                  </span>
                </th>
              </tr>
              <tr>
                {MODE_COLS.map(c => (
                  <th key={c.key}
                      style={{ ...S.th, cursor: "pointer", userSelect: "none",
                               textAlign: "center", background: "#f5f9ff" }}
                      onClick={() => toggleSort(`mode:${c.key}`)}
                      title={`Patients who prefer ${c.label}`}>
                    {c.label}{arrow(`mode:${c.key}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr>
                  <td style={{ ...S.td, color: "#6b7280" }} colSpan={MODE_COLS.length + 3}>
                    {nonEngagedOnly
                      ? "No non-engaged sites."
                      : (statusQuery ? `No branches match “${statusQuery}”.` : "No data.")}
                  </td>
                </tr>
              ) : sorted.map(e => {
                const id = String(e.branchId);
                const r = rateById[id];
                const pct = r && r.messagePct != null ? r.messagePct : null;
                const sPct = signUpPct(e);
                const nonEng = isNonEngaged(e);
                const rowBg = nonEng ? NON_ENGAGED_BG : undefined;
                return (
                  <tr key={id} style={{ background: rowBg }}
                      title={nonEng ? "Non-engaged: under 50 patients on SMS + EMAIL" : ""}>
                    <td style={{ ...S.td, fontWeight: 600 }}>{displayName(e.branchName, masked)}</td>
                    <td style={{ ...S.td, textAlign: "center",
                                 // Sign-up-rate shade; non-engaged red wins.
                                 background: nonEng ? NON_ENGAGED_BG : pctBg(sPct) }}
                        title={sPct != null
                          ? `${signedUp(e)} signed up of ${e.current?.total ?? 0} (${sPct}%)`
                          : ""}>
                      {e.current?.total ?? 0}
                    </td>
                    {MODE_COLS.map(c => (
                      <td key={c.key} style={{ ...S.td, textAlign: "center" }}>
                        {modeVal(e, c.key)}
                      </td>
                    ))}
                    <td style={{ ...S.td, textAlign: "center", fontWeight: 600,
                                 background: nonEng ? NON_ENGAGED_BG : (pctBg(pct) || "#f7faff") }}
                        title={r ? `${r.messaged ?? 0} of ${r.onShelf ?? 0} on-shelf bags messaged` : ""}>
                      {pct != null ? `${pct}%` : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
        );
      })()}

      {snapshot && mod.key !== "handover" && mod.key !== "dateChecking" && mod.key !== "cdRegister" && mod.key !== "patientSafety" && mod.key !== "visitorLogs" && mod.key !== "messaging" && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            {mod.snapshotTitle || "Snapshot"}
          </div>
          {SNAPSHOT_FORMATTERS[mod.path] ? (
            SNAPSHOT_FORMATTERS[mod.path](snapshot)
          ) : (
            <pre style={{ background: "#f9fafb", padding: 10, borderRadius: 4,
                          fontSize: 11, overflow: "auto", maxHeight: 480 }}>
              {JSON.stringify(snapshot, null, 2)}
            </pre>
          )}
        </div>
      )}

      {/* Fridge temperature history popup — same shell + PopupContent the
          main Mockupp dashboard uses, so the graphs render identically. */}
      {popupData && (
        <div className="popup">
          <div className="popup-content">
            <button className="close-btn" onClick={() => setPopupData(null)}>X</button>
            <PopupContent popupData={popupData} />
          </div>
        </div>
      )}

      {/* Handover grouped popup: overdue (red) → pending (amber) →
          completed today (green). */}
      {groupedPopup && (
        <div className="popup">
          <div className="popup-content">
            <button className="close-btn" onClick={() => setGroupedPopup(null)}>X</button>
            <h3 style={{ marginTop: 0 }}>Handover items — {displayName(groupedPopup.branchName, masked)}</h3>
            {[
              { key: "overdue",        title: "Overdue",          bg: "#fde2e1", showDue: true },
              { key: "pending",        title: "Pending",          bg: "#fef3c7", showDue: true },
              { key: "completedToday", title: "Completed today",  bg: "#dcfce7", showCompleted: true }
            ].map(group => {
              const items = groupedPopup[group.key] || [];
              return (
                <div key={group.key} style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>
                    {group.title} ({items.length})
                  </div>
                  {items.length === 0 ? (
                    <div style={{ fontSize: 12, color: "#6b7280" }}>None.</div>
                  ) : (
                    <table style={S.table}>
                      <thead>
                        <tr>
                          <th style={S.th}>Topic</th>
                          <th style={S.th}>Description</th>
                          <th style={S.th}>For</th>
                          {group.showDue && <th style={S.th}>Due</th>}
                          {group.showCompleted && <th style={S.th}>Completed</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((it, i) => (
                          <tr key={i} style={{ background: group.bg }}>
                            <td style={S.td}>{it.topic}</td>
                            <td style={S.td}>{it.description}</td>
                            <td style={S.td}>{it.fao}</td>
                            {group.showDue && (
                              <td style={S.td}>
                                {it.dueDate}
                                {group.key === "overdue" && it.daysOverdue != null &&
                                  ` (${it.daysOverdue}d)`}
                              </td>
                            )}
                            {group.showCompleted && <td style={S.td}>{it.completedAt}</td>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Date-checking drug list for one clicked cell. Header tinted by the
          cell's urgency; rows share that tint. Most urgent (soonest expiry)
          first comes pre-sorted from the server. */}
      {/* Removed-drugs full history: paged (2 months per fetch), sortable
          price/value, inline price editing. */}
      {removedPopup && (() => {
        const rp = removedPopup;
        const qtyOf = (d) => { const n = Number(d.quantity); return Number.isFinite(n) && n > 0 ? n : 1; };
        const valueOf = (d) => (d.price != null ? d.price * qtyOf(d) : null);
        const sorted = [...rp.rows];
        if (rp.sort.key === "price" || rp.sort.key === "value") {
          const get = rp.sort.key === "price" ? (d) => d.price : valueOf;
          sorted.sort((a, b) => {
            const av = get(a), bv = get(b);
            if (av == null && bv == null) return 0;
            if (av == null) return 1;              // unpriced rows always last
            if (bv == null) return -1;
            return (av - bv) * rp.sort.dir;
          });
        } // "date" keeps the fetch order (removedAt desc)
        const setSort = (key) => setRemovedPopup(prev => prev ? {
          ...prev,
          sort: { key, dir: prev.sort.key === key ? -prev.sort.dir : (key === "date" ? -1 : 1) }
        } : prev);
        const arrow = (key) => rp.sort.key === key ? (rp.sort.dir === 1 ? " ▲" : " ▼") : "";
        const sortableTh = (key, label, title) => (
          <th style={{ ...S.th, cursor: "pointer", userSelect: "none" }} title={title}
              onClick={() => setSort(key)}>{label}{arrow(key)}</th>
        );
        const startEdit = (d) => setRemovedPopup(prev => prev ? {
          ...prev,
          editing: { drugId: d.drugId,
                     text: d.price != null ? (d.price / 100).toFixed(2) : "" }
        } : prev);
        const commitEdit = (d) => {
          const pounds = Number(rp.editing.text);
          if (!Number.isFinite(pounds) || pounds < 0) {
            setRemovedPopup(prev => prev ? { ...prev, editing: null } : prev);
            return;
          }
          saveRemovedPrice(d.drugId, Math.round(pounds * 100));
        };
        // Only the unit PRICE is editable — the value column is derived
        // (price × qty) and recomputes automatically.
        const priceCell = (d, display) => {
          const isEditing = rp.editing && rp.editing.drugId === d.drugId;
          if (isEditing) {
            return (
              <td style={{ ...S.td, background: "#eff6ff" }}>
                <input autoFocus value={rp.editing.text}
                       onChange={e => setRemovedPopup(prev => prev
                         ? { ...prev, editing: { ...prev.editing, text: e.target.value } } : prev)}
                       onKeyDown={e => { if (e.key === "Enter") commitEdit(d);
                                         if (e.key === "Escape") setRemovedPopup(prev => prev ? { ...prev, editing: null } : prev); }}
                       onBlur={() => commitEdit(d)}
                       style={{ width: 70, fontSize: 12, padding: "2px 4px",
                                border: "1px solid #93c5fd", borderRadius: 3 }} />
              </td>
            );
          }
          return (
            <td style={{ ...S.td, cursor: "pointer",
                         background: (d.price === null) ? "#fef3c7"
                                   : (d.priceSource === "manual") ? "#ecfdf5" : undefined }}
                title={(d.price === null
                          ? "No price found in dmddata — click to enter one"
                          : d.priceSource === "manual"
                            ? "Manually entered price — click to change"
                            : "Click to override the dmddata price")}
                onClick={() => startEdit(d)}>
              {display}
            </td>
          );
        };
        return (
          <div className="popup">
            <div className="popup-content" style={{ maxWidth: 980 }}>
              <button className="close-btn" onClick={() => setRemovedPopup(null)}>X</button>
              <h3 style={{ marginTop: 0,
                           background: rp.tone || undefined,
                           padding: rp.tone ? "4px 8px" : 0,
                           borderRadius: 4, display: "inline-block" }}>
                Removed drugs — {displayName(rp.branchName, masked)} ({rp.rows.length} loaded{rp.hasMore ? ", more available" : ""})
              </h3>
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>
                Full removal history, loaded two months at a time — scroll to the bottom to
                fetch older entries. Click a price to enter a manual price — the value
                column recomputes automatically (green = manually priced, amber = no
                dmddata price found).
              </div>
              {rp.rows.length === 0 && !rp.hasMore ? (
                <div style={{ fontSize: 13, color: "#6b7280" }}>No removed drugs recorded.</div>
              ) : (
                <div style={{ maxHeight: 460, overflowY: "auto" }}
                     onScroll={e => {
                       const el = e.currentTarget;
                       if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) loadMoreRemoved();
                     }}>
                  <table style={S.table}>
                    <thead>
                      <tr>
                        <th style={S.th}>Drug</th>
                        <th style={S.th}>Location</th>
                        {sortableTh("date", "Removed", "Sort by removal date")}
                        <th style={S.th}>Expiry</th>
                        <th style={S.th}>Qty</th>
                        <th style={S.th}>Batch</th>
                        {sortableTh("price", "Price (£)", "Sort by unit price")}
                        {sortableTh("value", "Value (£)", "Sort by line value (price × qty)")}
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.map((d) => {
                        const val = valueOf(d);
                        return (
                          <tr key={d.drugId}>
                            <td style={S.td}>{d.drugName}</td>
                            <td style={S.td}>{d.location}</td>
                            <td style={S.td}>{d.removedAt}</td>
                            <td style={S.td}>{d.expiryDate}</td>
                            <td style={S.td}>{d.quantity}</td>
                            <td style={S.td}>{d.batchNumber}</td>
                            {priceCell(d, d.price != null ? `£${(d.price / 100).toFixed(2)}` : "—")}
                            <td style={S.td}
                                title="Computed automatically (price × qty)">
                              {val != null ? `£${(val / 100).toFixed(2)}` : "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {rp.loadingMore && (
                    <div style={{ textAlign: "center", padding: 10, fontSize: 12, color: "#6b7280" }}>
                      Loading older entries…
                    </div>
                  )}
                  {!rp.hasMore && rp.rows.length > 0 && (
                    <div style={{ textAlign: "center", padding: 10, fontSize: 11, color: "#9ca3af" }}>
                      Full history loaded.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {drugPopup && (
        <div className="popup">
          <div className="popup-content">
            <button className="close-btn" onClick={() => setDrugPopup(null)}>X</button>
            <h3 style={{ marginTop: 0,
                         background: drugPopup.tone || undefined,
                         padding: drugPopup.tone ? "4px 8px" : 0,
                         borderRadius: 4, display: "inline-block" }}>
              {drugPopup.cellLabel} — {displayName(drugPopup.branchName, masked)} ({drugPopup.drugs.length})
            </h3>
            {drugPopup.drugs.length === 0 ? (
              <div style={{ fontSize: 13, color: "#6b7280" }}>No drugs.</div>
            ) : (() => {
              // Show a Value (£) column only when these rows carry price data
              // (the removed / not-present cells enrich from dmddata; price is
              // in pence, null = looked up but no priced match).
              const hasPrice = drugPopup.drugs.some(d => d.price !== undefined);
              return (
              <div style={{ maxHeight: 420, overflowY: "auto" }}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Drug</th>
                      <th style={S.th}>Location</th>
                      <th style={S.th}>Expiry</th>
                      <th style={S.th}>Qty</th>
                      <th style={S.th}>Batch</th>
                      {hasPrice && <th style={S.th}>Price (£)</th>}
                      {hasPrice && <th style={S.th}>Value (£)</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {drugPopup.drugs.map((d, i) => {
                      const qty = Number(d.quantity) || 0;
                      const priced = d.price !== undefined && d.price !== null;
                      const value = priced ? (d.price * qty) / 100 : null;
                      return (
                      <tr key={i} style={{ background: drugPopup.tone || undefined }}>
                        <td style={S.td}>{d.drugName}</td>
                        <td style={S.td}>{d.location}</td>
                        <td style={S.td}>{d.expiryDate}</td>
                        <td style={S.td}>{d.quantity}</td>
                        <td style={S.td}>{d.batchNumber}</td>
                        {hasPrice && (
                          <td style={{ ...S.td,
                                       background: (d.price === null) ? "#fef3c7" : undefined }}
                              title={d.price === null ? "No price found in dmddata" : ""}>
                            {priced ? `£${(d.price / 100).toFixed(2)}` : "—"}
                          </td>
                        )}
                        {hasPrice && (
                          <td style={{ ...S.td,
                                       background: (d.price === null) ? "#fef3c7" : undefined }}
                              title={d.price === null ? "No price found in dmddata" : ""}>
                            {priced ? `£${value.toFixed(2)}` : "—"}
                          </td>
                        )}
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Inactive Sites view — branches that barely used anything last month
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Prescription activity cards — "Bag activity" and "Deliveries"
// ---------------------------------------------------------------------------
// Both read the prescriptions snapshot (one fleetStats call covering all
// active branches, each row carrying { today, monthToDate }). The card face
// shows TODAY's fleet-wide totals; clicking opens a per-branch grid that
// defaults to today with a toggle for month-to-date.
//
// variant: "retrievals" → scanned + collected
//          "deliveries"  → deliveredViaApp + deliveredWithoutApp

const PRESCRIPTION_CARDS = {
  retrievals: {
    title: "Bag activity",
    blurb: "Bags stacked and handed out across the group. Card shows TODAY's totals; click for a per-branch grid.",
    metrics: [
      { key: "scanned",   label: "Scanned",   hint: "Collection bags scanned onto a shelf (excludes walk-ins)." },
      { key: "walkIn",    label: "Walk-in",   hint: "Bags handed straight to a walk-in patient — never placed on a shelf." },
      { key: "collected", label: "Collected", hint: "Collection bags handed out to patients from the shelf (excludes walk-ins)." }
    ]
  },
  deliveries: {
    title: "Deliveries",
    blurb: "Bags sent out for delivery across the group. Card shows TODAY's totals; click for a per-branch grid.",
    metrics: [
      { key: "deliveredViaApp",     label: "Via app",     hint: "Delivery bags handed to a driver through the app (driver assigned)." },
      { key: "deliveredWithoutApp", label: "Not via app", hint: "Delivery bags marked delivered without a driver assigned in the app." }
    ]
  }
};


// Detail grid for a prescription-activity card. Defaults to today; toggle MTD.

// ---------------------------------------------------------------------------
// Periodic tasks — group dictionary + branches×tasks grid + history/graph.
// Cells are colour-coded by due-state (green ok / amber due-soon / red overdue
// / grey never / "N/A" opted out). Click a cell to record a value or view its
// history (with a sparkline for numeric tasks).
// ---------------------------------------------------------------------------
const PT_FREQS = ["weekly", "monthly", "quarterly", "yearly"];
const PT_TYPES = [
  { key: "numeric", label: "Number" },
  { key: "text", label: "Text" },
  { key: "boolean", label: "Yes/No" },
  { key: "date", label: "Date" },
  { key: "choice", label: "Multiple choice" }
];

function ptFmtValue(dataType, value) {
  if (value == null || value === "") return "—";
  switch (dataType) {
    case "boolean": return value === true || value === "true" ? "Yes" : "No";
    case "date": return fmtDate(value);
    case "numeric": return String(value);
    default: return String(value);
  }
}

// Tiny dependency-free sparkline for numeric history (oldest→newest).
function Sparkline({ points, width = 480, height = 140 }) {
  if (!points.length) return null;
  const xs = points.map((_, i) => i);
  const ys = points.map(p => p.y);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const pad = 24;
  const span = (maxY - minY) || 1;
  const px = (i) => pad + (xs.length === 1 ? 0 : (i / (xs.length - 1)) * (width - 2 * pad));
  const py = (y) => height - pad - ((y - minY) / span) * (height - 2 * pad);
  const path = points.map((p, i) => `${i === 0 ? "M" : "L"} ${px(i).toFixed(1)} ${py(p.y).toFixed(1)}`).join(" ");
  return (
    <svg width={width} height={height} style={{ maxWidth: "100%", border: "1px solid #e5e7eb", borderRadius: 6, background: "#fff" }}>
      <line x1={pad} y1={height - pad} x2={width - pad} y2={height - pad} stroke="#e5e7eb" />
      <line x1={pad} y1={pad} x2={pad} y2={height - pad} stroke="#e5e7eb" />
      <text x={pad - 4} y={py(maxY) + 4} textAnchor="end" fontSize="10" fill="#6b7280">{maxY}</text>
      <text x={pad - 4} y={py(minY) + 4} textAnchor="end" fontSize="10" fill="#6b7280">{minY}</text>
      <path d={path} fill="none" stroke="#1976d2" strokeWidth="2" />
      {points.map((p, i) => (
        <circle key={i} cx={px(i)} cy={py(p.y)} r="3" fill="#1976d2" />
      ))}
    </svg>
  );
}

const PT_STATE = {
  ok:      { bg: "#dcfce7", label: "In date" },
  due:     { bg: "#fef3c7", label: "Due soon" },
  overdue: { bg: "#fde2e1", label: "Overdue" },
  never:   { bg: "#f3f4f6", label: "Never recorded" },
  na:      { bg: "#fafafa", label: "Not relevant" }
};

// One editable row in the periodic-tasks manage list.
function ManageTaskRow({ def, inactive, onSave, onToggle, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [desc, setDesc] = useState(def.description || "");
  const [freq, setFreq] = useState(def.frequency || "monthly");
  const [opts, setOpts] = useState((def.options || []).join(", "));

  // System-managed tasks are the ones linked to a Regulatory Compliance element
  // (they carry complianceElementId). These must not be edited, deactivated, or
  // deleted from here — doing so would break the compliance dashboard wiring.
  // User-added tasks have no complianceElementId and remain fully editable.
  const isSystem = !!def.complianceElementId;

  const save = () => {
    const patch = { description: desc, frequency: freq };
    if (def.dataType === "choice") patch.options = opts.split(",").map(s => s.trim()).filter(Boolean);
    onSave(patch);
    setEditing(false);
  };

  if (editing && !isSystem) {
    return (
      <tr style={{ background: "#eef5ff" }}>
        <td style={S.td}>
          <input type="text" value={desc} onChange={e => setDesc(e.target.value)}
                 style={{ padding: "4px 6px", border: "1px solid #d1d5db", borderRadius: 4, width: 240 }} />
          {def.dataType === "choice" && (
            <input type="text" value={opts} onChange={e => setOpts(e.target.value)}
                   placeholder="options, comma-separated"
                   style={{ marginTop: 4, padding: "4px 6px", border: "1px solid #d1d5db", borderRadius: 4, width: 240 }} />
          )}
        </td>
        <td style={S.td}>{PT_TYPES.find(x => x.key === def.dataType)?.label || def.dataType}</td>
        <td style={S.td}>
          <select value={freq} onChange={e => setFreq(e.target.value)}
                  style={{ padding: "4px 6px", border: "1px solid #d1d5db", borderRadius: 4 }}>
            {PT_FREQS.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
        </td>
        <td style={{ ...S.td, textAlign: "center" }}>{inactive ? "Inactive" : "Active"}</td>
        <td style={{ ...S.td, textAlign: "center" }}>
          <button style={S.btn} onClick={save} disabled={!desc.trim()}>Save</button>
          <button style={{ ...S.btnGhost, marginLeft: 6 }} onClick={() => setEditing(false)}>Cancel</button>
        </td>
      </tr>
    );
  }

  return (
    <tr style={{ background: inactive ? "#f3f4f6" : undefined, color: inactive ? "#9ca3af" : undefined }}>
      <td style={{ ...S.td, fontWeight: 600 }}>
        {def.description}
        {isSystem && (
          <span title="Required for Regulatory Compliance — managed by the system and cannot be edited or deleted here."
            style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                     letterSpacing: 0.4, padding: "1px 6px", borderRadius: 4,
                     background: "#fee2e2", color: "#991b1b", border: "1px solid #fecaca",
                     verticalAlign: "middle" }}>
            🔒 Compliance
          </span>
        )}
      </td>
      <td style={S.td}>{PT_TYPES.find(x => x.key === def.dataType)?.label || def.dataType}</td>
      <td style={S.td}>{def.frequency}</td>
      <td style={{ ...S.td, textAlign: "center" }}>
        {inactive
          ? <span style={{ color: "#9ca3af" }}>Inactive</span>
          : <span style={{ color: "#059669", fontWeight: 600 }}>Active</span>}
      </td>
      <td style={{ ...S.td, textAlign: "center", whiteSpace: "nowrap" }}>
        {isSystem ? (
          <span style={{ fontSize: 12, color: "#9ca3af", fontStyle: "italic" }}>
            Locked
          </span>
        ) : (
          <>
            <button style={S.btnGhost} onClick={() => setEditing(true)}>Edit</button>
            <button style={{ ...S.btnGhost, marginLeft: 6 }} onClick={onToggle}>
              {inactive ? "Activate" : "Make inactive"}
            </button>
            <button style={{ ...S.btnGhost, marginLeft: 6, color: "#b91c1c" }} onClick={onDelete}>
              Delete
            </button>
          </>
        )}
      </td>
    </tr>
  );
}

function PeriodicTasksView({ token, onClose, initialOverdue = false }) {
  const masked = useMaskNames();
  const [grid, setGrid] = useState(null);     // { tasks, rows, summary }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [overdueOnly, setOverdueOnly] = useState(initialOverdue);
  const [eventSearch, setEventSearch] = useState("");   // "Search for event" box
  const [allDefs, setAllDefs] = useState(null);   // incl. inactive, for manage

  // New-task form
  const [form, setForm] = useState({ description: "", dataType: "numeric",
                                     frequency: "monthly", options: "" });

  // Cell entry / history popups. Entry now opens the SHARED
  // PeriodicTaskEntryDialog (value + month/date + notes) — the same popup
  // Regulatory Compliance and the Single-branch page use.
  const [entryCell, setEntryCell] = useState(null);   // { row, task }
  const [historyCell, setHistoryCell] = useState(null); // { row, task, def, entries }
  // Estate-wide monthly trend popup (numeric column header click).
  const [estateTrend, setEstateTrend] = useState(null); // { task, points }

  const openEstateTrend = async (task) => {
    if (task.dataType !== "numeric") return;
    try {
      const t = await apiGet(
        `periodic-tasks/estate-trend?taskId=${encodeURIComponent(String(task.taskId))}`,
        token);
      setEstateTrend({ task, points: t.points || [] });
    } catch (e) { setError(e.message); }
  };

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const g = await apiGet("periodic-tasks/grid", token);
      setGrid(g); setError(null);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [token]);

  // Manage list includes inactive tasks.
  const loadManage = useCallback(async () => {
    try {
      const d = await apiGet("periodic-tasks/defs?includeInactive=true", token);
      setAllDefs(Array.isArray(d) ? d : []);
    } catch (e) { setError(e.message); }
  }, [token]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { if (showManage) loadManage(); }, [showManage, loadManage]);

  const setTaskActive = async (taskId, isActive) => {
    try {
      await apiSend(`periodic-tasks/defs/${taskId}`, token, "PUT", { isActive });
      loadManage(); reload();
    } catch (e) { setError(e.message); }
  };

  const saveTaskEdit = async (taskId, patch) => {
    try {
      await apiSend(`periodic-tasks/defs/${taskId}`, token, "PUT", patch);
      loadManage(); reload();
    } catch (e) { setError(e.message); }
  };

  const addTask = async () => {
    try {
      const body = {
        description: form.description,
        dataType: form.dataType,
        frequency: form.frequency,
        options: form.dataType === "choice"
          ? form.options.split(",").map(s => s.trim()).filter(Boolean) : []
      };
      await apiSend("periodic-tasks/defs", token, "POST", body);
      setForm({ description: "", dataType: "numeric", frequency: "monthly", options: "" });
      setShowAdd(false);
      reload();
    } catch (e) { setError(e.message); }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm("Delete this task for the whole group? Existing values are kept but hidden.")) return;
    try { await apiSend(`periodic-tasks/defs/${taskId}`, token, "DELETE"); reload(); }
    catch (e) { setError(e.message); }
  };

  const toggleOptout = async (row, task, optedOut) => {
    try {
      await apiSend("periodic-tasks/optout", token, "POST", {
        taskId: String(task.taskId), dbName: row.dbName,
        branchId: String(row.branchId), optedOut
      });
      reload();
    } catch (e) { setError(e.message); }
  };

  const openEntry = (row, task) => setEntryCell({ row, task });

  const openHistory = async (row, task) => {
    try {
      const h = await apiGet(
        `periodic-tasks/history?taskId=${encodeURIComponent(String(task.taskId))}&dbName=${encodeURIComponent(row.dbName)}`,
        token);
      setHistoryCell({ row, task, def: h.def, entries: h.entries || [] });
    } catch (e) { setError(e.message); }
  };

  const allTasks = grid?.tasks || [];
  const q = query.trim().toLowerCase();
  const rows = (grid?.rows || []).filter(r =>
    (!q || (r.branchName || "").toLowerCase().includes(q)) &&
    (!overdueOnly || r.isOverdue));

  // "Search for event": match the typed text against ANY text of an event —
  // the task's description/frequency/type, or any branch's cell for it
  // (value, formatted date, note, entered-by). Non-matching task columns hide.
  const eq = eventSearch.trim().toLowerCase();
  const tasks = !eq ? allTasks : allTasks.filter(t => {
    const head = `${t.description || ""} ${t.frequency || ""} ${t.dataType || ""}`.toLowerCase();
    if (head.includes(eq)) return true;
    return (grid?.rows || []).some(r => {
      const c = r.cells?.[String(t.taskId)];
      if (!c) return false;
      const parts = [
        c.value != null ? ptFmtValue(t.dataType, c.value) : "",
        c.value != null ? String(c.value) : "",
        c.recordedAt ? fmtDate(c.recordedAt) : "",
        c.note || "",
        c.recordedBy || "",
        PT_STATE[c.state]?.label || ""
      ].join(" ").toLowerCase();
      return parts.includes(eq);
    });
  });
  const summary = grid?.summary || {};

  return (
    <div style={S.drill}>
      {loading && <LoadingOverlay label="Loading periodic tasks…" />}
      <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "center", marginBottom: 12 }}>
        <div style={S.drillTitle}>Periodic tasks</div>
        <div>
          <button
            onClick={async () => {
              if (!window.__ptCascadeBusy) {
                window.__ptCascadeBusy = true;
                try {
                  const d = await apiSend("periodic-tasks/cascade", token, "POST", {});
                  if (d.sent) {
                    window.alert(`Cascade email sent to ${d.to} with ${d.links.length} capture link${d.links.length === 1 ? "" : "s"}:\n\n` +
                      d.links.map(l => `• ${l.branchName} — ${l.outstanding} outstanding`).join("\n"));
                  } else {
                    window.alert(d.message || "Nothing outstanding to cascade, or the email could not be sent.");
                  }
                } catch (e) { window.alert("Cascade failed: " + (e.message || e)); }
                finally { window.__ptCascadeBusy = false; }
              }
            }}
            title="Email a mobile capture-form link for every branch with overdue or never-recorded tasks. Staff record results (with photos) from their device; completed tasks disappear from everyone's list."
            style={{ padding: "6px 12px", fontSize: 12.5, borderRadius: 6, cursor: "pointer", border: "1px solid #93c5fd", background: "#eff6ff", color: "#1560ac", fontWeight: 700, lineHeight: 1.2 }}>
            Cascade overdue
          </button>
          <button style={overdueOnly ? S.btn : S.btnGhost}
                  onClick={() => setOverdueOnly(v => !v)}
                  title="Filter to branches with at least one overdue task">
            {overdueOnly ? "Show all" : "Show overdue"}
          </button>
          <button style={{ ...S.btnGhost, marginLeft: 8 }} onClick={() => setShowManage(v => !v)}>
            {showManage ? "Hide manage" : "Manage tasks"}
          </button>
          <button style={{ ...S.btn, marginLeft: 8 }} onClick={() => setShowAdd(v => !v)}>
            {showAdd ? "Cancel" : "+ Add task"}
          </button>
          <button style={{ ...S.btnGhost, marginLeft: 8 }} onClick={onClose}>← Back</button>
        </div>
      </div>

      {error && <div style={S.error}>{error}</div>}

      {/* Manage list — edit description/frequency, activate/deactivate, delete */}
      {showManage && (
        <div style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: 12,
                      marginBottom: 14, background: "#f9fafb" }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>
            All tasks{" "}
            <span style={{ fontWeight: 400, color: "#6b7280", fontSize: 12 }}>
              (including inactive)
            </span>
          </div>
          {!allDefs ? (
            <div style={{ fontSize: 13, color: "#6b7280" }}>Loading…</div>
          ) : allDefs.length === 0 ? (
            <div style={{ fontSize: 13, color: "#6b7280" }}>No tasks defined.</div>
          ) : (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Description</th>
                  <th style={S.th}>Type</th>
                  <th style={S.th}>Frequency</th>
                  <th style={{ ...S.th, textAlign: "center" }}>Status</th>
                  <th style={{ ...S.th, textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {allDefs.map(d => {
                  const inactive = d.isActive === false;
                  return (
                    <ManageTaskRow key={String(d._id)} def={d} inactive={inactive}
                      onSave={(patch) => saveTaskEdit(String(d._id), patch)}
                      onToggle={() => setTaskActive(String(d._id), inactive)}
                      onDelete={() => deleteTask(String(d._id))} />
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Add-task form */}
      {showAdd && (
        <div style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: 12,
                      marginBottom: 14, background: "#f9fafb" }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
            <label style={{ fontSize: 12, color: "#374151" }}>
              Description<br />
              <input type="text" value={form.description}
                     onChange={e => setForm({ ...form, description: e.target.value })}
                     placeholder="e.g. Calibrate fridge thermometer"
                     style={{ padding: "6px 8px", border: "1px solid #d1d5db",
                              borderRadius: 4, width: 280 }} />
            </label>
            <label style={{ fontSize: 12, color: "#374151" }}>
              Data type<br />
              <select value={form.dataType}
                      onChange={e => setForm({ ...form, dataType: e.target.value })}
                      style={{ padding: "6px 8px", border: "1px solid #d1d5db", borderRadius: 4 }}>
                {PT_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </label>
            <label style={{ fontSize: 12, color: "#374151" }}>
              Frequency<br />
              <select value={form.frequency}
                      onChange={e => setForm({ ...form, frequency: e.target.value })}
                      style={{ padding: "6px 8px", border: "1px solid #d1d5db", borderRadius: 4 }}>
                {PT_FREQS.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </label>
            {form.dataType === "choice" && (
              <label style={{ fontSize: 12, color: "#374151" }}>
                Options (comma-separated)<br />
                <input type="text" value={form.options}
                       onChange={e => setForm({ ...form, options: e.target.value })}
                       placeholder="Pass, Fail, N/A"
                       style={{ padding: "6px 8px", border: "1px solid #d1d5db",
                                borderRadius: 4, width: 220 }} />
              </label>
            )}
            <button style={S.btn} onClick={addTask}
                    disabled={!form.description.trim()}>Save task</button>
          </div>
        </div>
      )}

      {/* Legend + search */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10, alignItems: "center" }}>
        {["ok", "due", "overdue", "never", "na"].map(st => (
          <span key={st} style={{ background: PT_STATE[st].bg, borderRadius: 6,
                                  padding: "3px 10px", fontSize: 12, fontWeight: 600,
                                  color: "#374151" }}>
            {PT_STATE[st].label}
          </span>
        ))}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <input type="text" value={query} onChange={e => setQuery(e.target.value)}
               placeholder="Search branch name…"
               style={{ padding: "6px 10px", fontSize: 13, border: "1px solid #d1d5db",
                        borderRadius: 6, width: 240, outline: "none" }} />
        {query && <button style={S.btnGhost} onClick={() => setQuery("")}>Clear</button>}
        <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginLeft: 10 }}>
          Search for event:
        </label>
        <input type="text" value={eventSearch} onChange={e => setEventSearch(e.target.value)}
               placeholder="Any text — task, value, note, staff, date…"
               title="Matches against every part of an event: task name, frequency, recorded value, date, note, entered-by and state. Non-matching columns are hidden."
               style={{ padding: "6px 10px", fontSize: 13, border: "1px solid #d1d5db",
                        borderRadius: 6, width: 260, outline: "none" }} />
        {eventSearch && <button style={S.btnGhost} onClick={() => setEventSearch("")}>Clear</button>}
        <span style={{ fontSize: 12, color: "#6b7280" }}>
          {tasks.length}{eq ? ` of ${allTasks.length}` : ""} task{tasks.length === 1 ? "" : "s"} · {rows.length} site{rows.length === 1 ? "" : "s"}
        </span>
      </div>

      {tasks.length === 0 ? (
        <div style={{ fontSize: 13, color: "#6b7280" }}>
          No tasks yet. Click "+ Add task" to create the first one.
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={{ ...S.th, position: "sticky", left: 0, background: "#fff" }}>
                  Branch
                </th>
                {tasks.map(t => {
                  const isNumeric = t.dataType === "numeric";
                  return (
                  <th key={String(t.taskId)} style={{ ...S.th, textAlign: "center", minWidth: 120 }}>
                    <span
                      onClick={() => isNumeric && openEstateTrend(t)}
                      style={{ cursor: isNumeric ? "pointer" : "default",
                               textDecoration: isNumeric ? "underline" : "none",
                               color: isNumeric ? "#1e3a5f" : undefined }}
                      title={isNumeric ? "Click for the estate-wide monthly trend" : ""}>
                      {t.description}
                    </span>
                    <div style={{ fontWeight: 400, fontSize: 11, color: "#6b7280" }}>
                      {t.frequency} · {PT_TYPES.find(x => x.key === t.dataType)?.label || t.dataType}
                    </div>
                    {t.complianceElementId ? (
                      <span title="Required for Regulatory Compliance — cannot be deleted."
                            style={{ fontSize: 10, color: "#991b1b", fontWeight: 700, marginTop: 2,
                                     display: "inline-block" }}>
                        🔒 Compliance
                      </span>
                    ) : (
                      <button style={{ ...S.btnGhost, fontSize: 10, padding: "1px 6px", marginTop: 2 }}
                              onClick={() => deleteTask(String(t.taskId))}
                              title="Delete this task for the group">delete</button>
                    )}
                  </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={String(r.branchId)}>
                  <td style={{ ...S.td, fontWeight: 600, position: "sticky", left: 0,
                               background: "#fff" }}>
                    {displayName(r.branchName, masked)}
                  </td>
                  {tasks.map(t => {
                    const cell = r.cells[String(t.taskId)] || { state: "never" };
                    const sc = PT_STATE[cell.state] || PT_STATE.never;
                    if (cell.state === "na") {
                      return (
                        <td key={String(t.taskId)}
                            style={{ ...S.td, textAlign: "center", background: sc.bg,
                                     color: "#9ca3af", cursor: "pointer" }}
                            title="Marked not relevant — click to re-enable"
                            onClick={() => toggleOptout(r, t, false)}>
                          N/A
                        </td>
                      );
                    }
                    return (
                      <td key={String(t.taskId)}
                          style={{ ...S.td, textAlign: "center", background: sc.bg,
                                   cursor: "pointer", position: "relative" }}
                          title={`${sc.label}${cell.recordedAt ? ` — last ${fmtDate(cell.recordedAt)}` : ""}${cell.recordedBy ? `\nEntered by ${cell.recordedBy}` : ""}${cell.note ? `\n“${cell.note}”` : ""}\nClick to ${cell.entryId ? "edit this entry" : "record"}`}
                          onClick={() => openEntry(r, t)}>
                        <div style={{ fontWeight: 600 }}>
                          {ptFmtValue(t.dataType, cell.value)}
                        </div>
                        <div style={{ fontSize: 11, color: "#6b7280" }}>
                          {cell.recordedAt ? fmtDate(cell.recordedAt) : "—"}
                        </div>
                        {cell.note && (
                          <div style={{ fontSize: 10, color: "#64748b", fontStyle: "italic",
                                        maxWidth: 130, margin: "0 auto", overflow: "hidden",
                                        textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            “{cell.note}”
                          </div>
                        )}
                        <span title="View full history"
                              onClick={(e) => { e.stopPropagation(); openHistory(r, t); }}
                              style={{ position: "absolute", top: 2, right: 4, fontSize: 10,
                                       color: "#94a3b8", cursor: "pointer" }}>⌚</span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* History / graph popup */}
      {historyCell && (() => {
        const { row, task, def, entries } = historyCell;
        const dataType = def?.dataType || task.dataType;
        const numericPoints = dataType === "numeric"
          ? [...entries].reverse().map(e => ({ x: e.recordedAt, y: Number(e.value) }))
          : [];
        return (
          <div className="popup">
            <div className="popup-content">
              <button className="close-btn" onClick={() => setHistoryCell(null)}>X</button>
              <h3 style={{ marginTop: 0, marginBottom: 2 }}>{displayName(row.branchName, masked)}</h3>
              <div style={{ fontSize: 13, color: "#374151", marginBottom: 12 }}>
                {task.description}{" "}
                <span style={{ color: "#6b7280" }}>
                  ({task.frequency} · {PT_TYPES.find(x => x.key === dataType)?.label || dataType})
                </span>
              </div>

              {/* Record-new + opt-out actions */}
              <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
                <button style={S.btn} onClick={() => { setHistoryCell(null); openEntry(row, task); }}>
                  + Record value
                </button>
                <button style={S.btnGhost} onClick={() => { toggleOptout(row, task, true); setHistoryCell(null); }}>
                  Mark not relevant
                </button>
              </div>

              {numericPoints.length > 1 && (
                <div style={{ marginBottom: 14 }}>
                  <Sparkline points={numericPoints} />
                </div>
              )}

              {entries.length === 0 ? (
                <div style={{ fontSize: 13, color: "#6b7280" }}>No values recorded yet.</div>
              ) : (
                <div style={{ maxHeight: 320, overflowY: "auto" }}>
                  <table style={S.table}>
                    <thead>
                      <tr>
                        <th style={S.th}>Recorded</th>
                        <th style={S.th}>Value</th>
                        <th style={S.th}>By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map(e => (
                        <tr key={String(e._id)}>
                          <td style={S.td}>{fmtDateTime(e.recordedAt)}</td>
                          <td style={{ ...S.td, fontWeight: 600 }}>
                            {ptFmtValue(dataType, e.value)}
                          </td>
                          <td style={S.td}>{e.recordedBy || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Estate-wide monthly trend popup (numeric task, column-header click) */}
      {estateTrend && (() => {
        const pts = estateTrend.points || [];
        const graphPoints = pts.map(p => ({ x: p.month, y: p.sum }));
        return (
          <div className="popup">
            <div className="popup-content">
              <button className="close-btn" onClick={() => setEstateTrend(null)}>X</button>
              <h3 style={{ marginTop: 0, marginBottom: 2 }}>
                {estateTrend.task.description}
              </h3>
              <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 12 }}>
                Estate-wide total by month (all sites combined)
              </div>
              {graphPoints.length === 0 ? (
                <div style={{ fontSize: 13, color: "#6b7280" }}>No entries recorded yet.</div>
              ) : (
                <>
                  <Sparkline points={graphPoints} width={560} height={180} />
                  {/* Month axis labels */}
                  <div style={{ display: "flex", justifyContent: "space-between",
                                fontSize: 10, color: "#6b7280", margin: "2px 24px 14px" }}>
                    <span>{pts[0].month}</span>
                    {pts.length > 2 && <span>{pts[Math.floor(pts.length / 2)].month}</span>}
                    <span>{pts[pts.length - 1].month}</span>
                  </div>
                  <div style={{ maxHeight: 240, overflowY: "auto" }}>
                    <table style={S.table}>
                      <thead>
                        <tr>
                          <th style={S.th}>Month</th>
                          <th style={{ ...S.th, textAlign: "center" }}>Total</th>
                          <th style={{ ...S.th, textAlign: "center" }}>Readings</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...pts].reverse().map(p => (
                          <tr key={p.month}>
                            <td style={S.td}>{p.month}</td>
                            <td style={{ ...S.td, textAlign: "center", fontWeight: 600 }}>{p.sum}</td>
                            <td style={{ ...S.td, textAlign: "center", color: "#6b7280" }}>{p.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })()}

      {/* Value-entry popup */}
      {entryCell && (() => {
        const { row, task } = entryCell;
        return (
          <PeriodicTaskEntryDialog
            token={token}
            task={task}
            target={{ dbName: row.dbName, branchId: row.branchId,
                      branchName: displayName(row.branchName, masked) }}
            existingEntry={(() => {
              const c = row.cells?.[String(task.taskId)];
              return c?.entryId ? { entryId: c.entryId, value: c.value,
                recordedAt: c.recordedAt, note: c.note, recordedBy: c.recordedBy } : null;
            })()}
            onClose={() => setEntryCell(null)}
            onSaved={() => { setEntryCell(null); reload(); }}
          />
        );
      })()}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SOP compliance card (face) — moved out of the old Compliance menu.
// Face shows: title + sites-using ratio (ever assigned a policy), then a
// row for sites with unread policies (xx/yy), red background if > 0.
// Click → full-page SOPCompliance view.
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Staff appraisals card — outstanding appraisals across the fleet.
// Red border + red-tinted count row when any staff member is overdue
// (anniversary model: >12 months since last completed appraisal, or
// never appraised and employed >12 months). Amber if only "due".
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Clock in/out card — opens the attendance panel (Day / Month / Summary).
// The "today" dynamics (who's in now, late arrivals so far, etc.) are
// deliberately deferred: the card is a plain opener until the detail views
// are signed off, then a status endpoint will light it up like the others.
// ---------------------------------------------------------------------------
// Holiday rota card - opens the per-branch rota (leave, absence, clockings).
// A plain opener for now: a status endpoint could light it up later with, say,
// how many branches are short-staffed this week.
// ---------------------------------------------------------------------------
/**
 * The customer register, on the dashboard.
 *
 * Reads /api/customers rather than /api/headoffice — customers are group-scoped
 * and the fleet dispatcher is branch-shaped, so routing this through the module
 * engine would have meant inventing a per-branch answer to a question that no
 * longer has one.
 *
 * page_size=1 because only the counts are wanted; the endpoint returns them
 * alongside the page regardless, so this costs one row of payload.
 */
function CustomersCard({ token, onOpen }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/customers?page_size=1`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json().then(d => {
        if (!r.ok) throw new Error((d.error && d.error.message) || `HTTP ${r.status}`);
        return d;
      }))
      .then(d => { if (!cancelled) setData(d); })
      .catch(e => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div style={{ ...S.card, border: "3px solid #22c55e" }} onClick={onOpen}>
      <div style={S.cardTitle}>Customers</div>
      <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 8, lineHeight: 1.4 }}>
        Everyone who receives parcels. One record per person, across every
        branch — a customer belongs to the group, their deliveries belong to
        the branch that sent them.
      </div>
      <div style={S.metaRow} title="Active people on the register.">
        <span>On the register</span>
        <span>{data ? data.total : (error ? "?" : "…")}</span>
      </div>
      <div style={S.metaRow}
           title="People with at least one parcel still waiting or out with a driver.">
        <span>Waiting on a parcel</span>
        <span>{data ? data.with_outstanding : (error ? "?" : "…")}</span>
      </div>
      {error && (
        <div style={{ fontSize: 11, color: "#b91c1c", marginTop: 6 }}>{error}</div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Communications card — replaces the old left-nav menu entry. Two buttons,
// each opening the Communications page pre-selected to that workspace
// (App.js's navParams -> Communications' initialTab prop). Unread/read
// counts come from GET /api/comms/summary — one call covers both buttons.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Staff messaging card and Customer messaging card — two separate cards,
// replacing the single combined Communications card (Sept 2026). Each is
// its own full card, styled exactly like CustomersCard/VehiclesCard rather
// than nested buttons inside a shared one — each opens straight into its
// own dedicated messaging page (Communications.js picks the right one from
// initialTab, showing only that audience's tabs, not a switcher between
// both). Both still read GET /api/comms/summary; each just displays its
// own half of it.
// ---------------------------------------------------------------------------
function StaffMessagingCard({ token, onNavigate }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/comms/summary`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json().then(d => {
        if (!r.ok) throw new Error((d.error && d.error.message) || `HTTP ${r.status}`);
        return d;
      }))
      .then(d => { if (!cancelled) setData(d); })
      .catch(e => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [token]);

  const drivers = data?.drivers;
  const hasUnread = (drivers?.unread || 0) > 0;

  return (
    <div style={{ ...S.card, border: `3px solid ${hasUnread ? "#f59e0b" : "#22c55e"}` }}
         onClick={() => onNavigate && onNavigate("communications", { tab: "drivers" })}>
      <div style={S.cardTitle}>Staff messaging</div>
      <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 8, lineHeight: 1.4 }}>
        Every message to and from drivers — push or email, whichever
        actually got through.
      </div>
      <div style={S.metaRow} title="Messages from drivers you haven't looked at yet.">
        <span>Number of unread messages</span>
        <span>{data ? drivers.unread : (error ? "?" : "…")}</span>
      </div>
      <div style={S.metaRow} title="Messages from drivers you've already read.">
        <span>Number read messages</span>
        <span>{data ? drivers.read : (error ? "?" : "…")}</span>
      </div>
      {error && (
        <div style={{ fontSize: 11, color: "#b91c1c", marginTop: 6 }}>{error}</div>
      )}
    </div>
  );
}

function CustomerMessagingCard({ token, onNavigate }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/comms/summary`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json().then(d => {
        if (!r.ok) throw new Error((d.error && d.error.message) || `HTTP ${r.status}`);
        return d;
      }))
      .then(d => { if (!cancelled) setData(d); })
      .catch(e => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [token]);

  const customers = data?.customers;
  const hasUnread = (customers?.unread || 0) > 0;

  return (
    <div style={{ ...S.card, border: `3px solid ${hasUnread ? "#f59e0b" : "#22c55e"}` }}
         onClick={() => onNavigate && onNavigate("communications", { tab: "customers" })}>
      <div style={S.cardTitle}>Customer messaging</div>
      <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 8, lineHeight: 1.4 }}>
        Every message to and from customers — push or email, whichever
        actually got through.
      </div>
      <div style={S.metaRow} title="Messages from customers you haven't looked at yet.">
        <span>Number of unread messages</span>
        <span>{data ? customers.unread : (error ? "?" : "…")}</span>
      </div>
      <div style={S.metaRow} title="Messages from customers you've already read.">
        <span>Number read messages</span>
        <span>{data ? customers.read : (error ? "?" : "…")}</span>
      </div>
      {error && (
        <div style={{ fontSize: 11, color: "#b91c1c", marginTop: 6 }}>{error}</div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Staff cards removed (Aug 2026)
// ---------------------------------------------------------------------------
// Clock in/out, holiday rota, staff absences and appraisals came across with
// the pharmacy cull and are not part of this product. Their cards, the
// ComplianceFullPage host that opened them and the four imports feeding it are
// gone from here. ClockInOut.js, holiday_rota.js, StaffAbsences.js, Staff.js
// and StaffAppraisal.js are untouched on disk - App.js still routes Staff and
// the holiday rota - so restoring any of this is a card plus a case, not a
// rebuild.
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// Staff card (face) — mock data for now. Amber card throughout.
// Face shows: title + sites-with-appraisals ratio, and a red "Complete
// appraisals" line with an outstanding count.
// Click → full-page Staff grid (same as the left-menu Staff view).
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// Accident book card (face). Pulls live counts from /api/headoffice/accident-book/riddor-card.
//   • ratio = branches with any accident-book entries / total sites
//   • Total non-RIDDOR reportable accidents
//   • Total RIDDOR reportable accidents (amber if > 0)
//   • Total accidents with RIDDOR status not yet known (red if > 0)
// Click → full-page Accident book detail (three panels).
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// Action Centre panel — sits to the right of the live-view banner.
// Shows the fleet-wide pending alert count and a button into the full-page
// Action Centre. Amber border/badge when there are pending alerts.
// ---------------------------------------------------------------------------
function ActionCentrePanel({ token, onOpen }) {
  const [counts, setCounts] = useState(null); // { notOpened, notAcknowledged }
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    apiGet("action-centre/card", token)
      .then(d => { if (!cancelled) { setCounts(d || { notOpened: 0, notAcknowledged: 0 }); setLoaded(true); } })
      .catch(() => { if (!cancelled) { setCounts(null); setLoaded(true); } });
    return () => { cancelled = true; };
  }, [token]);

  const notOpened = counts?.notOpened ?? 0;
  const notAck = counts?.notAcknowledged ?? 0;
  const has = (notOpened + notAck) > 0;
  const borderColor = !loaded ? "#f59e0b" : (has ? "#f59e0b" : "#22c55e");

  const badge = (label, count, color) => (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 999,
      color: color.text, background: color.bg, border: `1px solid ${color.border}`
    }}>
      {label}
      <span style={{ fontWeight: 700 }}>{loaded ? count : "…"}</span>
    </span>
  );

  // Horizontal strip — sits in line with the page heading at the top.
  return (
    <div style={{
      background: "#fff",
      border: `2px solid ${borderColor}`, borderRadius: 6,
      padding: "3px 10px", display: "flex", flexDirection: "row",
      gap: 8, alignItems: "center", flexWrap: "wrap"
    }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: "#111827", whiteSpace: "nowrap" }}>
        Action Centre
      </div>
      {badge("Not opened", notOpened, { text: "#92400e", bg: "#fef3c7", border: "#fde68a" })}
      {badge("Not acknowledged", notAck, { text: "#7f1d1d", bg: "#fee2e2", border: "#fecaca" })}
      <button
        style={S.btnGhost}
        onClick={onOpen}
      >
        Open Action Centre
      </button>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Dashboard row groups. Hoisted to module scope because the "Show rows"
// checkbox strip now renders in the page header, outside the block that used
// to own this list. Computed once — MODULES is a module constant, and any key
// not assigned to a group falls into "Other" so a new module never vanishes.
// ---------------------------------------------------------------------------
const DASHBOARD_GROUPS = (() => {
  const groups = [
    // Drivers joins Customers on the top line. The old "Drivers" row held it
    // alongside the four staff cards; with those gone it would have been a row
    // containing one card, so the row went and the card moved up.
    // Vehicles joined this row 25 Aug 2026, on the right, in the place of a
    // row of its own that had held nothing else.
    // Communications split into two separate cards (Sept 2026), each its
    // own peer of Customers/Drivers/Vehicles rather than one shared card.
    { label: "Customers, Drivers, Vehicles and Communications", keys: ["customers", "drivers", "vehicles", "staff_messaging", "customer_messaging"] },
    { label: "On the road",           keys: ["rounds", "deliveriesToday", "failedAttempts"] }
  ];
  const assigned = new Set(groups.flatMap(g => g.keys));
  const otherKeys = MODULES.map(m => m.key).filter(k => !assigned.has(k));
  if (otherKeys.length) groups.push({ label: "Other", keys: otherKeys });
  return groups;
})();

// Refresh / Job runs / Inactive sites / Pat App - dev are hidden per request but
// kept in code. Flip to false to restore that toolbar row.
const HIDDEN_TOOLBAR = true;

// ---------------------------------------------------------------------------
// HeaderPortal — if App.js provides the #section-header-slot next to the
// organisation title, the section heading (and anything beside it, like the
// Action Centre strip) renders INTO that top line instead of taking a row
// of its own — killing the vertical waste. Falls back to normal inline
// rendering when the slot doesn't exist (older App.js, tests, embeds).
// ---------------------------------------------------------------------------
function HeaderPortal({ children }) {
  const [slot, setSlot] = useState(null);
  useEffect(() => {
    setSlot(document.getElementById("section-header-slot"));
    return () => {
      // leave the slot itself in place; React removes our portal content
    };
  }, []);
  return slot ? createPortal(children, slot) : children;
}

// ---------------------------------------------------------------------------
// Top-level HeadOffice view
// ---------------------------------------------------------------------------
export default function HeadOffice({ token: tokenProp, onNavigate } = {}) {
  const [token] = useState(() =>
    tokenProp ||
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    ""
  );
  const [selected, setSelected] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [view, setView] = useState("modules"); // "modules" | "job_runs" | "inactive"
  // Messaging SIM detail (placeholder) — set to the messaging module when the
  // SIM line on its card is clicked.
  // Periodic-tasks view opened with the overdue filter pre-applied.
  const [periodicOverdue, setPeriodicOverdue] = useState(false);

  // Prescription activity snapshot (shared by the two activity cards + grids)

  // Compliance full-page view: "sop" | "nearMiss" | "actionCentre" | null.
  // Renders the migrated full-page components inline (replaces the old
  // Compliance menu entries).
  // The fleet map has no state here: it lives permanently in the right-hand
  // column of the dashboard rather than behind a button.

  // The prescriptions snapshot went with the pharmacy modules.

  if (!token) {
    return <div style={S.wrap}><div style={S.error}>Not signed in.</div></div>;
  }

  return (
    <div style={S.wrap}>
      {/* Heading row. The "Head Office — Fleet Dashboard" title and the
          per-row "Show rows" checkbox filter were removed per request —
          every row always shows now. Rendered INTO the top organisation
          line via HeaderPortal when App.js provides the slot, so the whole
          header costs no vertical space of its own. */}
      <HeaderPortal>
      <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
        {/* Action Centre strip removed — the Action centre has moved to the
            Communications page in full. Nothing here links to it any more;
            ActionCentrePanel below is left in place, unused, so it can be
            restored with one line if the move is reversed. */}
        <MaskToggleButton />
      </div>
      </HeaderPortal>

      {/* Refresh / Job runs / Inactive sites / Pat App - dev hidden per request
          but kept in code — flip HIDDEN_TOOLBAR (module scope) to false to
          restore them. The views they open (job_runs / inactive / the Pat App
          config) remain fully functional underneath. The whole row is gated
          now rather than just its contents: "Hide names" has moved to the
          header, so leaving the row in place would render an empty div with a
          16px bottom margin. */}
      {/* The dev toolbar (Refresh / Job runs / Inactive sites / Pat App) was
          already hidden behind HIDDEN_TOOLBAR, and three of its four buttons
          pointed at screens removed with the branch modules. Refresh alone
          did not justify keeping the block. */}

      {view === "periodic" && (
        <PeriodicTasksView token={token} initialOverdue={periodicOverdue}
          onClose={() => { setView("modules"); setPeriodicOverdue(false); }} />
      )}

      {view === "vehicles" && (
        <VehiclesView onClose={() => setView("modules")} />
      )}

      {view === "modules" && !selected && (
        <>
          {/* Dashboard cards, arranged in labelled groups. Module cards are
              looked up from MODULES by key; custom cards use special tokens.
              Any MODULES key not assigned to a group lands in "Other"
              (hidden while empty), so future modules never vanish. */}
          {(() => {
            const GROUPS = DASHBOARD_GROUPS;

            const cardFor = (key) => {
              switch (key) {
                case "customers":
                  return <CustomersCard key={key} token={token}
                    onOpen={() => onNavigate && onNavigate("customers")} />;
                case "communications":
                  // Retained as a no-op case rather than deleted: if a stale
                  // link or bookmark ever passes the old single "communications"
                  // key, it silently renders nothing instead of crashing on
                  // an unmatched switch falling through to MODULES.find(),
                  // which would return null anyway for a non-MODULES key —
                  // this is just that same outcome, documented rather than
                  // implicit.
                  return null;
                case "staff_messaging":
                  return <StaffMessagingCard key={key} token={token} onNavigate={onNavigate} />;
                case "customer_messaging":
                  return <CustomerMessagingCard key={key} token={token} onNavigate={onNavigate} />;
                case "vehicles":
                  // Vehicles recorded from the app, by branch, editable in
                  // place. Replaces the periodic-entries card, whose backend
                  // was the pharmacy platform's and answered 404 here.
                  return <VehiclesCard key={key} onOpen={() => setView("vehicles")} />;
                default: {
                  // Everything else is a MODULES entry driven by the generic
                  // engine — rounds, deliveries today, failed attempts,
                  // drivers. Adding a module needs no code here.
                  const m = MODULES.find(x => x.key === key);
                  if (!m) return null;
                  return <ModuleCard key={m.key} mod={m} token={token} onSelect={setSelected} />;
                }
              }
            };

            return (
              /* TWO COLUMNS. The card rows take the left half and the fleet map
                 holds the right, permanently, because the two answer the same
                 question from different ends - the rows say how much is
                 outstanding, the map says where it is.

                 flexWrap, so a narrow window stacks them rather than crushing
                 both. minWidth:0 on the left column because a flex child
                 defaults to min-content width and the card grid inside would
                 otherwise refuse to narrow below its widest card. */
              <div key={refreshKey} style={{ display: "flex", gap: 18, alignItems: "flex-start", flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 520px", minWidth: 0 }}>
                  {GROUPS.map(g => (
                    <div key={g.label} style={{
                      border: "1px solid #d1d5db", borderRadius: 10,
                      padding: "12px 14px 14px", marginBottom: 18, background: "#fcfcfd"
                    }}>
                      <div style={{
                        fontSize: 12, fontWeight: 800, color: "#374151",
                        textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10
                      }}>
                        {g.label}
                      </div>
                      <div style={S.grid}>
                        {g.keys.map(k => cardFor(k))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Sticky, so scrolling a long left column does not scroll the
                    map off the screen. Its height is the viewport less the
                    header strip above it, with a floor - a map shorter than
                    about 420px shows nothing useful. */}
                <div style={{
                  flex: "1 1 520px", minWidth: 0,
                  position: "sticky", top: 12,
                  height: "calc(100vh - 150px)", minHeight: 420,
                }}>
                  <FleetMap token={token} />
                </div>
              </div>
            );
          })()}
        </>
      )}

      {view === "modules" && selected && (
        <ModuleDetail
          mod={selected}
          token={token}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}